import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  TrendingUp,
  Shield,
  GitBranch,
  Activity,
  BarChart2,
  Layers,
  Terminal,
  Database,
  Upload,
  X,
  Send,
  Download,
  Sparkles,
  FileText,
  FileSpreadsheet,
  Presentation,
  Loader2,
  ChevronRight,
  Code2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  CheckCircle2,
  MessageSquare,
  History,
  PlusCircle,
  Trash2,
  Users,
  Map,
  Network,
  Target,
  ClipboardList,
  DollarSign,
  Briefcase,
  AlertTriangle,
  Expand,
  Minimize2,
  Crown,
  Filter,
  RefreshCw,
  Pencil,
  Paperclip,
  Workflow,
  ShieldCheck,
  Cloud,
  DoorOpen,
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../api';
import toast from 'react-hot-toast';
import PageErrorBoundary from '../components/PageErrorBoundary';
import DataSimRiskAnalyzer from '../components/Intelligence/DataSimRiskAnalyzer';
import VerificationBadge from '../components/Verification/VerificationBadge';
import VerificationPanel from '../components/Verification/VerificationPanel';
import VerificationToggle from '../components/Verification/VerificationToggle';
import { DataCleaningInput } from '../components/Intelligence/DataCleaningPanel';
import { CleaningResultsPanel } from '../components/Intelligence/cleaning';
import CustomTransformChat from '../components/Intelligence/CustomTransformChat';
import TransformationLibrary from '../components/Intelligence/TransformationLibrary';
import ContractAnalysisResults from '../components/Intelligence/ContractAnalysisResults';
import RefineChatPanel from '../components/Intelligence/RefineChatPanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DashboardRenderer, { DashboardSpecType, ChartSpec } from '../components/DashboardRenderer';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';
import { useSmartRouterSessionContext } from '../hooks/useSmartRouterSessionContext';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { getSmartStartSessionDocuments, launchPipeline } from '../services/smartStartApi';
import PortfolioReporting from './PortfolioReporting';
import { LD_ACCEPT, LD_ONLY_EXTENSIONS, isLdOnly, setPendingLdFiles } from '../components/LogicDecomposer/ldHandoff';
import MigrationArchitectWorkspace, { type ConsolidatedSummaryRow, type RiskFlag } from '../components/MigrationArchitect/MigrationArchitectWorkspace';
import GFDIntakeWorkspace from '../components/Intelligence/GFDIntake/GFDIntakeWorkspace';
import ProcessMapSynthesisWorkspace from '../components/Intelligence/ProcessMapSynthesis/ProcessMapSynthesisWorkspace';
import { deleteProcessMapSession, listProcessMapSessions } from '../components/Intelligence/ProcessMapSynthesis/processMapSynthesisApi';
import SessionCollaboratorsList from '../components/Collaborators/SessionCollaboratorsList';
import { countOf } from '../utils/pluralize';
import {
  getIntelligenceCollaborators,
  getAvailableIntelligenceCollaborators,
  addIntelligenceCollaborator,
  removeIntelligenceCollaborator,
  getTenantUsers,
} from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IntelligenceMode {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
  // When set, clicking the card launches the named Smart Router pipeline and
  // navigates to the resulting session instead of selecting an analytical
  // sub-mode within this page. Used for cards backed by multi-stage pipelines
  // (intake → PM persistence → SDA → AI Council → export) whose work can't be
  // collapsed into a single Intelligence mode.
  pipelineKey?: string;
  // When set, clicking the card navigates directly to this route instead of
  // launching a pipeline or selecting an in-page mode. Used for cards that are
  // just an entry point into an existing standalone page (e.g. Process
  // Optimization, Risk & Controls) rather than an analysis mode implemented
  // inside Jarvis Intelligence itself.
  linkTo?: string;
}

// Migration Architect only (Asana 1216373781992284 item 2): per-file Path A/B/C/D routing with
// evidence. `disposition` never appears here for a Path D file — dispositions come from the
// separate `dispositions` array below, which is always the same fixed pending-engine marker,
// never model-authored text.
interface RoutingEntry {
  file: string;
  path: 'A' | 'B' | 'C' | 'D';
  label: string;
  confidence: number | null;
  evidence: { quote: string; reason: string }[];
}

interface PendingDisposition {
  file: string;
  status: 'pending_logic_decomposer';
  message: string;
}

interface AnalysisResult {
  analysis?: string;
  code?: string;
  output?: string;
  errors?: string;
  isPython?: boolean;
  mode: { id: string; name: string };
  hasFile: boolean;
  fileName: string | null;
  routing?: RoutingEntry[] | null;
  dispositions?: PendingDisposition[] | null;
  // Item 4 (Asana 1216373781992284): Migration Architect's structured consolidated-summary +
  // risk-flags fence, parsed server-side (services/migrationArchitect/summary.js). null when the
  // run predates this or the model didn't return the fence — MigrationArchitectWorkspace falls
  // back to the narrative rather than fabricating rows.
  consolidatedSummary?: ConsolidatedSummaryRow[] | null;
  riskFlags?: RiskFlag[] | null;
}

interface DQColumn {
  column: string;
  dtype: string;
  null_count: number;
  null_pct: number;
  unique: number;
  total: number;
  outliers: number | null;
  min: number | null;
  max: number | null;
  mean: number | null;
}

interface DQFile {
  total_rows: number;
  total_cols: number;
  duplicate_rows: number;
  columns: DQColumn[];
  error?: string;
}

interface SmartResult {
  sessionId?: number;
  fileName: string | null;
  hasFile: boolean;
  mode: { id: string; name: string };
  analysisPlan: string;
  dataQuality: Record<string, DQFile> | null;
  rawData: { fileName: string; sheets: { sheetName: string; headers: string[]; rows: unknown[][]; totalRows: number }[] }[] | null;
  executive: {
    ingestionCheck: {
      raw?: string; fileName: string; rows: string | null; columns: string | null;
      dateRange: string | null; status: 'COMPLETE' | 'PARTIAL';
      fileDetails?: { fileName: string; rows: string | null; columns: string | null; dateRange: string | null; status: 'COMPLETE' | 'PARTIAL' }[];
    } | null;
    summary: { label: string; value: string }[];
    kpis: { label: string; value: number; format?: string; delta?: number | null; deltaLabel?: string; isPositiveGood?: boolean }[];
    bullets: string;
    charts: ChartSpec[];
    recommendations: string;
  } | null;
  dashboard: DashboardSpecType | null;
  deepInsight: {
    qualitativeAnalysis: string;
    recommendations: string;
    insights: { what: string; soWhat: string; nowWhat: string }[];
    quantitative: { code: string; output: string; errors: string };
  } | null;
}

// ─── Icon Map ─────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  TrendingUp, Shield, GitBranch, Activity, BarChart2, Layers, Terminal, Database,
  Users, Map, Network, Target, ClipboardList, DollarSign, Briefcase, FileText, Presentation,
  Workflow, ShieldCheck, DoorOpen
};

// ─── Help Content (skills + why not ChatGPT?) ────────────────────────────────

const HELP_CONTENT: Record<string, { why: string; chatgpt: string; skills: string[] }> = {
  'risk-analyzer': {
    skills: [
      'Parse risk descriptions and categorize by type',
      'Score inherent & residual risk (likelihood × impact)',
      'Evaluate control effectiveness for each risk',
      'Map to Basel III, COSO, and ISO 31000 frameworks',
      'Generate compliance gap report & remediation plan',
    ],
    why: 'Runs RCSA-specific risk analysis with inherent/residual risk ratings, control effectiveness scoring, and compliance gap detection.',
    chatgpt: 'ChatGPT doesn\'t understand RCSA methodology. This mode maps risks to Basel III, COSO, and ISO 31000 frameworks automatically.'
  },
  'migration-architect': {
    skills: [
      'Assess current architecture & system dependencies',
      'Identify compatibility issues and blockers',
      'Design phased migration roadmap with timelines',
      'Generate rollback strategies for each phase',
      'Produce zero-downtime cutover plan',
    ],
    why: 'Produces phased migration plans with rollback strategies, effort estimates, and zero-downtime analysis.',
    chatgpt: 'ChatGPT can\'t assess your actual system dependencies. Upload your architecture docs and this mode creates a tailored migration roadmap.'
  },
  'observability-commander': {
    skills: [
      'Parse logs, metrics, and distributed traces',
      'Apply 5-Why root cause analysis',
      'Assess SLO breach & error budget impact',
      'Classify incident severity (P1 / P2 / P3)',
      'Generate structured incident response & prevention plan',
    ],
    why: 'Applies 5-Why root cause analysis, SLO/error budget assessment, and P1/P2/P3 incident prioritization.',
    chatgpt: 'ChatGPT can\'t analyze your actual logs. Paste or upload your observability data and get a structured incident response plan instantly.'
  },
  'data-analyst': {
    skills: [
      'Ingest & validate uploaded CSV or Excel file',
      'Run statistical analysis (distributions, correlations, outliers)',
      'Detect trends, patterns, and anomalies',
      'Quantify findings with specific numbers & percentages',
      'Recommend follow-up analyses and next actions',
    ],
    why: 'Reads your uploaded CSV or Excel files and performs statistical analysis, trend detection, and anomaly identification.',
    chatgpt: 'ChatGPT can\'t process your actual files. This mode ingests your data directly and surfaces insights with specific numbers and percentages.'
  },
  'python-analyst': {
    skills: [
      'Understand your data structure and analysis goal',
      'Write pandas / numpy Python code tailored to your dataset',
      'Execute the code against your uploaded file',
      'Return real computed output (not simulated)',
      'Iterate on the code if results need refinement',
    ],
    why: 'Writes and executes real Python (pandas/numpy) code on your uploaded data — live output, not simulated.',
    chatgpt: 'ChatGPT cannot run code on your files. This mode actually executes Python against your dataset and returns real computed results.'
  },
  'smart-data-analyst': {
    skills: [
      'Runs qualitative AI analysis — trends, patterns, anomalies, and strategic insights',
      'Executes live Python code for exact statistics, correlations, and outlier detection',
      'Builds an interactive dashboard with 6–8 charts (bar, line, donut, waterfall, and more)',
      'Generates KPI cards, executive summary bullets, and What / So What / Now What insights',
      'Exports a professional PowerPoint deck via Anthropic\'s official pptx skill',
      'Exports a detailed Word report with cover page, KPIs, insights, and full analysis',
    ],
    why: 'Upload your file, describe your goal, and get a complete boardroom-ready report — executive summary, interactive dashboard, deep insights, and export-ready PowerPoint and Word in one click.',
    chatgpt: 'ChatGPT can\'t run real code, build interactive dashboards, or export presentation-quality PowerPoint. This mode does all of that automatically from your raw data file.'
  },
  'user-research-synthesis': {
    skills: [
      'Parse interview transcripts, surveys, and raw notes automatically',
      'Extract recurring themes, pain points, and unmet needs',
      'Rank insights by frequency and business impact',
      'Map findings to user personas or stakeholder segments',
      'Generate a structured insights report with prioritized recommendations',
    ],
    why: 'Turns messy qualitative data into structured, actionable insights with theme extraction, pain-point ranking, and stakeholder segmentation built in.',
    chatgpt: 'ChatGPT gives generic summaries. This tool extracts themes, ranks pain points by frequency, and maps findings to stakeholder segments — ready for a board presentation.'
  },
  'roadmap-narrative': {
    skills: [
      'Transform a raw initiative list into a coherent strategic story',
      'Sequence initiatives by dependencies, effort, and business impact',
      'Generate executive-ready roadmap narrative with rationale',
      'Align initiatives to company OKRs and strategic goals',
      'Produce both a detailed plan and a concise board-level summary',
    ],
    why: 'Takes your prioritized initiative list and builds a compelling narrative that explains the why, what, and when — ready to present to executives or operational teams.',
    chatgpt: 'ChatGPT writes generic text. This tool structures your roadmap against OKRs, sequences by dependencies, and produces both a board summary and an operational plan in one run.'
  },
  'stakeholder-influence-mapper': {
    skills: [
      'Map each stakeholder\'s influence level, stance, and concerns',
      'Identify champions, blockers, and neutral parties',
      'Generate tailored talking points for each stakeholder',
      'Recommend engagement sequencing to build momentum',
      'Produce a stakeholder influence matrix with action plan',
    ],
    why: 'Builds a full stakeholder influence map with personalized messaging and engagement sequencing — so you walk into every meeting with the right talking points.',
    chatgpt: 'ChatGPT gives generic advice. This tool creates a stakeholder matrix, identifies blockers vs champions, and writes tailored talking points for each person on your list.'
  },
  'okr-cascade-generator': {
    skills: [
      'Break company-level OKRs into team-level objectives and key results',
      'Score alignment between company goals and team OKRs',
      'Define data feed sources and measurement cadence for each KR',
      'Identify gaps where no team is owning a company goal',
      'Output a cascaded OKR table ready to load into any tracking tool',
    ],
    why: 'Automates the tedious OKR cascading process — alignment scoring, KR definition, and data feed mapping across every team in one structured output.',
    chatgpt: 'ChatGPT can\'t score alignment or define metrics. This tool cascades your OKRs across teams, scores alignment, and maps data sources — ready to paste into Notion, JIRA, or Excel.'
  },
  'change-management-suite': {
    skills: [
      'Perform a full change impact assessment across people, process, and technology',
      'Structure a board deck outline for change program governance',
      'Run a scenario war room: identify risks, triggers, and mitigation playbooks',
      'Define governance model, decision rights, and escalation paths',
      'Generate a stakeholder communication and training plan',
    ],
    why: 'Covers the full change management lifecycle in one run — impact assessment, board deck, scenario planning, and governance playbook — so nothing falls through the cracks.',
    chatgpt: 'ChatGPT writes generic change plans. This tool produces a full impact assessment, scenario war room analysis, governance model, and stakeholder comms plan tailored to your program.'
  },
  'pe-investment': {
    skills: [
      'Run comprehensive due diligence across financial, commercial, legal & operational dimensions',
      'Build full LBO models with Base/Bull/Bear scenarios, DCF valuation, and IRR/MOIC projections',
      'Generate institutional-quality Investment Committee memorandums',
      'Detect red flags in uploaded deal documents (10-Ks, CIMs, financials)',
      'Export results as PDF, DOCX, or PPTX — ready for IC presentation',
    ],
    why: 'Covers the full PE deal workflow in one place — DD checklist, LBO modeling, and IC memo generation — powered by AI trained on institutional-grade frameworks.',
    chatgpt: 'ChatGPT gives generic financial advice. This tool runs structured PE-grade due diligence, builds real LBO models with scenario analysis, and writes IC-ready memos — all from your deal parameters.'
  },
  'due-diligence': {
    skills: [
      'Generate a comprehensive DD checklist across financial, commercial, legal & operational dimensions',
      'Detect red flags in uploaded documents (10-Ks, CIMs, financials)',
      'Score each DD area by risk level (High / Medium / Low)',
      'Map findings to standard PE due diligence frameworks',
      'Export a structured checklist ready for IC review',
    ],
    why: 'Runs a systematic, multi-dimensional DD process — covering financial health, commercial viability, legal exposure, and operational risks — in minutes instead of days.',
    chatgpt: 'ChatGPT gives generic questions. This tool runs a structured PE-grade DD framework against your uploaded documents and surfaces red flags with severity ratings automatically.'
  },
  'financial-analysis': {
    skills: [
      'Build a full LBO model with Base / Bull / Bear scenarios',
      'Run DCF valuation with sensitivity tables',
      'Project IRR and MOIC across hold periods and exit multiples',
      'Model debt structure, interest expense, and equity returns',
      'Generate an investment returns summary for IC presentation',
    ],
    why: 'Produces a complete LBO model with three scenarios, DCF valuation, and IRR/MOIC projections — all from the deal parameters you provide.',
    chatgpt: 'ChatGPT can\'t run real financial models. This tool builds a full LBO with scenario analysis, DCF valuation, and IRR projections from your inputs — ready for IC presentation.'
  },
  'ic-memo': {
    skills: [
      'Write an institutional-quality Investment Committee memorandum',
      'Structure memo with executive summary, investment thesis, and key risks',
      'Include market analysis, competitive positioning, and value creation plan',
      'Generate management team assessment and exit strategy sections',
      'Export as PDF or DOCX ready for senior decision-makers',
    ],
    why: 'Produces an IC-ready memo in the format senior partners and investment committees expect — not a summary, but a full institutional memorandum.',
    chatgpt: 'ChatGPT writes generic summaries. This tool generates a full IC memo in institutional format — executive summary, thesis, risks, market analysis, and exit strategy — export-ready.'
  },
  'data-cleaning': {
    skills: [
      'Auto-detect data quality issues: duplicates, nulls, type mismatches, inconsistent formats',
      'Reconcile records across multiple files using key-column alignment',
      'Standardize dates, currencies, phone numbers, and categorical values',
      'Generate a cleaning report showing every transformation applied',
      'Download cleaned dataset or send directly to Smart Data Analyst / AI Council',
    ],
    why: 'Automates the tedious 80% of data prep — dedup, null handling, type normalization, format standardization, and cross-file reconciliation — all in one click with real Python execution.',
    chatgpt: 'ChatGPT can describe what to clean but cannot execute it. This mode runs real Python on your files to produce a cleaned, downloadable dataset.'
  },
  'portfolio-reporting': {
    skills: [
      'Upload vendor reports in any format — Excel, Word, or PowerPoint',
      'AI extracts risks, decisions, milestones, and financials automatically',
      'Consolidated portfolio dashboard with RAG status and burn rate across all vendors',
      'One-click Executive Brief — 6-slide PPTX matching your reference layout',
      'Weekly reset to clear previous data and start a fresh reporting cycle',
    ],
    why: 'Eliminates hours of manual consolidation — Jarvis ingests reports from multiple vendors in different formats and produces a single executive-ready output in minutes.',
    chatgpt: 'ChatGPT cannot ingest multiple vendor files, normalize their data, or generate a structured PPTX report. This module does all of that automatically from raw vendor submissions.'
  },
  'powerpoint-restructure': {
    skills: [
      'Upload a .pptx and automatically remove intro/title slides',
      'Reorder remaining slides by EAI ID',
      'Detect and archive PRE (pre-existing/placeholder) slides separately',
      'Preserve original slide content and formatting — reorders, doesn\'t rewrite',
      'Download the restructured deck ready to present',
    ],
    why: 'Applies a consistent restructuring pass — intro removal, EAI ID ordering, PRE-slide archiving — without manually reordering slides by hand.',
    chatgpt: 'ChatGPT can\'t open or reorder your actual .pptx file. This mode processes the real file and gives you back a restructured deck, not instructions.'
  },
  'contract-analysis': {
    skills: [
      'Upload a PDF or DOCX contract',
      'Extract every clause with its exact original numbering preserved',
      'Pull out all quantitative values (amounts, dates, durations, thresholds)',
      'Generate a plain-English explanation for each clause',
      'Run an automated completeness check to flag anything that looks missing',
    ],
    why: 'Extracts and explains every clause with exact numbering intact, plus a completeness check — not a generic summary.',
    chatgpt: 'ChatGPT can summarize a contract you paste in, but it won\'t reliably preserve exact clause numbering or systematically check for missing sections the way this mode does.'
  },
  'process-optimization': {
    skills: [
      'Identify process inefficiencies and automation opportunities from your process map',
      'Classify each opportunity by Model Risk Management (MRM) tier',
      'Run Segregation of Duties (SOD) analysis on proposed changes',
      'Score and rank opportunities on a prioritization matrix (impact vs. effort)',
      'Generate a Gantt-chart implementation roadmap',
    ],
    why: 'Turns a mapped process into a ranked, presentation-ready improvement roadmap — with MRM classification and SOD analysis built in.',
    chatgpt: 'ChatGPT can\'t see your actual process steps or run SOD/MRM analysis. This opens the Process Optimization workspace, which works directly from your process data.'
  },
  'risk-controls': {
    skills: [
      'Map risks to controls step-by-step across a process',
      'Score inherent and residual risk (likelihood × impact)',
      'Rate control effectiveness for each mapped control',
      'Map risks and controls to compliance frameworks',
      'Surface coverage gaps — risks with no control, or weak controls',
    ],
    why: 'Opens the Risk & Controls workspace for structured risk-to-control mapping, effectiveness scoring, and framework alignment on a real process.',
    chatgpt: 'ChatGPT can\'t map risks to your actual process steps or track control effectiveness over time. This opens the dedicated Risk & Controls workspace built for that.'
  },
  'gfd-intake': {
    skills: [
      'Ingest SOW, approach decks, transcripts and notes into the 32-field GFD Stage 1 schema',
      'Tag every field Confirmed / Inferred / Best-guess / Not found / Conflict / User-confirmed with citations',
      'Run a clarification queue, then a DataSim-style business-case interview and Monte Carlo for cost, timeline, savings and revenue',
      'Research analog ranges on the web with URL + excerpt citations, then cross-check the packet for hallucinations',
      'Export a branded Word walkthrough + Excel checklist for ServiceNow paste, plus a separate business-case Word/Excel pack',
    ],
    why: 'We cannot sit in the client ServiceNow instance. This mode prepares a provenance-tagged packet and a grounded business case an internal champion can submit.',
    chatgpt: 'ChatGPT will invent form labels and dollar figures. This mode is grounded in the GFD UI map, uploaded documents, cited web research, and a field-level accuracy pack.',
  }
};

// ─── Modes ───────────────────────────────────────────────────────────────────

const MODES: IntelligenceMode[] = [
  {
    id: 'data-cleaning',
    name: 'Data Cleaning & Reconciliation',
    description: 'Clean, deduplicate, reconcile & standardize messy data from multiple systems',
    icon: 'ClipboardList',
    color: '#14b8a6',
    gradient: 'from-teal-50 to-emerald-50'
  },
  {
    id: 'smart-data-analyst',
    name: 'Smart Data Analyst',
    description: 'Quantitative + qualitative analysis in one click — charts, PPT & Excel',
    icon: 'Database',
    color: '#8b5cf6',
    gradient: 'from-violet-50 to-purple-50'
  },
  {
    id: 'powerpoint-restructure',
    name: 'PowerPoint Restructure',
    description: 'Reorganize a .pptx — remove intro slides, order cards by EAI ID, archive PRE slides',
    icon: 'Presentation',
    color: '#d97706',
    gradient: 'from-amber-50 to-orange-50'
  },
  {
    id: 'contract-analysis',
    name: 'Contract Analysis',
    description: 'Upload a PDF or DOCX contract — extracts every clause (exact numbering preserved), all quantitative values, and plain-English explanations with an automated completeness check.',
    icon: 'FileText',
    color: '#0284c7',
    gradient: 'from-sky-50 to-blue-50'
  },
  {
    id: 'risk-analyzer',
    name: 'DataSim Risk Analyzer',
    description: 'AI-guided Monte Carlo simulation engine for project risk & timeline modeling',
    icon: 'Shield',
    color: '#ef4444',
    gradient: 'from-red-50 to-rose-50'
  },
  {
    id: 'migration-architect',
    name: 'Migration Architect',
    description: 'Platform migration planning, compatibility & rollback strategies',
    icon: 'GitBranch',
    color: '#f59e0b',
    gradient: 'from-amber-50 to-yellow-50'
  },
  {
    id: 'observability-commander',
    name: 'Observability Commander',
    description: 'Logs, metrics, SLOs, incident diagnosis & root-cause tracing',
    icon: 'Activity',
    color: '#10b981',
    gradient: 'from-emerald-50 to-teal-50'
  },
  {
    id: 'pe-investment',
    name: 'PE / Investment',
    description: 'Due diligence, LBO modeling & IC memo generation for private equity deals',
    icon: 'Briefcase',
    color: '#0ea5e9',
    gradient: 'from-sky-50 to-cyan-50'
  },
  {
    id: 'portfolio-reporting',
    name: 'Portfolio Reporting',
    description: 'Multi-vendor report consolidation — upload, extract & generate executive brief',
    icon: 'BarChart2',
    color: '#4f46e5',
    gradient: 'from-indigo-50 to-violet-50'
  },
  {
    id: 'process-optimization',
    name: 'Process Optimization',
    description: 'Identify inefficiencies and generate a prioritized improvement roadmap for an existing process',
    icon: 'Workflow',
    color: '#7c3aed',
    gradient: 'from-violet-50 to-purple-50',
    linkTo: '/app/process-optimization',
  },
  {
    id: 'risk-controls',
    name: 'Risk & Controls',
    description: 'Map risks to controls and assess coverage across a process',
    icon: 'ShieldCheck',
    color: '#dc2626',
    gradient: 'from-red-50 to-rose-50',
    linkTo: '/app/risk-mapping',
  },
  {
    id: 'gfd-intake',
    name: 'GFD Intake',
    description: 'SMBC Global Front Door — provenance-tagged Stage 1 packet, business-case Monte Carlo, and ServiceNow paste pack',
    icon: 'DoorOpen',
    color: '#221551',
    gradient: 'from-indigo-50 to-sky-50',
  },
  {
    id: 'process-map-synthesis',
    name: 'Process Map Synthesis',
    description: 'Temporary module — convert client Process Map PDFs into House-Standard SOP Word documents. Removed once the associated project closes.',
    icon: 'Map',
    color: '#6b7280',
    gradient: 'from-gray-50 to-slate-50',
  },
];

const BUDGET_JUSTIFICATION_LAUNCH: IntelligenceMode = {
  id: 'budget-justification',
  name: 'Budget & Headcount Justification',
  description: 'Defensible executive case for program budget & headcount — financial model + CFO/Risk/Skeptic Council pressure-test',
  icon: 'DollarSign',
  color: '#059669',
  gradient: 'from-emerald-50 to-green-50',
  pipelineKey: 'budget_justification',
};


// ─── PE Investment Sub-Tools ─────────────────────────────────────────────────

interface StrategicSubTool {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
  placeholder: string;
}

type PEMode = 'due-diligence' | 'financial-analysis' | 'ic-memo';

interface PESubTool {
  id: PEMode;
  name: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
}

interface PEResult {
  mode: PEMode;
  analysis?: string;
  memo?: string;
  redFlags?: { flag: string; description: string; severity: 'High' | 'Medium' | 'Low' }[];
  companyName?: string;
  parameters?: { companyName?: string; [key: string]: any };
  documentCount: number;
}

const PE_SUBTOOLS: PESubTool[] = [
  {
    id: 'due-diligence',
    name: 'Due Diligence Checklist',
    description: 'Comprehensive DD checklist with red flag detection across financial, commercial, legal & operational dimensions',
    icon: 'ClipboardList',
    color: '#6366f1',
    gradient: 'from-indigo-50 to-purple-50',
  },
  {
    id: 'financial-analysis',
    name: 'Financial Analysis',
    description: 'LBO model with Base/Bull/Bear scenarios, DCF valuation, IRR/MOIC projections, and sensitivity tables',
    icon: 'DollarSign',
    color: '#10b981',
    gradient: 'from-emerald-50 to-teal-50',
  },
  {
    id: 'ic-memo',
    name: 'IC Memo Generator',
    description: 'Investment Committee memorandum — institutional quality, ready for senior decision-makers',
    icon: 'FileText',
    color: '#f59e0b',
    gradient: 'from-amber-50 to-orange-50',
  },
];

// ─── Markdown Renderer ────────────────────────────────────────────────────────

function renderMarkdown(text: string): React.ReactNode {
  // Strip internal-only sections before rendering
  const cleaned = text
    .split('\n')
    .filter(l => !/^INGESTION CHECK:/i.test(l.trim()))
    .join('\n');

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1 className="text-xl font-bold text-super-dark-purple mt-5 mb-3 border-b-2 border-violet-200 pb-2">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold text-super-dark-purple mt-5 mb-2 border-b border-gray-200 pb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold text-super-dark-purple mt-4 mb-1">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-700 mt-3 mb-1">{children}</h4>,
        p: ({ children }) => <p className="text-gray-700 text-sm mb-2 leading-relaxed">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-super-dark-purple">{children}</strong>,
        em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
        ul: ({ children }) => <ul className="space-y-1 my-2 ml-4 list-disc">{children}</ul>,
        ol: ({ children }) => <ol className="space-y-1 my-2 ml-4 list-decimal">{children}</ol>,
        li: ({ children }) => <li className="text-gray-700 text-sm leading-relaxed">{children}</li>,
        code: ({ children, className }) => {
          const isBlock = !!className;
          return isBlock
            ? <pre className="bg-gray-950 text-green-300 text-xs font-mono p-4 rounded-lg overflow-x-auto my-3 leading-relaxed whitespace-pre-wrap"><code>{children}</code></pre>
            : <code className="bg-gray-100 text-indigo-600 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
        },
        table: ({ children }) => <div className="overflow-x-auto my-3 rounded-lg border border-gray-200"><table className="w-full text-sm border-collapse" style={{ minWidth: 'max-content' }}>{children}</table></div>,
        thead: ({ children }) => <thead>{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr>{children}</tr>,
        th: ({ children }) => <th className="bg-super-dark-purple text-white px-3 py-2 text-left font-semibold border border-gray-300 text-xs align-top whitespace-normal" style={{ minWidth: '8rem' }}>{children}</th>,
        td: ({ children }) => <td className="px-3 py-2 text-gray-700 border border-gray-200 text-xs even:bg-gray-50 align-top whitespace-normal" style={{ minWidth: '8rem' }}>{children}</td>,
        blockquote: ({ children }) => <blockquote className="border-l-4 border-violet-300 pl-4 my-2 text-gray-500 italic text-sm">{children}</blockquote>,
        hr: () => <hr className="my-4 border-gray-200" />,
        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">{children}</a>,
      }}
    >
      {cleaned}
    </ReactMarkdown>
  );
}

// ─── Migration Architect: surviving a reload mid-analysis ─────────────────────
// Asana "Bug: Migration Architect — Analyze session silently resets" (QA 2026-08-04).
//
// Analyze answers 202 and the result arrives later by polling, but every trace of the
// in-flight run lived in component state: the prompt, the files, the poll loop itself.
// A reload or a remount threw all of it away and left a blank panel with no error —
// the mode survived only because it is read back from the URL's ?mode=, which is
// exactly the "everything reset except the mode" QA saw. We persist the one thing
// that can recover the run (its session id) so a fresh mount can pick it back up.
//
// This mirrors the DataSim session-id persistence already in this file; the files
// themselves are deliberately NOT persisted (File objects don't survive JSON, and the
// backend already has them) — only their names, to caption the reconnect banner.
const MIGRATION_INFLIGHT_KEY = 'migration_architect_inflight';
const MIGRATION_POLL_INTERVAL_MS = 4000;
const MIGRATION_POLL_DEADLINE_MS = 1800000; // 30 min hard cap
const MIGRATION_POLL_MAX_ERRORS = 5;        // ~20s of consecutive failures before giving up

type MigrationInflight = {
  sessionId: number;
  prompt: string;
  fileNames: string[];
  startedAt: number;
};

function readMigrationInflight(): MigrationInflight | null {
  try {
    const raw = localStorage.getItem(MIGRATION_INFLIGHT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.sessionId !== 'number') return null;
    return {
      sessionId: v.sessionId,
      prompt: typeof v.prompt === 'string' ? v.prompt : '',
      fileNames: Array.isArray(v.fileNames) ? v.fileNames : [],
      startedAt: typeof v.startedAt === 'number' ? v.startedAt : 0,
    };
  } catch {
    return null; // unreadable or private-mode storage — behave as if there were no run
  }
}

function writeMigrationInflight(v: MigrationInflight): void {
  try { localStorage.setItem(MIGRATION_INFLIGHT_KEY, JSON.stringify(v)); } catch { /* quota / private mode */ }
}

function clearMigrationInflight(): void {
  try { localStorage.removeItem(MIGRATION_INFLIGHT_KEY); } catch { /* ignore */ }
}

function describeElapsed(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Analyzing your inventory...';
  return `Still analyzing — ${mins} minute${mins === 1 ? '' : 's'} elapsed. You can leave this tab open.`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const IntelligenceInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { mode?: string; prompt?: string; fileNames?: string[] } | null;
  const [selectedMode, setSelectedMode] = useState<IntelligenceMode>(() => {
    const queryMode = new URLSearchParams(location.search).get('mode')
      || new URLSearchParams(location.search).get('subStep');
    if (queryMode) {
      const found = MODES.find(m => m.id === queryMode);
      if (found) return found;
    }
    if (locationState?.mode) {
      const found = MODES.find(m => m.id === locationState.mode);
      if (found) return found;
    }
    return MODES[0];
  });
  const [prompt, setPrompt] = useState(() => locationState?.prompt || '');
  const [selectedSubTool, setSelectedSubTool] = useState<StrategicSubTool | null>(null);
  const [selectedPESubTool, setSelectedPESubTool] = useState<PESubTool | null>(null);
  const activeHistoryModeRef = React.useRef<string>('');
  const [peResult, setPEResult] = useState<PEResult | null>(null);
  const [peLoading, setPELoading] = useState(false);
  const [peExpandedRedFlags, setPEExpandedRedFlags] = useState(false);
  const peFileInputRef = useRef<HTMLInputElement>(null);
  const [peFiles, setPEFiles] = useState<File[]>([]);
  const [peCompanyName, setPECompanyName] = useState('');
  const [peSector, setPESector] = useState('');
  const [peDealSize, setPEDealSize] = useState('');
  const [peRevenue, setPERevenue] = useState('');
  const [peEbitda, setPEEbitda] = useState('');
  const [peRevenueGrowth, setPERevenueGrowth] = useState('');
  const [peEntryMultiple, setPEEntryMultiple] = useState('');
  const [peExitMultiple, setPEExitMultiple] = useState('');
  const [peHoldPeriod, setPEHoldPeriod] = useState('5');
  const [peDebtEquityRatio, setPEDebtEquityRatio] = useState('60/40');
  const [peInvestmentThesis, setPEInvestmentThesis] = useState('');
  const [peKeyRisks, setPEKeyRisks] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  // Smart Data Analyst reads whatever it is given as a file, so the project's interview
  // findings arrive the same way — attached as one more source rather than through a second
  // input path (M5, Asana 1216725280641177: "AI Council / SDA handoff for interview JSON").
  //
  // ATTACHED AS A FILE ON PURPOSE. Everything downstream of this screen — parseFileToText,
  // the Anthropic Files upload, the exports, the session record — already handles files and
  // handles them the same way for every mode. A parallel "project data" field would need each
  // of those to learn about it, and the first one that didn't would drop the interviews
  // silently, which is the failure this whole milestone exists to stop.
  const [loadingInterviewSource, setLoadingInterviewSource] = useState(false);

  const attachInterviewInsights = async () => {
    if (!currentProject?.id) {
      toast.error('Open a project first — interviews are ingested into one.');
      return;
    }
    setLoadingInterviewSource(true);
    try {
      const res = await api.get('/api/intelligence/interview-insights', {
        params: { projectId: currentProject.id },
      });
      const d = res.data?.data;
      if (!d?.available || !d?.text) {
        toast.error(d?.reason || 'This project has no interviews ingested yet.');
        return;
      }
      const name = `Interview Intelligence — ${currentProject.name || 'project'}.md`;
      if (files.some(f => f.name === name)) {
        toast.error('Already attached.');
        return;
      }
      const file = new File([d.text], name, { type: 'text/markdown' });
      setFiles(prev => [...prev, file].slice(0, 5));
      toast.success(
        `Attached ${countOf(d.meta?.interview_count, 'interview')} (${(d.text.length / 1000).toFixed(1)}k chars)`
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not load the project\'s interviews.');
    } finally {
      setLoadingInterviewSource(false);
    }
  };
  const [xlsWarning, setXlsWarning] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [verificationMode, setVerificationMode] = useState<'off' | 'standard' | 'deep'>('standard');
  const [verificationMetadata, setVerificationMetadata] = useState<any>(null);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [helpOpenId, setHelpOpenId] = useState<string | null>(null);
  const [helpPos, setHelpPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Tracks which pipeline-backed card is mid-launch so we can show a spinner
  // and block re-clicks (launch involves a backend session-creation roundtrip).
  const [launchingPipelineKey, setLaunchingPipelineKey] = useState<string | null>(null);

  const handleLaunchPipeline = useCallback(async (mode: IntelligenceMode) => {
    if (!mode.pipelineKey || launchingPipelineKey) return;
    setLaunchingPipelineKey(mode.pipelineKey);
    try {
      const result = await launchPipeline(mode.pipelineKey, {
        projectId: null,
        goalText: mode.name,
        attachedDocuments: [],
      });
      navigate(`/app/smart-start/${result.session_id}`);
    } catch (err: any) {
      toast.error(`Could not launch ${mode.name}: ${err?.response?.data?.error || err?.message || 'unknown error'}`);
      setLaunchingPipelineKey(null);
    }
    // On success we navigate away — the component unmounts so we don't need
    // to clear the launching state explicitly.
  }, [launchingPipelineKey, navigate]);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  // Smart Data Analyst state
  const [smartResult, setSmartResult] = useState<SmartResult | null>(null);
  const [smartTab, setSmartTab] = useState<'executive' | 'plan' | 'insights' | 'dashboards' | 'quality'>('plan');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSmartCode, setShowSmartCode] = useState(false);
  const [collapsedDQFiles, setCollapsedDQFiles] = useState<Set<string>>(new Set());
  const [exportingSmartPptx, setExportingSmartPptx] = useState(false);
  const [exportingSmartExcel, setExportingSmartExcel] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [editingExecSummary, setEditingExecSummary] = useState(false);
  const [execSummaryDraft, setExecSummaryDraft] = useState('');
  const [savingExecSummary, setSavingExecSummary] = useState(false);
  const { projects, currentProject } = useProject();
  const [brandingProjectId, setBrandingProjectId] = useState<number | null>(() => currentProject?.id ?? null);
  // Data Cleaning state
  const [cleanResult, setCleanResult] = useState<any>(null);
  const [cleanLoading, setCleanLoading] = useState(false);
  const [cleaningOptions, setCleaningOptions] = useState({
    removeDuplicates: true,
    handleMissing: true,
    standardizeDates: true,
    normalizeText: true,
    standardizeNumbers: true,
    forwardFill: false,
    removeOutliers: false,
  });
  const [reconciliationKey, setReconciliationKey] = useState('');
  // How to combine multiple uploaded files: none (clean separately), union (stack rows), join (match on key)
  const [combineMode, setCombineMode] = useState<'none' | 'union' | 'join'>('none');
  // PowerPoint Restructure result (download URL + stats)
  const [pptxResult, setPptxResult] = useState<any>(null);
  // Contract Analysis result — { sessionId, results: [{ file, clauses, summary, verification }, ...] }
  const [contractResult, setContractResult] = useState<any>(null);
  const [skipContractSummaries, setSkipContractSummaries] = useState(false);
  // Refine & Chat panel open state — separate per mode so toggling one doesn't
  // affect the other, and so the panel can be closed independently of the rest
  // of the page's loading flow.
  const [cleaningChatOpen, setCleaningChatOpen]     = useState(false);
  const [smartAnalystChatOpen, setSmartAnalystChatOpen] = useState(false);
  // SME enrichment — interview notes or emails that explain what ambiguous
  // systems/values actually are. Fed into Phase 2 so the AI can propose
  // canonical name mappings and flag SME-backed issues.
  const [smeNotes, setSmeNotes] = useState('');
  const [smeFiles, setSmeFiles] = useState<File[]>([]);
  // Pipeline extensions state
  const [cleanSubView, setCleanSubView] = useState<'results' | 'questions' | 'transform' | 'library'>('results');
  const [exportingSmartWord, setExportingSmartWord] = useState(false);
  // Chat / refine state
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string; chart?: ChartSpec }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [chatPanelExpanded, setChatPanelExpanded] = useState(false);
  const [chartMode, setChartMode] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ldFileInputRef = useRef<HTMLInputElement>(null);
  const analyzingRef = useRef(false); // prevents concurrent requests (more reliable than loading state)
  // Migration Architect background-run feedback. `migrationProgress` replaces the fixed
  // "This may take a few moments" with something that moves; `migrationError` is a
  // panel that stays on screen — a toast can be missed on a long run, and the QA
  // finding was precisely that the user was left with nothing to read.
  const [migrationProgress, setMigrationProgress] = useState<string | null>(null);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  // Set on unmount so the poll loop stops instead of running on against a dead tree.
  const migrationPollAbortRef = useRef(false);
  // Prompt helper state
  const [showPromptHelper, setShowPromptHelper] = useState(false);
  const [promptDescription, setPromptDescription] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [generatedPromptPreview, setGeneratedPromptPreview] = useState('');
  // Session history state
  const [sessions, setSessions] = useState<{ id: number; title: string; prompt: string; mode_id: string; mode_name: string; has_file: boolean; file_name: string | null; created_at: string; is_collaborator?: boolean; collaborator_names?: string[] }[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [isCollaboratorSession, setIsCollaboratorSession] = useState(false);
  const [datasimSessionId, setDatasimSessionId] = useState<number | null>(() => {
    const saved = localStorage.getItem('datasim_session_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [datasimSessions, setDatasimSessions] = useState<{ id: number; title: string; project_name: string; status: string; current_screen: string; updated_at: string }[]>([]);
  // Session History bug fix (June 2026): the backend caps the list endpoints
  // (intelligence: 50, datasim: 30) so `sessions.length` never grew past the
  // page size — Ryan's QA stuck at 53 after running 2 new contract-analysis
  // sessions. We track the REAL total separately so the badge increments
  // immediately while the dropdown list stays performant.
  const [sessionsTotalCount, setSessionsTotalCount] = useState(0);
  const [datasimTotalCount, setDatasimTotalCount] = useState(0);
  const [datasimResetKey, setDatasimResetKey] = useState(0);
  const [gfdSessionId, setGfdSessionId] = useState<number | null>(() => {
    const saved = localStorage.getItem('gfd_session_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [gfdSessions, setGfdSessions] = useState<{ id: number; title: string; initiative_name: string; status: string; current_screen: string; updated_at: string }[]>([]);
  const [gfdTotalCount, setGfdTotalCount] = useState(0);
  const [gfdResetKey, setGfdResetKey] = useState(0);
  const [pmsSessionId, setPmsSessionId] = useState<number | null>(() => {
    const saved = localStorage.getItem('pms_session_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [pmsSessions, setPmsSessions] = useState<{ id: number; title?: string | null; status: string; updated_at?: string; documents?: { id: string; fileName: string; status: string }[] }[]>([]);
  const [pmsTotalCount, setPmsTotalCount] = useState(0);
  const [pmsResetKey, setPmsResetKey] = useState(0);
  const skipAutoRestoreRef = useRef(false);

  // Only auto-sync when the user's active mode matches the routed subStep —
  // otherwise an SDA-run could prematurely complete a DCR-targeted step (or
  // vice versa) when the consultant explored both modes in one routed visit.
  const orchestratorSubStep = new URLSearchParams(location.search).get('subStep');

  useEffect(() => {
    if (!orchestratorSubStep) return;
    if (orchestratorSubStep === selectedMode.id) return;
    const matched = MODES.find((m) => m.id === orchestratorSubStep);
    if (matched) setSelectedMode(matched);
  }, [orchestratorSubStep, selectedMode.id]);

  // Follow ?mode= when the address bar changes under us — Back and Forward.
  // The useState initializer above only runs on mount, so without this the URL
  // would step back while the panel stayed where it was.
  //
  // Deliberately quiet in two cases: while ?subStep= is present the effect
  // above owns the mode, and a value that already matches is skipped so this
  // cannot ping-pong with the click handler that writes the param.
  const routedMode = new URLSearchParams(location.search).get('mode');

  useEffect(() => {
    if (orchestratorSubStep) return;
    if (!routedMode || routedMode === selectedMode.id) return;
    const matched = MODES.find((m) => m.id === routedMode);
    if (matched) setSelectedMode(matched);
  }, [routedMode, orchestratorSubStep, selectedMode.id]);

  const orchestratorModeMatches =
    !orchestratorSubStep || orchestratorSubStep === selectedMode.id;
  const safeSyncSignal = activeSessionId && orchestratorModeMatches ? activeSessionId : null;

  const smartRouter = useSmartRouterStepSync({
    autoSyncSignal: safeSyncSignal,
    autoSyncArtifact: safeSyncSignal
      ? { kind: 'intelligence_session', session_id: safeSyncSignal, mode_id: selectedMode.id }
      : undefined,
    autoSyncSummary: safeSyncSignal
      ? `Saved Intelligence session #${safeSyncSignal} (${selectedMode.name}).`
      : undefined,
  });

  const [routerPreloadedDocNames, setRouterPreloadedDocNames] = useState<string[]>([]);
  const orchestratorCtx = useOrchestratorContext();
  const routerSession = useSmartRouterSessionContext();
  const promptPrefillKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!routerSession.inRouter || !routerSession.userGoal) return;
    const key = `${routerSession.sessionId}:${selectedMode.id}`;
    if (promptPrefillKeyRef.current === key) return;
    promptPrefillKeyRef.current = key;
    setPrompt((prev) => (prev.trim() ? prev : routerSession.userGoal!));
  }, [routerSession.inRouter, routerSession.userGoal, routerSession.sessionId, selectedMode.id]);
  const reopenAttemptedRef = useRef(false);

  useEffect(() => {
    if (reopenAttemptedRef.current) return;
    if (!orchestratorCtx.inRouter) return;
    const sessionIdParam = orchestratorCtx.extraParams?.session_id;
    const targetSessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : NaN;
    if (!Number.isFinite(targetSessionId)) return;
    if (activeSessionId === targetSessionId) return;
    reopenAttemptedRef.current = true;
    loadSession(targetSessionId).catch((err) => {
      console.warn('[Intelligence] Re-open of routed session failed:', err);
      reopenAttemptedRef.current = false;
    });
  }, [orchestratorCtx.inRouter, orchestratorCtx.extraParams?.session_id, activeSessionId]);

  // Fetch sessions on mount and whenever the active mode/sub-tool changes, so
  // History always holds the selected tool's own page of sessions.
  React.useEffect(() => {
    activeHistoryModeRef.current = selectedSubTool?.id || selectedPESubTool?.id || selectedMode.id;
    fetchSessions();
  }, [selectedMode.id, selectedSubTool?.id, selectedPESubTool?.id]); // eslint-disable-line


  const { subscribeToLiveNotifications } = useNotifications();
  React.useEffect(() => {
    return subscribeToLiveNotifications(() => fetchSessions());
  }, [subscribeToLiveNotifications]); // eslint-disable-line

  React.useEffect(() => {
    const sessionParam = new URLSearchParams(location.search).get('session');
    if (sessionParam) {
      const sessionId = parseInt(sessionParam, 10);
      if (!Number.isNaN(sessionId)) loadSession(sessionId);
    }
  }, [location.search]); // eslint-disable-line


  React.useEffect(() => {
    if (!isCollaboratorSession || !activeSessionId) return;
    const interval = setInterval(async () => {
      fetchSessions();
      try {
        const res = await api.get(`/api/intelligence/sessions/${activeSessionId}`);
        const s = res.data.session;
        if (!s) return;
        if (s.mode_id === 'data-cleaning') {
          let meta: any = {};
          if (s.metadata) { try { meta = JSON.parse(s.metadata); } catch { /* ignore */ } }
          const isV2 = meta && meta.version === 2 && Array.isArray(meta.files);
          if (isV2) {
            const allUnits: any[] = [];
            for (const file of meta.files) {
              for (const sheet of (file.sheets || [])) { allUnits.push({ file, sheet }); }
            }
            const lead = allUnits.find(u => u.sheet.status !== 'failed') || allUnits[0] || null;
            const leadSheet = lead?.sheet || {};
            const leadStats = leadSheet.stats || {};
            const rollup = meta.rollup || {};
            const legacyStats = {
              ...leadStats,
              qualityScoreBefore: leadSheet.qualityScoreBefore || rollup.qualityScoreBefore_weighted || 0,
              qualityScoreAfter: leadSheet.qualityScoreAfter || rollup.qualityScoreAfter_weighted || 0,
              totalRows: leadSheet.rows_total || leadStats.totalRows || 0,
              rowsAfterCleaning: leadSheet.rows_after || leadStats.rowsAfterCleaning || leadSheet.rows_total || 0,
              columnsProcessed: leadSheet.columns_processed || leadStats.columnsProcessed || 0,
            };
            setCleanResult((prev: any) => prev ? {
              ...prev,
              version: 2, pipeline: meta.pipeline || 'tabular', files: meta.files, rollup,
              units: allUnits.length, sheetsTotal: rollup.total_sheets || allUnits.length,
              unitsFailed: rollup.failed_units || allUnits.filter((u: any) => u.sheet.status === 'failed').length,
              summary: s.analysis || '', issuesFound: leadSheet.issuesFound || [],
              changeLog: meta.changeLog || [], cleaningLog: meta.cleaningLog || leadSheet.cleaningLog || '',
              previewBefore: leadSheet.previewBefore || '', previewAfter: leadSheet.previewAfter || '',
              stats: legacyStats,
            } : null);
          }
        } else if (s.mode_id === 'smart-data-analyst') {
          let meta: any = {};
          if (s.metadata) { try { meta = JSON.parse(s.metadata); } catch { /* ignore */ } }
          setSmartResult((prev: any) => prev ? {
            ...prev,
            analysisPlan: meta.analysisPlan || prev.analysisPlan,
            dataQuality: meta.dataQuality || prev.dataQuality,
            executive: meta.executive || prev.executive,
            dashboard: meta.dashboard || prev.dashboard,
            deepInsight: meta.deepInsight || prev.deepInsight,
          } : null);
        } else if (s.mode_id === 'contract-analysis') {
          let meta: any = {};
          if (s.metadata) { try { meta = JSON.parse(s.metadata); } catch { /* ignore */ } }
          if (Array.isArray(meta.results) && meta.results.length > 0) {
            setContractResult((prev: any) => prev ? { ...prev, results: meta.results } : { sessionId: activeSessionId, results: meta.results });
          }
        } else {
          if (s.analysis) {
            setResult((prev: any) => prev ? { ...prev, analysis: s.analysis } : { analysis: s.analysis, mode: { id: s.mode_id, name: s.mode_name }, hasFile: s.has_file, fileName: s.file_name });
          }
        }
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [isCollaboratorSession, activeSessionId]);


  const descPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const sessionId = contractResult?.sessionId;
    const hasPending = (contractResult?.results || []).some(
      (r: any) => r.descriptions_status === 'pending',
    );
    if (!sessionId || !hasPending) {
      if (descPollRef.current) { clearInterval(descPollRef.current); descPollRef.current = null; }
      return;
    }
    if (descPollRef.current) return; // already polling this session
    descPollRef.current = setInterval(async () => {
      try {
        const poll = await api.get(`/api/intelligence/sessions/${sessionId}`);
        const sess = poll.data?.session;
        if (!sess) return;
        let meta: any = {};
        try { meta = JSON.parse(sess.metadata || '{}'); } catch { return; }
        if (!Array.isArray(meta.results)) return;
        const stillPending = meta.results.some((r: any) => r.descriptions_status === 'pending');
        setContractResult((prev: any) => prev ? { ...prev, results: meta.results } : prev);
        if (!stillPending && descPollRef.current) {
          clearInterval(descPollRef.current);
          descPollRef.current = null;
        }
      } catch { /* silent */ }
    }, 15000);
    return () => {
      if (descPollRef.current) { clearInterval(descPollRef.current); descPollRef.current = null; }
    };
  }, [contractResult?.sessionId, contractResult?.results]);

  // Auto-restore most recent DataSim session after login (localStorage may be empty).
  // Skipped if user explicitly clicked New Session — persisted via localStorage flag across reloads.
  React.useEffect(() => {
    const userWantsNew = localStorage.getItem('datasim_wants_new') === 'true';
    if (datasimSessions.length > 0 && !datasimSessionId && !userWantsNew) {
      const latest = datasimSessions[0]; // already sorted by updated_at DESC
      setDatasimSessionId(latest.id);
      localStorage.setItem('datasim_session_id', String(latest.id));
    }
  }, [datasimSessions]);

  React.useEffect(() => {
    const userWantsNew = localStorage.getItem('gfd_wants_new') === 'true';
    if (gfdSessions.length > 0 && !gfdSessionId && !userWantsNew) {
      const latest = gfdSessions[0];
      setGfdSessionId(latest.id);
      localStorage.setItem('gfd_session_id', String(latest.id));
    }
  }, [gfdSessions]);

  React.useEffect(() => {
    const userWantsNew = localStorage.getItem('pms_wants_new') === 'true';
    if (pmsSessions.length > 0 && !pmsSessionId && !userWantsNew) {
      const latest = pmsSessions[0];
      setPmsSessionId(latest.id);
      localStorage.setItem('pms_session_id', String(latest.id));
    }
  }, [pmsSessions, pmsSessionId]);

  // Restore files passed from AI Council
  useEffect(() => {
    if ((window as any)._sdaFiles?.length) {
      setFiles((window as any)._sdaFiles as File[]);
      (window as any)._sdaFiles = undefined;
    }
  }, []);

  const lastPreloadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!smartRouter.inRouter || smartRouter.sessionId == null) return;
    const preloadKey = `${smartRouter.sessionId}:${selectedMode.id}`;
    if (lastPreloadedKeyRef.current === preloadKey) return;
    lastPreloadedKeyRef.current = preloadKey;

    let cancelled = false;
    (async () => {
      try {
        const payload = await getSmartStartSessionDocuments(smartRouter.sessionId as number);
        if (cancelled) return;
        const docs = (payload.documents || []).filter(
          (d) => typeof d.text === 'string' && d.text.length > 0
        );
        if (docs.length === 0) return;

        const stripExt = (name: string) => name.replace(/\.[^.]+$/, '');
        const builtFiles: File[] = docs.map((d) => {
          const blob = new Blob([d.text!], { type: 'text/plain' });
          return new File([blob], `${stripExt(d.name)}.txt`, { type: 'text/plain' });
        });

        const incomingNames = new Set(builtFiles.map((f) => f.name));
        setFiles((prev) => {
          const kept = prev.filter((f) => !incomingNames.has(f.name));
          return [...builtFiles, ...kept].slice(0, 5);
        });
        setRouterPreloadedDocNames(docs.map((d) => d.name));
        toast.success(
          `Loaded ${docs.length} document${docs.length === 1 ? '' : 's'} from Smart Router — no need to re-upload.`,
          { duration: 4500 }
        );
      } catch (err) {
        console.warn('[Intelligence] Smart Router doc pre-load failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [smartRouter.inRouter, smartRouter.sessionId, selectedMode.id]);

  const sendToLogicDecomposer = useCallback((ldFiles: File[]) => {
    setPendingLdFiles(ldFiles);
    toast.success(`Sending ${ldFiles.length} workflow file${ldFiles.length > 1 ? 's' : ''} to Logic Decomposer`);
    navigate('/app/logic-decomposer');
  }, [navigate]);

  // Path D routing (Asana 1216373781992284 item 2): the analysis pass never invents a
  // disposition for these — it hands the actual uploaded file to the Logic Decomposer engine,
  // which is the only thing allowed to score it (Box + Cat). `files` still holds the File
  // objects from the analyze call that produced this routing, so match by name and reuse the
  // same handoff the LD-only extension auto-route already uses.
  const sendRoutedFileToLogicDecomposer = useCallback((fileName: string) => {
    const match = files.find((f) => f.name === fileName);
    if (!match) {
      toast.error(`Could not find "${fileName}" among the uploaded files to send — try re-uploading it directly in Logic Decomposer.`);
      return;
    }
    sendToLogicDecomposer([match]);
  }, [files, sendToLogicDecomposer]);

  const routeLdFiles = useCallback((incoming: File[], modeId: string): File[] => {
    if (modeId !== 'migration-architect') return incoming;
    const ldFiles = incoming.filter(f => isLdOnly(f.name));
    if (ldFiles.length === 0) return incoming;
    sendToLogicDecomposer(ldFiles);
    return incoming.filter(f => !isLdOnly(f.name));
  }, [sendToLogicDecomposer]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = routeLdFiles(Array.from(e.target.files || []), selectedMode.id);
    if (selected.length > 0) {
      setFiles(prev => [...prev, ...selected].slice(0, 5));
      // Staging new files for contract-analysis should clear the previous session
      // result so the panel doesn't show stale data while the new run is pending.
      if (selectedMode.id === 'contract-analysis') setContractResult(null);
    }
  };

  const handleLdFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length > 0) sendToLogicDecomposer(picked);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = routeLdFiles(Array.from(e.dataTransfer.files), selectedMode.id);
    if (dropped.length > 0) {
      setFiles(prev => [...prev, ...dropped].slice(0, 5));
      if (selectedMode.id === 'contract-analysis') setContractResult(null);
    }
  }, [routeLdFiles, selectedMode.id]);

  // ── Migration Architect background run ──────────────────────────────────────
  // Stop the poll loop when the component goes away, so a remount never leaves an
  // orphan loop behind calling setState on an unmounted tree.
  useEffect(() => () => { migrationPollAbortRef.current = true; }, []);

  /**
   * Poll a Migration Architect session to completion.
   *
   * Returns the session metadata on success, or null when the caller should stop
   * quietly (the component unmounted). Throws with a message the user can act on for
   * every real failure — the previous loop swallowed EVERY error with a bare
   * `catch { continue; }`, so an expired token or a deleted session polled silently
   * for thirty minutes and then reported a misleading "timed out".
   */
  const pollMigrationSession = useCallback(async (pollSessionId: number, startedAt: number): Promise<any | null> => {
    const deadline = startedAt + MIGRATION_POLL_DEADLINE_MS;
    let consecutiveErrors = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, MIGRATION_POLL_INTERVAL_MS));
      if (migrationPollAbortRef.current) return null;
      if (Date.now() > deadline) {
        throw new Error('Migration Architect is taking longer than 30 minutes. The run may still finish — reopen it from Session History in a few minutes.');
      }
      setMigrationProgress(describeElapsed(Date.now() - startedAt));

      let poll;
      try {
        poll = await api.get(`/api/intelligence/sessions/${pollSessionId}`);
        consecutiveErrors = 0;
      } catch (e: any) {
        const status = e?.response?.status;
        // These never resolve by retrying — fail now with something the user can act on.
        if (status === 401 || status === 403) {
          throw new Error('Your sign-in expired while the analysis was running. Sign in again and reopen it from Session History.');
        }
        if (status === 404) {
          throw new Error('That analysis session no longer exists. Please run the analysis again.');
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= MIGRATION_POLL_MAX_ERRORS) {
          throw new Error('Lost contact with the server while the analysis was running. Check your connection and reopen the run from Session History.');
        }
        continue; // transient network blip — keep polling
      }

      const sess = poll.data?.session;
      if (!sess) continue;
      if (sess.status === 'completed') {
        let meta: any = {};
        try { meta = JSON.parse(sess.metadata || '{}'); } catch { meta = {}; }
        return meta;
      }
      if (sess.status === 'failed') {
        let meta: any = {};
        try { meta = JSON.parse(sess.metadata || '{}'); } catch { meta = {}; }
        throw new Error(meta.error || 'Migration Architect analysis failed.');
      }
    }
  }, []);

  // Resume a run that was in flight when the tab reloaded or the component remounted.
  // Without this the run is simply lost: the backend finishes and writes its result to a
  // session nobody is watching any more, and the user is left staring at a blank panel.
  useEffect(() => {
    if (selectedMode.id !== 'migration-architect') return;
    if (analyzingRef.current) return; // a run started in this mount is already being polled
    const inflight = readMigrationInflight();
    if (!inflight) return;
    // Older than the poll window: the backend resolved it long ago one way or the other,
    // and Session History is the right place to find it.
    if (inflight.startedAt && Date.now() - inflight.startedAt > MIGRATION_POLL_DEADLINE_MS) {
      clearMigrationInflight();
      return;
    }

    let cancelled = false;
    analyzingRef.current = true;
    migrationPollAbortRef.current = false;
    setLoading(true);
    setMigrationError(null);
    setMigrationProgress(inflight.fileNames.length
      ? `Reconnecting to your analysis of ${inflight.fileNames.join(', ')}...`
      : 'Reconnecting to your analysis...');
    setActiveSessionId(inflight.sessionId);
    if (inflight.prompt) setPrompt((cur) => cur || inflight.prompt);

    (async () => {
      try {
        const meta = await pollMigrationSession(inflight.sessionId, inflight.startedAt || Date.now());
        if (meta === null || cancelled) return; // unmounted mid-poll
        clearMigrationInflight();
        setResult(meta);
        setVerificationMetadata(meta?.verification_metadata || null);
        fetchSessions();
        toast.success('Your Migration Architect analysis finished.');
      } catch (err: any) {
        clearMigrationInflight();
        if (cancelled) return;
        const msg = err?.message || 'Migration Architect analysis failed.';
        console.error('[Intelligence] Migration Architect resume failed:', msg);
        setMigrationError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setMigrationProgress(null);
        }
        analyzingRef.current = false;
      }
    })();

    // Releasing the guard here matters: without it a cleanup (mode switch, or a
    // double-invoked effect under StrictMode) would leave analyzingRef stuck true and
    // the next resume would silently decline to run — the exact class of bug this
    // whole change exists to remove.
    return () => { cancelled = true; analyzingRef.current = false; };
    // Deliberately keyed on the mode alone: this is a mount/mode-entry recovery, not
    // something that should re-run when the prompt or file list changes.
  }, [selectedMode.id]);

  const handleAnalyze = async () => {
    if (analyzingRef.current) return; // Synchronous guard — prevents race conditions with loading state
    const activeMode2 = selectedSubTool ? selectedSubTool.id : selectedMode.id;
    const isCleanMode = activeMode2 === 'data-cleaning';
    const isPptxMode = activeMode2 === 'powerpoint-restructure';
    const isContractMode = activeMode2 === 'contract-analysis';
    // Data cleaning, PPTX restructure, and Contract Analysis are file-driven
    // and don't need a prompt — they have deterministic pipelines on the
    // backend. Everything else still requires a prompt.
    if (!isCleanMode && !isContractMode && !prompt.trim()) {
      toast.error('Enter a prompt to analyze');
      return;
    }
    if (isCleanMode && files.length === 0) {
      toast.error('Upload at least one file to clean');
      return;
    }
    if (isPptxMode && files.length === 0) {
      toast.error('Upload a .pptx file to restructure');
      return;
    }
    if (isContractMode && files.length === 0) {
      toast.error('Upload at least one contract (PDF or DOCX) to analyze');
      return;
    }
    analyzingRef.current = true;
    migrationPollAbortRef.current = false;
    setLoading(true);
    setMigrationError(null);
    setResult(null);
    setSmartResult(null);
    setCleanResult(null);
    setPptxResult(null);
    setContractResult(null);
    setShowCode(false);
    setChatMessages([]);
    setIsExpanded(false);
    setChatInput('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('modelId', 'claude-4-sonnet');
      if (activeSessionId) formData.append('sessionId', String(activeSessionId));
      if (verificationMode !== 'off') {
        formData.append('verify', 'true');
        formData.append('verification_mode', verificationMode);
      }
      files.forEach(f => formData.append('files', f));

      const activeMode = selectedSubTool ? selectedSubTool.id : selectedMode.id;
      const isSmart = activeMode === 'smart-data-analyst';
      const isPython = activeMode === 'python-analyst';
      const isClean = activeMode === 'data-cleaning';
      const isPptx = activeMode === 'powerpoint-restructure';
      const isContract = activeMode === 'contract-analysis';
      const isMigration = activeMode === 'migration-architect';

      let endpoint: string;
      if (isClean) {
        endpoint = '/api/intelligence/data-clean';
        formData.append('cleaningOptions', JSON.stringify(cleaningOptions));
        formData.append('combineMode', combineMode);
        if (reconciliationKey) formData.append('reconciliationKey', reconciliationKey);
        if (smeNotes && smeNotes.trim()) formData.append('sme_notes', smeNotes.trim());
        smeFiles.forEach((f) => formData.append('sme_files', f));
      } else if (isPptx) {
        endpoint = '/api/intelligence/pptx-restructure';
      } else if (isContract) {
        endpoint = '/api/intelligence/contract-analysis';
        // Client said: "Use Claude Haiku for the summarization / translation
        // into plain English." Pin Haiku regardless of the global modelId.
        formData.set('modelId', 'claude-haiku-4-5-20251001');
        if (skipContractSummaries) formData.append('skip_summaries', 'true');
      } else if (isSmart) {
        endpoint = '/api/intelligence/smart-analyze';
      } else if (isPython) {
        endpoint = '/api/intelligence/python-execute';
      } else {
        endpoint = '/api/intelligence/analyze';
        formData.append('mode', activeMode);
      }

      let response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: (isSmart || isClean || isPptx || isContract) ? 600000 : 300000,
      });

      // Contract Analysis runs as a background job to stay under Railway's
      // edge-proxy ceiling on large documents: the POST returns immediately
      // with { status: 'processing', sessionId } and we poll the session
      // until it completes, then unwrap metadata.results into the same shape
      // the old synchronous response used.
      if (isContract && response.data?.data?.status === 'processing') {
        const pollSessionId = response.data.data.sessionId;
        const deadline = Date.now() + 1800000; // 30 min hard cap
        let contractData: any = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 4000));
          if (Date.now() > deadline) {
            throw new Error('Contract analysis timed out. Please try again or use a smaller document.');
          }
          let poll;
          try {
            poll = await api.get(`/api/intelligence/sessions/${pollSessionId}`);
          } catch {
            continue; // transient network blip — keep polling
          }
          const sess = poll.data?.session;
          if (!sess) continue;
          if (sess.status === 'completed') {
            let meta: any = {};
            try { meta = JSON.parse(sess.metadata || '{}'); } catch { meta = {}; }
            contractData = { sessionId: pollSessionId, results: meta.results || [] };
            break;
          }
          if (sess.status === 'failed') {
            let meta: any = {};
            try { meta = JSON.parse(sess.metadata || '{}'); } catch { meta = {}; }
            throw new Error(meta.error || 'Contract analysis failed.');
          }
        }
        setContractResult(contractData);
        if (contractData?.sessionId) setActiveSessionId(contractData.sessionId);
        fetchSessions();
        return;
      }

      if (isMigration && response.data?.data?.status === 'processing') {
        const pollSessionId = response.data.data.sessionId;
        const startedAt = Date.now();
        // Persist BEFORE the first poll. From here the session id is the only handle on
        // this run, and a reload one second from now has to be able to find it again.
        writeMigrationInflight({
          sessionId: pollSessionId,
          prompt,
          fileNames: files.map((f) => f.name),
          startedAt,
        });
        setActiveSessionId(pollSessionId);
        let migrationData: any;
        try {
          migrationData = await pollMigrationSession(pollSessionId, startedAt);
        } catch (e) {
          clearMigrationInflight();
          throw e; // handled by the outer catch, which surfaces it to the user
        }
        // Unmounted mid-poll: leave the record in place so the resume effect can pick
        // the run back up on the next mount instead of losing it.
        if (migrationData === null) return;
        clearMigrationInflight();
        response = { ...response, data: { success: true, data: migrationData } } as any;
      }

      if (response.data.success) {
        console.log('[Intelligence] Analysis succeeded:', { isSmart, isClean, hasData: !!response.data.data, sessionId: response.data.data?.sessionId });
        if (isClean) {
          setCleanResult(response.data.data);
          setCleanSubView('results');
          const sessionId = response.data.data?.sessionId;
          const localPath = response.data.data?.cleanedFilePath || '';
          if (localPath) {
            api.post('/api/intelligence/pipeline/run-extensions', {
              dfPath: localPath,
              config: {},
              session_id: sessionId || null,
              engagement_id: sessionId ? String(sessionId) : '',
            }).then(extRes => {
              if (extRes.data.success) {
                const qCount = extRes.data.data?.questions?.length || 0;
                if (qCount > 0) {
                  toast.success(`Pipeline found ${qCount} questions requiring your input`);
                }
              }
            }).catch(() => {});
          }
        } else if (isSmart) {
          setSmartResult(response.data.data);
          setSmartTab(response.data.data?.dashboard ? 'dashboards' : 'plan');
        } else if (isPptx) {
          setPptxResult(response.data.data);
        } else if (isContract) {
          setContractResult(response.data.data);
        } else {
          setResult(response.data.data);
        }
        if (response.data.data?.verification_metadata) {
          setVerificationMetadata(response.data.data.verification_metadata);
        } else {
          setVerificationMetadata(null);
        }
        if (response.data.data?.sessionId) setActiveSessionId(response.data.data.sessionId);
        fetchSessions();
      } else {
        console.error('[Intelligence] Backend returned success:false —', response.data.error);
        toast.error(response.data.error || 'Analysis failed');
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.message || 'Analysis failed';
      console.error('[Intelligence] handleAnalyze error:', err.response?.status, errMsg, err);
      // A toast is easy to miss after a run that took minutes — Migration Architect also
      // gets a panel that stays put until the next run.
      if (selectedMode.id === 'migration-architect') setMigrationError(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
      setMigrationProgress(null);
      analyzingRef.current = false;
    }
  };

  // ── PE Investment handlers ────────────────────────────────────────────────
  const handlePEAnalyze = async () => {
    if (!selectedPESubTool) return;
    if (!peCompanyName.trim()) { toast.error('Please enter a company name'); return; }
    setPELoading(true);
    setPEResult(null);
    try {
      const formData = new FormData();
      formData.append('companyName', peCompanyName);
      formData.append('sector', peSector);
      formData.append('dealSize', peDealSize);
      if (selectedPESubTool.id === 'financial-analysis') {
        formData.append('revenue', peRevenue);
        formData.append('ebitda', peEbitda);
        formData.append('revenueGrowth', peRevenueGrowth);
        formData.append('entryMultiple', peEntryMultiple);
        formData.append('exitMultiple', peExitMultiple);
        formData.append('holdPeriod', peHoldPeriod);
        formData.append('debtEquityRatio', peDebtEquityRatio);
      }
      if (selectedPESubTool.id === 'ic-memo') {
        formData.append('investmentThesis', peInvestmentThesis);
        formData.append('keyRisks', peKeyRisks);
      }
      peFiles.forEach(f => formData.append('files', f));
      const { data } = await api.post(`/api/investment-analysis/${selectedPESubTool.id}`, formData);
      if (data.success) {
        setPEResult({ ...data.data, mode: selectedPESubTool.id });
        toast.success('Analysis complete');
      } else {
        toast.error(data.error || 'Analysis failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Analysis failed');
    } finally {
      setPELoading(false);
    }
  };

  const handlePEExport = async (type: 'pdf' | 'docx' | 'pptx') => {
    if (!peResult || !selectedPESubTool) return;
    try {
      const content = peResult.memo || peResult.analysis || '';
      const name = peResult.companyName || peCompanyName || 'analysis';
      const resp = await api.post(
        `/api/investment-analysis/export-${type}`,
        { content, title: PE_SUBTOOLS.find(t => t.id === peResult.mode)?.name || 'Analysis', companyName: name, filename: `${name.replace(/\s+/g, '_')}_${peResult.mode}`, projectId: brandingProjectId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${name}_${peResult.mode}.${type}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch { toast.error('Export failed'); }
  };

  const handlePEChat = async () => {
    if (!chatInput.trim() || !peResult) return;
    const userMsg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const context = peResult.memo || peResult.analysis || '';
      const res = await api.post('/api/intelligence/chat', {
        prompt: userMsg,
        previousAnalysis: context,
        mode: peResult.mode,
      });
      if (res.data.success) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.data.response }]);
      }
    } catch {
      toast.error('Chat failed');
    } finally {
      setChatLoading(false);
    }
  };

  const handlePESendToCouncil = () => {
    if (!peResult) return;
    const content = (peResult.memo || peResult.analysis || '').slice(0, 8000);
    const toolName = PE_SUBTOOLS.find(t => t.id === peResult.mode)?.name || 'PE Analysis';
    const councilPrompt = `Review and critique this ${toolName} output. Identify gaps, challenge assumptions, and provide a multi-perspective evaluation:`;
    navigate('/app/ai-council', { state: { prompt: councilPrompt, inputData: content } });
    toast.success('Analysis sent to AI Council');
  };

  const handleExportExcel = async () => {
    if (!result?.analysis) return;
    setExportingExcel(true);
    try {
      const response = await api.post('/api/intelligence/export-excel', {
        analysis: result.analysis,
        modeName: result.mode.name,
        modelId: 'claude-4-sonnet'
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.mode.name.replace(/\s+/g, '-').toLowerCase()}-analysis.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded!');
    } catch {
      toast.error('Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPptx = async () => {
    if (!result?.analysis) return;
    setExportingPptx(true);
    try {
      const response = await api.post('/api/intelligence/export-pptx', {
        analysis: result.analysis,
        modeName: result.mode.name,
        modelId: 'claude-4-sonnet',
        projectId: brandingProjectId,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.mode.name.replace(/\s+/g, '-').toLowerCase()}-presentation.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PowerPoint downloaded!');
    } catch {
      toast.error('PowerPoint export failed');
    } finally {
      setExportingPptx(false);
    }
  };

  const handleSendToCouncil = () => {
    const analysisContent = (result?.analysis || smartResult?.deepInsight?.qualitativeAnalysis || '').slice(0, 8000);
    const modeLabel = result?.mode?.name || smartResult?.mode?.name || selectedMode.name;
    const councilPrompt = `Review and critique this ${modeLabel} output. Identify gaps, challenge assumptions, and provide a multi-perspective evaluation:`;
    navigate('/app/ai-council', { state: { prompt: councilPrompt, inputData: analysisContent } });
    toast.success('Analysis sent to AI Council');
  };


  // Capture the dashboard charts as PNGs for Word/PPTX export. The charts only
  // exist in the DOM while the Dashboards tab is mounted, so we force that tab
  // active (and wait for Recharts to lay out) before screenshotting, then
  // restore the previous tab. Returns whatever it captures; the caller decides
  // how to handle an empty result.
  const captureDashboardChartImages = async (
    skipLabels: string[] = ['KPI Overview', 'Key Insights'],
  ): Promise<{ label: string; base64: string; width: number; height: number }[]> => {
    const images: { label: string; base64: string; width: number; height: number }[] = [];

    // Make sure the dashboard is mounted. If a dashboard exists but the active
    // tab isn't 'dashboards', switch to it and give React + Recharts time to
    // render before we look for the chart nodes.
    const hasDashboard = !!smartResult?.dashboard;
    const previousTab = smartTab;
    const needsTabSwitch = hasDashboard && smartTab !== 'dashboards';
    if (needsTabSwitch) {
      setSmartTab('dashboards');
      // Two animation frames + a settle delay let Recharts' ResponsiveContainer
      // measure its size (it renders blank at zero dimensions otherwise).
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => r(null))));
      await new Promise(r => setTimeout(r, 600));
    }

    // html2canvas clips glyph bottoms on tight single-line boxes that use
    // `overflow: hidden` (e.g. chart legend labels), cutting the text in half.
    // Inject a capture-only stylesheet that relaxes the line box and overflow
    // for elements inside an export section, and remove it when we're done.
    const captureStyle = document.createElement('style');
    captureStyle.textContent = `
      [data-export-section], [data-export-section] *:not(svg):not(svg *) {
        overflow: visible !important;
      }
    `;
    document.head.appendChild(captureStyle);

    try {
      const html2canvas = (await import('html2canvas')).default;
      const skip = new Set(skipLabels);
      const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-export-section]'))
        .filter(el => !skip.has(el.getAttribute('data-export-section') || ''));
      const CAPTURE_WIDTH = 900;
      for (const el of sections) {
        const label = el.getAttribute('data-export-section') || 'Chart';
        // Recharts' ResponsiveContainer follows its parent's width, and the
        // dashboard lays charts out in a 2-column grid — so a chart cell can be
        // ~300-550px wide, which squishes bars into thin slivers when captured.
        // Pin the cell to a fixed wide size (and pull it out of the grid) for the
        // duration of the screenshot, let Recharts re-measure, then restore.
        const inline = {
          width: el.style.width,
          minWidth: el.style.minWidth,
          maxWidth: el.style.maxWidth,
          gridColumn: el.style.gridColumn,
          background: el.style.background,
        };
        try {
          el.style.width = `${CAPTURE_WIDTH}px`;
          el.style.minWidth = `${CAPTURE_WIDTH}px`;
          el.style.maxWidth = `${CAPTURE_WIDTH}px`;
          el.style.gridColumn = '1 / -1';
          if (!el.style.background) el.style.background = '#ffffff';
          el.scrollIntoView({ block: 'center', behavior: 'instant' });
          // Two animation frames let Recharts pick up the new width before the
          // settle delay; then poll for a real laid-out size.
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => r(null))));
          await new Promise(r => setTimeout(r, 450));
          let rect = el.getBoundingClientRect();
          for (let tries = 0; tries < 12 && (rect.width < 50 || el.scrollHeight < 10); tries++) {
            await new Promise(r => setTimeout(r, 150));
            rect = el.getBoundingClientRect();
          }
          if (rect.width < 50 || el.scrollHeight < 10) {
            console.warn(`[chart-export] Skipping "${label}" — chart did not lay out (size ${Math.round(rect.width)}x${el.scrollHeight}).`);
            continue;
          }
          const canvas = await html2canvas(el, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
            width: Math.round(rect.width),
            height: el.scrollHeight,
            windowWidth: document.documentElement.scrollWidth,
 
            onclone: (clonedDoc: Document) => {
              clonedDoc.querySelectorAll<HTMLElement>('[data-export-section], [data-export-section] *').forEach(node => {
                if (node.tagName === 'SVG' || node.closest('svg')) return;
                node.style.overflow = 'visible';
                const cs = window.getComputedStyle(node);
                if (node.childElementCount === 0 && (node.textContent || '').trim()) {
                  const fontPx = parseFloat(cs.fontSize) || 12;
                  node.style.lineHeight = `${Math.ceil(fontPx * 1.6)}px`;
                  node.style.paddingBottom = '2px';
                }
              });
            },
          });
          images.push({
            label,
            base64: canvas.toDataURL('image/png').replace('data:image/png;base64,', ''),
            width: Math.round(rect.width),
            height: Math.round(el.scrollHeight),
          });
        } catch (err) {
          // One chart failing shouldn't drop the rest.
          console.warn(`[chart-export] Failed to capture "${label}":`, err);
        } finally {
          el.style.width = inline.width;
          el.style.minWidth = inline.minWidth;
          el.style.maxWidth = inline.maxWidth;
          el.style.gridColumn = inline.gridColumn;
          el.style.background = inline.background;
        }
      }
    } catch (err) {
      console.warn('[chart-export] Chart capture unavailable:', err);
    } finally {
      captureStyle.remove();
      if (needsTabSwitch) setSmartTab(previousTab);
    }
    return images;
  };

  const handleSmartExportPptx = async () => {
    if (!smartResult) return;
    setExportingSmartPptx(true);
    try {
      const dashboardImages = await captureDashboardChartImages();
      if ((smartResult.dashboard?.charts?.length || 0) > 0 && dashboardImages.length === 0) {
        toast('Charts could not be captured — the PowerPoint will include the analysis without chart images.', { icon: '⚠️' });
      }
      const response = await api.post('/api/intelligence/export-pptx-smart', {
        executive: smartResult.executive,
        dashboard: smartResult.dashboard,
        deepInsight: smartResult.deepInsight,
        analysisPlan: smartResult.analysisPlan,
        fileName: smartResult.fileName,
        projectId: brandingProjectId,
        sessionId: smartResult.sessionId,
        dashboardImages,
      }, { responseType: 'blob', timeout: 1200000 });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'smart-data-analysis.pptx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PowerPoint downloaded!');
    } catch (err) {
      console.error('PPT export error:', err);
      toast.error('PowerPoint export failed');
    } finally {
      setExportingSmartPptx(false);
    }
  };

  const handleSmartExportExcel = async () => {
    if (!smartResult) return;
    setExportingSmartExcel(true);
    try {
      const response = await api.post('/api/intelligence/export-excel-smart', {
        qualitativeAnalysis: smartResult.deepInsight?.qualitativeAnalysis,
        quantitativeOutput: smartResult.deepInsight?.quantitative?.output,
        summary: smartResult.executive?.bullets,
        recommendations: smartResult.executive?.recommendations,
        analysisPlan: smartResult.analysisPlan,
        kpis: smartResult.executive?.kpis,
        executiveSummary: smartResult.executive?.summary,
        insights: smartResult.deepInsight?.insights,
        rawData: smartResult.rawData,
        fileName: smartResult.fileName,
        modelId: 'claude-4-sonnet'
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'smart-data-analysis.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded!');
    } catch {
      toast.error('Excel export failed');
    } finally {
      setExportingSmartExcel(false);
    }
  };

  const handleExportWord = async () => {
    if (!result?.analysis) return;
    setExportingWord(true);
    try {
      const response = await api.post('/api/intelligence/export-word', {
        analysis: result.analysis,
        modeName: result.mode.name,
        projectId: brandingProjectId,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.mode.name.replace(/\s+/g, '-').toLowerCase()}-analysis.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Word document downloaded!');
    } catch {
      toast.error('Word export failed');
    } finally {
      setExportingWord(false);
    }
  };

  const handleSmartExportWord = async () => {
    if (!smartResult) return;
    setExportingSmartWord(true);
    try {
      const dashboardImages = await captureDashboardChartImages();
      if ((smartResult.dashboard?.charts?.length || 0) > 0 && dashboardImages.length === 0) {
        toast('Charts could not be captured — the Word document will include the analysis without chart images.', { icon: '⚠️' });
      }
      const response = await api.post('/api/intelligence/export-word-smart', {
        executive: smartResult.executive,
        dashboard: smartResult.dashboard,
        deepInsight: smartResult.deepInsight,
        fileName: smartResult.fileName,
        projectId: brandingProjectId,
        dashboardImages,
      }, { responseType: 'blob', timeout: 1200000 });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'smart-data-analysis.docx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Word document downloaded!');
    } catch {
      toast.error('Word export failed');
    } finally {
      setExportingSmartWord(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    toast.loading('Generating PDF...', { id: 'pdf-gen' });

    // Build markdown → HTML with proper table support
    const mdToHtml = (md: string): string => {
      const lines = md.split('\n');
      const out: string[] = [];
      let inTable = false;
      let tableRows: string[][] = [];

      const flushTable = () => {
        if (!tableRows.length) return;
        let html = '<table><tbody>';
        tableRows.forEach((cells, i) => {
          if (i === 1 && cells.every(c => /^[-:]+$/.test(c.trim()))) return; // separator row
          const tag = i === 0 ? 'th' : 'td';
          html += '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
        });
        html += '</tbody></table>';
        out.push(html);
        tableRows = [];
        inTable = false;
      };

      lines.forEach(line => {
        if (line.startsWith('|')) {
          inTable = true;
          tableRows.push(line.split('|').filter((_, i, a) => i > 0 && i < a.length - 1));
          return;
        }
        if (inTable) flushTable();

        const l = line
          .replace(/^## (.+)$/, '<h2>$1</h2>')
          .replace(/^### (.+)$/, '<h3>$1</h3>')
          .replace(/^#### (.+)$/, '<h4>$1</h4>')
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/`(.+?)`/g, '<code>$1</code>');

        if (/^<h[234]>/.test(l)) { out.push(l); return; }
        if (/^[-*] (.+)/.test(line)) { out.push(`<li>${line.replace(/^[-*] /, '')}</li>`); return; }
        if (/^\d+\. (.+)/.test(line)) { out.push(`<li>${line.replace(/^\d+\. /, '')}</li>`); return; }
        if (line.trim() === '' || line.trim() === '---') { out.push('<br/>'); return; }
        out.push(`<p>${l}</p>`);
      });
      if (inTable) flushTable();
      return out.join('\n');
    };

    const htmlContent = mdToHtml(result.analysis || '');
    const modeColor = selectedMode.color;
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Create off-screen render div
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed; left: -9999px; top: 0;
      width: 794px; background: #ffffff;
      font-family: 'Segoe UI', Arial, sans-serif;
      color: #1a1a2e; padding: 48px;
      box-sizing: border-box;
    `;
    container.innerHTML = `
      <div style="border-bottom: 3px solid ${modeColor}; padding-bottom: 18px; margin-bottom: 28px;">
        <div style="display:flex; align-items:center; gap:10px; margin-bottom:8px;">
          <span style="background:${modeColor}18; color:${modeColor}; padding:3px 12px; border-radius:20px; font-size:11px; font-weight:700; letter-spacing:0.5px; text-transform:uppercase;">${result.mode.name}</span>
          <span style="font-size:12px; color:#888;">Jarvis Intelligence</span>
        </div>
        <div style="font-size:22px; font-weight:800; color:#221551; margin-bottom:4px;">${result.mode.name} Analysis</div>
        <div style="font-size:11px; color:#999;">Generated ${date}</div>
        ${result.hasFile ? `<div style="display:inline-block; background:#f0f4ff; color:#4a5568; padding:2px 10px; border-radius:12px; font-size:11px; margin-top:6px; border:1px solid #e2e8f0;">📎 ${result.fileName}</div>` : ''}
      </div>
      <style>
        h2 { font-size:16px; font-weight:700; color:#221551; margin:22px 0 8px; padding-bottom:5px; border-bottom:1.5px solid #f0f0f0; }
        h3 { font-size:14px; font-weight:700; color:#2d3748; margin:16px 0 6px; }
        h4 { font-size:13px; font-weight:600; color:#4a5568; margin:12px 0 4px; }
        p  { font-size:12px; line-height:1.75; color:#374151; margin-bottom:6px; }
        li { font-size:12px; line-height:1.75; color:#374151; margin-bottom:3px; margin-left:18px; list-style:disc; }
        strong { color:#221551; font-weight:700; }
        code  { background:#f3f4f6; color:#4f46e5; padding:1px 4px; border-radius:3px; font-size:11px; font-family:monospace; }
        table { width:100%; border-collapse:collapse; margin:12px 0; font-size:11px; }
        th { background:#f5f5fa; color:#221551; font-weight:700; padding:6px 10px; border:1px solid #e2e8f0; text-align:left; }
        td { padding:5px 10px; border:1px solid #e2e8f0; color:#374151; vertical-align:top; }
        tr:nth-child(even) td { background:#fafafa; }
        br { display:block; margin:4px 0; content:''; }
      </style>
      <div>${htmlContent}</div>
      <div style="margin-top:36px; padding-top:14px; border-top:1px solid #e5e7eb; display:flex; justify-content:space-between; font-size:10px; color:#aaa;">
        <span>Jarvis Intelligence — Powered by AI</span>
        <span>Confidential</span>
      </div>
    `;
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 794
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();   // 210mm
      const pageH = pdf.internal.pageSize.getHeight();  // 297mm

      // px per mm based on canvas width vs A4 width
      const pxPerMm = canvas.width / pageW;
      const pageHeightPx = pageH * pxPerMm;

      // Find best cut point: scan upward from idealY for a fully white row
      const findCutY = (idealY: number): number => {
        const ctx2 = canvas.getContext('2d')!;
        const scanPx = Math.floor(pageHeightPx * 0.12); // scan up to 12% of page height
        for (let y = Math.floor(idealY); y > Math.floor(idealY) - scanPx; y--) {
          if (y <= 0) return 0;
          const row = ctx2.getImageData(20, y, canvas.width - 40, 1).data;
          let allWhite = true;
          for (let i = 0; i < row.length; i += 4) {
            if (row[i] < 245 || row[i + 1] < 245 || row[i + 2] < 245) {
              allWhite = false;
              break;
            }
          }
          if (allWhite) return y;
        }
        return Math.floor(idealY); // fallback
      };

      // Build cut points
      const cuts: number[] = [0];
      let pos = pageHeightPx;
      while (pos < canvas.height) {
        const cut = findCutY(pos);
        cuts.push(cut);
        pos = cut + pageHeightPx;
      }
      cuts.push(canvas.height);

      // Render each page slice
      for (let i = 0; i < cuts.length - 1; i++) {
        const startY = cuts[i];
        const endY = cuts[i + 1];
        const sliceH = endY - startY;

        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceH;
        const sCtx = slice.getContext('2d')!;
        sCtx.fillStyle = '#ffffff';
        sCtx.fillRect(0, 0, slice.width, slice.height);
        sCtx.drawImage(canvas, 0, startY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const sliceHeightMm = (sliceH / pxPerMm);
        if (i > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, pageW, sliceHeightMm);
      }

      pdf.save(`${selectedMode.id}-analysis-${Date.now()}.pdf`);
      toast.success('PDF downloaded!', { id: 'pdf-gen' });
    } catch (err) {
      toast.error('PDF generation failed', { id: 'pdf-gen' });
    } finally {
      document.body.removeChild(container);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAnalyze();
    }
  };

  const handleGeneratePrompt = async () => {
    if (!promptDescription.trim()) {
      toast.error('Please describe what you want to do');
      return;
    }
    setGeneratingPrompt(true);
    try {
      const activeMode = selectedSubTool ? selectedSubTool.name : selectedMode.name;
      const res = await api.post('/api/ai-council/generate-prompt', {
        description: `[Context: ${activeMode}] ${promptDescription}`,
        selectedModels: [],
      });
      setGeneratedPromptPreview(res.data.prompt);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to generate prompt');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleUseGeneratedPrompt = () => {
    setPrompt(generatedPromptPreview);
    setShowPromptHelper(false);
    setPromptDescription('');
    setGeneratedPromptPreview('');
  };

  const handleClosePromptHelper = () => {
    setShowPromptHelper(false);
    setPromptDescription('');
    setGeneratedPromptPreview('');
  };

  // ── Session history handlers ──────────────────────────────────────────────
  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const modeId = activeHistoryModeRef.current;
      const [intRes, dsRes, gfdRes, pmsRes] = await Promise.all([
        api.get('/api/intelligence/sessions', { params: modeId ? { mode_id: modeId } : {} }),
        api.get('/api/datasim/sessions').catch(() => ({ data: { sessions: [] } })),
        api.get('/api/gfd-intake/sessions').catch(() => ({ data: { sessions: [] } })),
        listProcessMapSessions().catch(() => ({ sessions: [], totalCount: 0 })),
      ]);
      setSessions(intRes.data.sessions || []);
      setDatasimSessions(dsRes.data.sessions || []);
      setGfdSessions(gfdRes.data.sessions || []);
      setPmsSessions(pmsRes.sessions || []);
      // Session History bug fix (June 2026): use the backend's authoritative
      // totalCount when present; fall back to the returned list length for
      // back-compat with cached responses from before the endpoint change.
      const intCount = typeof intRes.data.totalCount === 'number'
        ? intRes.data.totalCount
        : (intRes.data.sessions || []).length;
      const dsCount = typeof dsRes.data.totalCount === 'number'
        ? dsRes.data.totalCount
        : (dsRes.data.sessions || []).length;
      const gfdCount = typeof gfdRes.data.totalCount === 'number'
        ? gfdRes.data.totalCount
        : (gfdRes.data.sessions || []).length;
      const pmsCount = typeof pmsRes.totalCount === 'number'
        ? pmsRes.totalCount
        : (pmsRes.sessions || []).length;
      setSessionsTotalCount(intCount);
      setDatasimTotalCount(dsCount);
      setGfdTotalCount(gfdCount);
      setPmsTotalCount(pmsCount);
    } catch {
      // silent
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (id: number) => {
    try {
      const res = await api.get(`/api/intelligence/sessions/${id}`);
      const s = res.data.session;
      const mode = MODES.find(m => m.id === s.mode_id) || MODES[0];
      setSelectedMode(mode);
      setSelectedSubTool(null);
      setSelectedPESubTool(null);
      setPrompt(s.prompt || '');
      setActiveSessionId(id);
      setIsCollaboratorSession(!!s.is_collaborator);
      setShowHistory(false);
      setChatMessages([]);
      setShowPromptHelper(false);
      setGeneratedPromptPreview('');
      setPromptDescription('');
      setIsExpanded(false);

      if (s.mode_id === 'data-cleaning') {
        setResult(null);
        setSmartResult(null);
        let meta: any = {};
        if (s.metadata) {
          try { meta = JSON.parse(s.metadata); } catch { /* ignore */ }
        }

        const isV2 = meta && meta.version === 2 && Array.isArray(meta.files);

        if (isV2) {
          const allUnits: any[] = [];
          for (const file of meta.files) {
            for (const sheet of (file.sheets || [])) {
              allUnits.push({ file, sheet });
            }
          }
          const lead = allUnits.find(u => u.sheet.status !== 'failed') || allUnits[0] || null;
          const leadSheet = lead?.sheet || {};
          const leadStats = leadSheet.stats || {};
          const rollup = meta.rollup || {};

          const legacyStats = {
            ...leadStats,
            qualityScoreBefore: leadSheet.qualityScoreBefore || rollup.qualityScoreBefore_weighted || 0,
            qualityScoreAfter: leadSheet.qualityScoreAfter || rollup.qualityScoreAfter_weighted || 0,
            totalRows: leadSheet.rows_total || leadStats.totalRows || 0,
            rowsAfterCleaning: leadSheet.rows_after || leadStats.rowsAfterCleaning || leadSheet.rows_total || 0,
            columnsProcessed: leadSheet.columns_processed || leadStats.columnsProcessed || 0,
          };

          const originalFileNames = meta.files.map((f: any) => f.file_name);

          setCleanResult({
            version: 2,
            pipeline: meta.pipeline || 'tabular',
            files: meta.files,
            rollup,
            units: allUnits.length,
            sheetsTotal: rollup.total_sheets || allUnits.length,
            unitsFailed: rollup.failed_units || allUnits.filter(u => u.sheet.status === 'failed').length,
            smeFilesUsed: meta.smeFilesUsed || [],
            smeNotesProvided: !!meta.smeNotesProvided,
            smeDerivedInsights: meta.smeDerivedInsights || [],
            classification: meta.classification || null,
            summary: s.analysis || '',
            issuesFound: leadSheet.issuesFound || [],
            stakeholderQuestions: leadSheet.stakeholderQuestions || [],
            canonicalMapping: leadSheet.canonicalMapping || [],
            changeLog: meta.changeLog || [],
            cleaningLog: meta.cleaningLog || leadSheet.cleaningLog || '',
            previewBefore: leadSheet.previewBefore || '',
            previewAfter: leadSheet.previewAfter || '',
            stats: legacyStats,
            cleanedFileUrl: leadSheet.cleanedFileUrl
              || (leadSheet.cleanedFileS3Key ? `/api/intelligence/download-cleaned-s3/${encodeURIComponent(leadSheet.cleanedFileS3Key)}` : null),
            cleanedFilePath: leadSheet.cleanedFilePath || null,
            cleanedFileName: leadSheet.cleanedFileName || '',
            originalFileNames,
            pythonCode: leadSheet.pythonCode || '',
            mode: { id: 'data-cleaning', name: 'Data Cleaning & Reconciliation' },
            sessionId: id,
            crossUnitFindings: meta.crossUnitFindings || null,
          });
        } else {
          const metaStats = meta.stats && typeof meta.stats === 'object' ? meta.stats : {};
          const issuesFound = meta.issuesFound || [];
          const changeLog = meta.changeLog || [];
          const issuesCount = issuesFound.length;
          const fixedIssues = issuesFound.filter((i: any) => i.status === 'fixed').length;
          const pendingIssues = issuesFound.filter((i: any) => i.status === 'needs_review' || i.status === 'cannot_fix').length;

          const maxAffectedRows = issuesFound.reduce((max: number, i: any) => Math.max(max, i.count || 0), 0);
          const totalChangeLogRows = changeLog.reduce((sum: number, cl: any) => sum + (cl.rowsAffected || 0), 0);

          const resolvedTotalRows =
            (metaStats.totalRows && metaStats.totalRows > 0 ? metaStats.totalRows : 0) ||
            (metaStats.rowsAfterCleaning && metaStats.rowsAfterCleaning > 0 ? metaStats.rowsAfterCleaning : 0) ||
            maxAffectedRows ||
            totalChangeLogRows ||
            0;

          const resolvedRowsAfter =
            (metaStats.rowsAfterCleaning && metaStats.rowsAfterCleaning > 0 ? metaStats.rowsAfterCleaning : 0) ||
            resolvedTotalRows;

          const resolvedColumns =
            (metaStats.columnsProcessed && metaStats.columnsProcessed > 0 ? metaStats.columnsProcessed : 0) ||
            (issuesFound.length > 0 ? new Set(issuesFound.map((i: any) => i.column).filter(Boolean)).size : 0);

          const derivedBefore = issuesCount > 0 ? Math.max(40, 100 - Math.min(50, issuesCount * 4) - Math.min(10, pendingIssues * 2)) : 72;
          const derivedAfter = issuesCount > 0 ? Math.max(derivedBefore + 5, Math.min(100, 100 - pendingIssues * 3)) : 95;

          const resolvedBefore =
            (metaStats.qualityScoreBefore && metaStats.qualityScoreBefore > 0 ? metaStats.qualityScoreBefore : 0) ||
            (meta.qualityScoreBefore && meta.qualityScoreBefore > 0 ? meta.qualityScoreBefore : 0) ||
            derivedBefore;

          const resolvedAfter =
            (metaStats.qualityScoreAfter && metaStats.qualityScoreAfter > 0 ? metaStats.qualityScoreAfter : 0) ||
            (meta.qualityScoreAfter && meta.qualityScoreAfter > 0 ? meta.qualityScoreAfter : 0) ||
            derivedAfter;

          const mergedStats = {
            ...metaStats,
            qualityScoreBefore: resolvedBefore,
            qualityScoreAfter: resolvedAfter,
            totalRows: resolvedTotalRows,
            rowsAfterCleaning: resolvedRowsAfter,
            columnsProcessed: resolvedColumns,
            duplicatesRemoved: metaStats.duplicatesRemoved || (changeLog.filter((cl: any) => /dup/i.test(cl.operation || '')).reduce((s: number, cl: any) => s + (cl.rowsAffected || 0), 0)),
            nullsFilled: metaStats.nullsFilled || (changeLog.filter((cl: any) => /null|missing/i.test(cl.operation || '')).reduce((s: number, cl: any) => s + (cl.rowsAffected || 0), 0)),
            typesFixed: metaStats.typesFixed || fixedIssues,
            formatsStandardized: metaStats.formatsStandardized || (changeLog.filter((cl: any) => /format|standard/i.test(cl.operation || '')).reduce((s: number, cl: any) => s + (cl.rowsAffected || 0), 0)),
          };

          setCleanResult({
            pipeline: meta.pipeline || 'tabular',
            summary: s.analysis || meta.summary || '',
            issuesFound,
            stakeholderQuestions: meta.stakeholderQuestions || [],
            changeLog,
            cleaningLog: meta.cleaningLog || '',
            previewBefore: meta.previewBefore || '',
            previewAfter: meta.previewAfter || '',
            stats: mergedStats,
            cleanedFileUrl: meta.cleanedFileName ? `/api/intelligence/download-cleaned/${meta.cleanedFileName}` : null,
            cleanedFilePath: meta.cleanedFilePath || null,
            cleanedFileName: meta.cleanedFileName || '',
            originalFileNames: s.file_name ? s.file_name.split(', ') : [],
            pythonCode: meta.pythonCode || '',
            sessionId: id,
            crossUnitFindings: meta.crossUnitFindings || null,
          });
        }
      } else if (s.mode_id === 'smart-data-analyst') {
        setResult(null);
        setCleanResult(null);
        let meta: Partial<SmartResult> = {};
        if (s.metadata) {
          try { meta = JSON.parse(s.metadata); } catch { /* ignore */ }
        }
        setSmartResult({
          sessionId: id,
          analysisPlan: meta.analysisPlan || '',
          dataQuality: meta.dataQuality || null,
          rawData: meta.rawData || null,
          hasFile: s.has_file,
          fileName: s.file_name,
          mode: { id: s.mode_id, name: s.mode_name },
          executive: meta.executive || null,
          dashboard: meta.dashboard || null,
          deepInsight: meta.deepInsight || {
            qualitativeAnalysis: (meta as any).qualitativeAnalysis || s.analysis || '',
            recommendations: (meta as any).recommendations || '',
            insights: [],
            quantitative: (meta as any).quantitative || { code: '', output: '', errors: '' },
          },
        });
        setSmartTab('plan');
        toast('Saved analysis loaded. Re-upload your file and click Analyze to re-run.', { icon: '↺' });
      } else if (s.mode_id === 'contract-analysis') {
        // Contract Analysis stores its per-file results array in
        // session.metadata.results. The generic fallback below only sets a
        // summary string into `result`, which left the right-hand pane empty
        // because ContractAnalysisResults reads contractResult.results[] —
        // forcing the user to re-upload and re-run to see their own past
        // session. Branch here so a click in the History sidebar rehydrates
        // the full clause/quant payload and the download buttons (PDF / Excel
        // / PPT / Word / AI Council) work against the saved data immediately.
        setResult(null);
        setSmartResult(null);
        setCleanResult(null);
        let meta: any = {};
        if (s.metadata) {
          try { meta = JSON.parse(s.metadata); } catch { /* ignore */ }
        }
        setContractResult({
          sessionId: id,
          results: Array.isArray(meta.results) ? meta.results : [],
        });
      } else {
        setSmartResult(null);
        setCleanResult(null);
        setResult({ analysis: s.analysis, mode: { id: s.mode_id, name: s.mode_name }, hasFile: s.has_file, fileName: s.file_name });
      }
    } catch {
      toast.error('Failed to load session');
    }
  };

  const deleteSession = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/intelligence/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      // Session History bug fix: decrement the authoritative total too so the
      // badge ticks down immediately. Floored at 0 to defend against double-
      // delete races where the count would otherwise dip negative.
      setSessionsTotalCount(prev => Math.max(0, prev - 1));
      if (activeSessionId === id) { setResult(null); setActiveSessionId(null); setIsCollaboratorSession(false); }
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleNewSession = () => {
    localStorage.setItem('datasim_wants_new', 'true');
    localStorage.removeItem('datasim_session_id');
    localStorage.setItem('gfd_wants_new', 'true');
    localStorage.removeItem('gfd_session_id');
    localStorage.setItem('pms_wants_new', 'true');
    localStorage.removeItem('pms_session_id');
    setPmsResetKey(k => k + 1);
    setPmsSessionId(null);
    // Starting fresh means abandoning any Migration Architect run still being polled —
    // otherwise the resume effect would drop its result into the cleared panel.
    migrationPollAbortRef.current = true;
    clearMigrationInflight();
    setMigrationError(null);
    setMigrationProgress(null);
    setDatasimResetKey(k => k + 1);
    setGfdResetKey(k => k + 1);
    setSelectedSubTool(null);
    setSelectedPESubTool(null);
    setPEResult(null);
    setResult(null);
    setSmartResult(null);
    setCleanResult(null);
    setContractResult(null);
    setPptxResult(null);
    setPrompt('');
    setFiles([]);
    setPEFiles([]);
    setChatMessages([]);
    setActiveSessionId(null);
    setIsCollaboratorSession(false);
    setDatasimSessionId(null);
    setGfdSessionId(null);
    setShowHistory(false);
    setShowPromptHelper(false);
    setGeneratedPromptPreview('');
    setPromptDescription('');
    setIsExpanded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const looksLikeChartRequest = (text: string): boolean => {
    return /\b(chart|plot|graph|visuali[sz]e|visuali[sz]ation|bar chart|line chart|donut|pie chart|histogram|scatter|heat ?map|trend line|stacked bar|waterfall)\b/i.test(text);
  };

  const handleChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    // Auto-route chart requests to the chart builder when a saved session exists.
    if (smartResult?.sessionId && !peResult && looksLikeChartRequest(chatInput)) {
      return handleGenerateChart();
    }
    const userMsg = { role: 'user' as const, content: chatInput.trim() };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const originalAnalysis = result?.analysis || smartResult?.deepInsight?.qualitativeAnalysis || '';
      const modeId = selectedMode.id === 'smart-data-analyst' ? 'data-analyst' : selectedMode.id;
      const response = await api.post('/api/intelligence/chat', {
        mode: modeId,
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        originalAnalysis,
        modelId: 'claude-4-sonnet',
        sessionId: smartResult?.sessionId,
      });
      if (response.data.success) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.data.reply }]);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      } else {
        toast.error(response.data.error || 'Chat failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Chat failed');
    } finally {
      setChatLoading(false);
    }
  };

  // Persist an inline-edited executive summary. The export endpoints read
  // smartResult.executive directly, so updating state also flows the edit
  // into any subsequently downloaded document.
  const handleSaveExecSummary = async () => {
    if (!smartResult || !smartResult.executive) return;
    setSavingExecSummary(true);
    try {
      if (smartResult.sessionId) {
        await api.patch(`/api/intelligence/sessions/${smartResult.sessionId}/executive-summary`, {
          bullets: execSummaryDraft,
        });
      }
      setSmartResult({
        ...smartResult,
        executive: { ...smartResult.executive, bullets: execSummaryDraft },
      });
      setEditingExecSummary(false);
      toast.success('Executive summary updated');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save summary');
    } finally {
      setSavingExecSummary(false);
    }
  };

  const handleGenerateChart = async () => {
    if (!chatInput.trim() || chatLoading) return;
    if (!smartResult?.sessionId) {
      toast.error('Chart generation needs a saved Smart Data Analyst session.');
      return;
    }
    const request = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: request }]);
    setChatInput('');
    setChatLoading(true);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const response = await api.post('/api/intelligence/generate-chart', {
        sessionId: smartResult.sessionId,
        request,
      });
      if (response.data.success && response.data.data?.chart) {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Here is the chart built from this session’s data:',
          chart: response.data.data.chart as ChartSpec,
        }]);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      } else {
        const msg = response.data.error || 'Could not build that chart.';
        setChatMessages(prev => [...prev, { role: 'assistant', content: msg }]);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Could not build that chart.';
      setChatMessages(prev => [...prev, { role: 'assistant', content: msg }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleAddChartToDashboard = (chart: ChartSpec) => {
    if (!smartResult?.dashboard) {
      toast.error('No dashboard available to add the chart to.');
      return;
    }
    setSmartResult(prev => {
      if (!prev?.dashboard) return prev;
      return {
        ...prev,
        dashboard: {
          ...prev.dashboard,
          charts: [...(prev.dashboard.charts || []), chart],
        },
      };
    });
    toast.success('Chart added to the Dashboards tab.');
  };

  const handleDownloadChart = async (chartId: string, title: string) => {
    const el = document.getElementById(chartId);
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${(title || 'chart').replace(/[^a-z0-9]+/gi, '_').slice(0, 60)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      toast.error('Could not download the chart image.');
    }
  };

  // Detects "SLIDE N — Title" / "SLIDE N - Title" / "SLIDE N: Title" markers,
  // including when prefixed by a markdown heading. Used to decide whether to
  // show the "Download as PPTX" affordance on an assistant message.
  const hasSlideMarkers = (text: string): boolean =>
    /(^|\n)\s*(?:#{1,6}\s*)?SLIDE\s+\d+\s*[—\-:]/i.test(text || '');

  const [chatPptxDownloadingIdx, setChatPptxDownloadingIdx] = useState<number | null>(null);

  const handleDownloadChatAsPptx = async (idx: number, content: string) => {
    setChatPptxDownloadingIdx(idx);
    try {
      const titleMatch = content.match(/^\s*#{1,6}\s*(.+?)\s*$/m);
      const deckTitle = titleMatch ? titleMatch[1].replace(/[*_`]/g, '').trim() : 'Executive Update';
      const sourceFileName = files[0]?.name || smartResult?.fileName || undefined;

      const res = await api.post(
        '/api/intelligence/export-chat-pptx',
        { markdown: content, deckTitle, fileName: sourceFileName },
        { responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${deckTitle.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 60) || 'Executive_Update'}_${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PPTX downloaded');
    } catch (err: any) {
      // Backend returns JSON inside a Blob on error — try to read it for a useful message
      let msg = 'PPTX export failed';
      try {
        if (err?.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.error) msg = parsed.error;
        } else if (err?.response?.data?.error) {
          msg = err.response.data.error;
        } else if (err?.message) {
          msg = err.message;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setChatPptxDownloadingIdx(null);
    }
  };

  const Icon = ICON_MAP[selectedMode.icon] || TrendingUp;

  return (
    <div className="space-y-6">
      {smartRouter.inRouter && (
        <SmartRouterBanner
          stepLabel={`${selectedMode.name} (Smart Router)`}
          canFinish={!!activeSessionId}
          disabledReason="Run the analysis (or save a session) before finishing the routed step."
          buildCompletionPayload={async () => {
            const narrative = (result?.analysis || smartResult?.deepInsight?.qualitativeAnalysis || '') as string;
            const analysisPayload = result
              ? {
                  findings: (result as any)?.findings || [],
                  tables: (result as any)?.tables || [],
                  gaps: (result as any)?.gaps || [],
                  narrative,
                }
              : null;
            const cleanedPayload = cleanResult
              ? {
                  headers: (cleanResult as any)?.cleaned?.headers || (cleanResult as any)?.headers || [],
                  rows: (cleanResult as any)?.cleaned?.rows || (cleanResult as any)?.rows || [],
                  summary: (cleanResult as any)?.summary || null,
                  reconciliation: (cleanResult as any)?.reconciliation || null,
                }
              : null;
            await smartRouter.finishStep(
              activeSessionId
                ? {
                    refs: [
                      {
                        kind: 'intelligence_session',
                        session_id: activeSessionId,
                        mode_id: selectedMode.id,
                        label: `${selectedMode.name} session`,
                      },
                    ],
                    output_payload: {
                      kind: 'intelligence_session',
                      session_id: activeSessionId,
                      mode_id: selectedMode.id,
                      analysis: analysisPayload,
                      analysis_narrative: narrative || null,
                      cleaned: cleanedPayload,
                      smart_data_analyst: smartResult
                        ? {
                            executive: (smartResult as any)?.executive || null,
                            dashboard: (smartResult as any)?.dashboard || null,
                            deepInsight: (smartResult as any)?.deepInsight || null,
                          }
                        : null,
                    },
                    verifiable_text: narrative || null,
                  }
                : undefined,
              activeSessionId
                ? `Finished ${selectedMode.name} session #${activeSessionId}.`
                : undefined,
            );
            return null;
          }}
        />
      )}
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-electric-blue" />
            <h1 className="text-3xl font-bold text-super-dark-purple">Jarvis Intelligence</h1>
            {verificationMetadata && (
              <VerificationBadge
                metadata={verificationMetadata}
                size="sm"
                onClick={() => setShowVerificationPanel(!showVerificationPanel)}
              />
            )}
          </div>
          <p className="text-gray-500 text-sm">
            Select an intelligence mode, enter your question, and optionally upload a file for analysis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewSession}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-electric-blue text-white hover:opacity-90 transition-all whitespace-nowrap shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            New Session
          </button>
          <SessionCollaboratorsList
            variant="dropdown"
            sessionId={activeSessionId}
            isOwner={!isCollaboratorSession}
            getCollaborators={getIntelligenceCollaborators}
            getAvailable={getAvailableIntelligenceCollaborators}
            getAllUsers={getTenantUsers}
            addCollaborator={addIntelligenceCollaborator}
            removeCollaborator={removeIntelligenceCollaborator}
            onError={msg => toast.error(msg)}
            onCollaboratorsChanged={fetchSessions}
          />
          <button
            onClick={() => { setShowHistory(p => !p); if (!showHistory) fetchSessions(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              showHistory ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-700'
            }`}
          >
            <History className="w-4 h-4" />
            History
            {/* Session History bug fix (June 2026): show the authoritative
                total returned by the backend so the badge ticks up the moment
                a new session lands in the DB, not just when the dropdown's
                paginated slice happens to include it. Falls back to the
                rendered-list length when the backend total isn't available
                (older responses, network errors). */}
            {((sessionsTotalCount || sessions.length) + (selectedMode.id === 'risk-analyzer' ? (datasimTotalCount || datasimSessions.length) : 0) + (selectedMode.id === 'gfd-intake' ? (gfdTotalCount || gfdSessions.length) : 0) + (selectedMode.id === 'process-map-synthesis' ? (pmsTotalCount || pmsSessions.length) : 0)) > 0 && (
              <span className="bg-electric-blue text-white text-xs px-1.5 py-0.5 rounded-full">{(sessionsTotalCount || sessions.length) + (selectedMode.id === 'risk-analyzer' ? (datasimTotalCount || datasimSessions.length) : 0) + (selectedMode.id === 'gfd-intake' ? (gfdTotalCount || gfdSessions.length) : 0) + (selectedMode.id === 'process-map-synthesis' ? (pmsTotalCount || pmsSessions.length) : 0)}</span>
            )}
          </button>
        </div>
      </div>

      {/* Sections hidden when result is expanded */}
      {!isExpanded && <>

      {/* Session History Panel */}
      {showHistory && (() => {
        // Bug fix: scope history to active module. DataSim shown only when on risk-analyzer;
        // Intelligence sessions filtered to the current mode_id. Sort all results date desc.
        const isDataSim = selectedMode.id === 'risk-analyzer';
        const isGfd = selectedMode.id === 'gfd-intake';
        const isPms = selectedMode.id === 'process-map-synthesis';
        const activeHistoryMode = selectedSubTool?.id || selectedPESubTool?.id || selectedMode.id;
        const filteredIntel = isPms ? [] : sessions.filter(s => s.mode_id === activeHistoryMode);
        const filteredDatasim = isDataSim ? datasimSessions : [];
        const filteredGfd = isGfd ? gfdSessions : [];
        const filteredPms = isPms ? pmsSessions : [];
        const allItems = [
          ...filteredDatasim.map(ds => ({ _type: 'datasim' as const, _date: new Date(ds.updated_at), ds })),
          ...filteredGfd.map(gs => ({ _type: 'gfd' as const, _date: new Date(gs.updated_at), gs })),
          ...filteredPms.map(ps => ({ _type: 'pms' as const, _date: new Date(ps.updated_at || 0), ps })),
          ...filteredIntel.map(s => ({ _type: 'intelligence' as const, _date: new Date(s.created_at), s })),
        ].sort((a, b) => b._date.getTime() - a._date.getTime());
        const isEmpty = allItems.length === 0;
        return (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <History className="w-4 h-4 text-electric-blue" />
                Past Sessions
                <span className="text-xs font-normal text-gray-400 ml-1">— {selectedMode.name}</span>
              </div>
              <span className="text-xs text-gray-400">Click to reload a session</span>
            </div>
            {loadingSessions && isEmpty ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm p-5">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : isEmpty ? (
              <p className="text-sm text-gray-400 p-5 text-center">No past sessions for {selectedMode.name}</p>
            ) : (
              <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                {allItems.map(item => {
                  if (item._type === 'datasim') {
                    const ds = item.ds;
                    return (
                      <div
                        key={`ds-${ds.id}`}
                        title={`DataSim · ${new Date(ds.updated_at).toLocaleString()}`}
                        onClick={() => {
                          const mode = MODES.find(m => m.id === 'risk-analyzer');
                          if (mode) setSelectedMode(mode);
                          setDatasimSessionId(ds.id);
                          localStorage.setItem('datasim_session_id', String(ds.id));
                          localStorage.removeItem('datasim_wants_new');
                          setShowHistory(false);
                        }}
                        className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${datasimSessionId === ds.id ? 'bg-red-50' : ''}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${datasimSessionId === ds.id ? 'text-red-500' : 'text-gray-300'}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600">DataSim</span>
                              <span className="text-sm text-gray-700 truncate max-w-xs">{ds.project_name || ds.title}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{ds.status === 'simulation_run' ? 'Complete' : ds.status === 'interview_done' ? 'Interview Done' : 'In Progress'}</span>
                            </div>
                            <span className="text-xs text-gray-400">{new Date(ds.updated_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await api.delete(`/api/datasim/sessions/${ds.id}`);
                              setDatasimSessions(prev => prev.filter(x => x.id !== ds.id));
                              setDatasimTotalCount(prev => Math.max(0, prev - 1));
                              if (datasimSessionId === ds.id) setDatasimSessionId(null);
                              toast.success('Session deleted');
                            } catch { toast.error('Failed to delete'); }
                          }}
                          className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  }
                  if (item._type === 'gfd') {
                    const gs = item.gs;
                    return (
                      <div
                        key={`gfd-${gs.id}`}
                        title={`GFD · ${new Date(gs.updated_at).toLocaleString()}`}
                        onClick={() => {
                          const mode = MODES.find(m => m.id === 'gfd-intake');
                          if (mode) setSelectedMode(mode);
                          setGfdSessionId(gs.id);
                          localStorage.setItem('gfd_session_id', String(gs.id));
                          localStorage.removeItem('gfd_wants_new');
                          setShowHistory(false);
                        }}
                        className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${gfdSessionId === gs.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${gfdSessionId === gs.id ? 'text-indigo-600' : 'text-gray-300'}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">GFD</span>
                              <span className="text-sm text-gray-700 truncate max-w-xs">{gs.initiative_name || gs.title}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{gs.status}</span>
                            </div>
                            <span className="text-xs text-gray-400">{new Date(gs.updated_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await api.delete(`/api/gfd-intake/sessions/${gs.id}`);
                              setGfdSessions(prev => prev.filter(x => x.id !== gs.id));
                              setGfdTotalCount(prev => Math.max(0, prev - 1));
                              if (gfdSessionId === gs.id) setGfdSessionId(null);
                              toast.success('Session deleted');
                            } catch { toast.error('Failed to delete'); }
                          }}
                          className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  }
                  if (item._type === 'pms') {
                    const ps = item.ps;
                    const fileNames = (ps.documents || []).map((doc) => doc.fileName).filter(Boolean);
                    const running = (ps.documents || []).some((doc) => doc.status !== 'complete' && doc.status !== 'failed' && doc.status !== 'stopped');
                    const statusLabel = running ? 'In progress' : ps.status === 'complete' ? 'Complete' : 'In progress';
                    return (
                      <div
                        key={`pms-${ps.id}`}
                        title={`Process Map Synthesis · ${ps.updated_at ? new Date(ps.updated_at).toLocaleString() : ''}`}
                        onClick={() => {
                          const mode = MODES.find(m => m.id === 'process-map-synthesis');
                          if (mode) setSelectedMode(mode);
                          setPmsSessionId(ps.id);
                          localStorage.setItem('pms_session_id', String(ps.id));
                          localStorage.removeItem('pms_wants_new');
                          setShowHistory(false);
                        }}
                        className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${pmsSessionId === ps.id ? 'bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${pmsSessionId === ps.id ? 'text-electric-blue' : 'text-gray-300'}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">SOP</span>
                              <span className="text-sm text-gray-700 truncate max-w-xs">{fileNames[0] || ps.title || 'Process Map Synthesis'}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{statusLabel}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              {ps.updated_at && <span className="text-xs text-gray-400">{new Date(ps.updated_at).toLocaleString()}</span>}
                              {fileNames.length > 0 && (
                                <span className="text-xs text-gray-500 truncate max-w-sm">{fileNames.join(', ')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await deleteProcessMapSession(ps.id);
                              setPmsSessions(prev => prev.filter(x => x.id !== ps.id));
                              setPmsTotalCount(prev => Math.max(0, prev - 1));
                              if (pmsSessionId === ps.id) {
                                setPmsSessionId(null);
                                localStorage.removeItem('pms_session_id');
                                localStorage.setItem('pms_wants_new', 'true');
                              }
                              toast.success('Session deleted');
                            } catch { toast.error('Failed to delete'); }
                          }}
                          className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 rounded transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  }
                  const s = item.s;
                  const modeColor = MODES.find(m => m.id === s.mode_id)?.color || '#6366f1';
                  return (
                    <div
                      key={s.id}
                      title={`${s.mode_name} · ${new Date(s.created_at).toLocaleString()}${s.prompt ? ` · "${s.prompt.slice(0, 80)}${s.prompt.length > 80 ? '…' : ''}"` : ''}${s.has_file && s.file_name ? ` · ${s.file_name}` : ''}`}
                      onClick={() => loadSession(s.id)}
                      className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${activeSessionId === s.id ? 'bg-indigo-50' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${activeSessionId === s.id ? 'text-electric-blue' : 'text-gray-300'}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: modeColor + '18', color: modeColor }}>{s.mode_name}</span>
                            <span className="text-sm text-gray-700 truncate max-w-xs">{s.title}</span>
                            {s.is_collaborator && (
                              <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">shared</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-gray-400">{new Date(s.created_at).toLocaleString()}</span>
                            {s.has_file && s.file_name && (
                              <div className="flex flex-wrap gap-1">
                                {s.file_name.split(', ').map((name, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-xs text-blue-700 max-w-[200px]">
                                    <Paperclip className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{name}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {s.collaborator_names && s.collaborator_names.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-500">Collaborators</span>
                                <span className="text-[10px] text-purple-400">{s.collaborator_names.join(' · ')}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteSession(s.id, e)}
                        className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Collaborator banner ─────────────────────────────────────────── */}
      {isCollaboratorSession && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
          <p className="text-blue-900 font-medium">You are a collaborator on this session</p>
          <p className="text-blue-700 text-sm">You have full access to all features and can contribute to this Jarvis Intelligence session.</p>
        </div>
      )}

      {/* Mode Selection Cards — compact, no description, wraps into rows */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {MODES.map((mode) => {
          const ModeIcon = ICON_MAP[mode.icon] || TrendingUp;
          const isPipeline = !!mode.pipelineKey;
          const isLaunching = isPipeline && launchingPipelineKey === mode.pipelineKey;
          const isSelected = !isPipeline && selectedMode.id === mode.id;
          return (
            <div key={mode.id} className="relative flex flex-col">
              <button
                  disabled={isLaunching}
                  onClick={() => {
                    if (mode.linkTo) {
                      navigate(mode.linkTo);
                      return;
                    }
                    if (mode.pipelineKey) {
                      handleLaunchPipeline(mode);
                      return;
                    }
                    setSelectedMode(mode);

                    // Put the chosen module in the address bar. The state above is
                    // React-only, so a refresh or a Back press used to land on
                    // MODES[0] (Data Cleaning) no matter where you were. The
                    // initializer already reads ?mode=, so this closes the loop.
                    //
                    // Two params are handled deliberately:
                    //   subStep — while Smart Router is driving, that param owns the
                    //     mode and a sync effect follows it. Writing ?mode= here would
                    //     fight it and snap the user back, so routed visits are left
                    //     exactly as they were.
                    //   session — switching module starts a fresh panel (see
                    //     setActiveSessionId(null) below). Leaving ?session= in place
                    //     would re-trigger the loader on the next search change and
                    //     pull the old session, and its mode, straight back.
                    const modeParams = new URLSearchParams(location.search);
                    if (!modeParams.get('subStep')) {
                      modeParams.delete('session');
                      modeParams.set('mode', mode.id);
                      const modeSearch = modeParams.toString();
                      navigate({
                        pathname: location.pathname,
                        search: modeSearch ? `?${modeSearch}` : '',
                      });
                    }

                    const isPE = mode.id === 'pe-investment';

                    setSelectedSubTool(null);
                    setSelectedPESubTool(isPE ? PE_SUBTOOLS[0] : null);

                    setResult(null);
                    setPEResult(null);
                    setSmartResult(null);

                    setHelpOpenId(null);
                    setChatMessages([]);

                    setPrompt('');
                    setShowPromptHelper(false);
                    setGeneratedPromptPreview('');
                    setPromptDescription('');

                    // Switching to a new mode/panel starts a fresh, un-loaded session —
                    // without this, activeSessionId (and the "You are a collaborator on
                    // this session" banner + collaborators list it drives) kept pointing
                    // at whatever session was last opened, so the collaborator badge for
                    // the OLD session stayed visible on the new blank panel.
                    setActiveSessionId(null);
                    setIsCollaboratorSession(false);
                  }}
                  className={`relative flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left hover:shadow-md w-full ${
                    isSelected
                      ? 'border-electric-blue shadow-md bg-gradient-to-br ' + mode.gradient
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${isLaunching ? 'cursor-wait opacity-70' : ''}`}
                >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: mode.color + '18' }}
                >
                  <ModeIcon className="w-4.5 h-4.5" style={{ color: mode.color, width: 18, height: 18 }} />
                </div>
                <p className={`text-sm font-semibold pr-5 ${isSelected ? 'text-super-dark-purple' : 'text-gray-800'}`}>
                  {mode.name}
                </p>
                {isPipeline && !isLaunching && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase bg-indigo-100 text-indigo-700">
                    Guided
                  </span>
                )}
                {isLaunching && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase bg-indigo-600 text-white">
                    <Loader2 className="w-3 h-3 animate-spin" /> Launching…
                  </span>
                )}
                {isSelected && (
                  <div
                    className="absolute bottom-2 right-2 w-2 h-2 rounded-full"
                    style={{ backgroundColor: mode.color }}
                  />
                )}
              </button>

              {/* Help icon — hover to show tooltip (shown whenever HELP_CONTENT has an entry for this mode) */}
              {HELP_CONTENT[mode.id] && (
                <div
                  className="absolute top-2 right-2 z-10"
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHelpPos({
                      top: rect.bottom + 6,
                      left: Math.min(rect.right - 300, window.innerWidth - 316),
                    });
                    setHelpOpenId(mode.id);
                  }}
                  onMouseLeave={() => setHelpOpenId(null)}
                >
                  <HelpCircle size={14} className="text-gray-300 hover:text-electric-blue transition-colors cursor-pointer" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── PE Investment Sub-Tools ──────────────────────────────────── */}
      {selectedMode.id === 'pe-investment' && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
            Select an Analysis Type
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PE_SUBTOOLS.map((tool) => {
              const SubIcon = ICON_MAP[tool.icon] || Briefcase;
              const isActive = selectedPESubTool?.id === tool.id;
              return (
                <div key={tool.id} className="relative">
                  <button
                    onClick={() => { setSelectedPESubTool(tool); setPEResult(null); setPEFiles([]); if (peFileInputRef.current) peFileInputRef.current.value = ''; }}
                    className={`flex flex-col items-start p-3 rounded-xl border-2 transition-all text-left hover:shadow-md w-full ${
                      isActive
                        ? 'border-electric-blue shadow-md bg-gradient-to-br ' + tool.gradient
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: tool.color + '18' }}>
                      <SubIcon style={{ color: tool.color, width: 16, height: 16 }} />
                    </div>
                    <p className={`text-xs font-semibold mb-0.5 pr-4 ${isActive ? 'text-super-dark-purple' : 'text-gray-800'}`}>{tool.name}</p>
                    <p className="text-xs text-gray-400 leading-snug line-clamp-2">{tool.description}</p>
                    {isActive && <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full" style={{ backgroundColor: tool.color }} />}
                  </button>
                  <div
                    className="absolute top-2 right-2 z-10"
                    onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setHelpPos({ top: rect.bottom + 6, left: Math.min(rect.right - 300, window.innerWidth - 316) }); setHelpOpenId(tool.id); }}
                    onMouseLeave={() => setHelpOpenId(null)}
                  >
                    <HelpCircle size={14} className="text-gray-300 hover:text-electric-blue transition-colors cursor-pointer" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Help tooltip — fixed overlay, always on top ───────────────── */}
      {helpOpenId && HELP_CONTENT[helpOpenId] && (
        <div
          onMouseEnter={() => setHelpOpenId(helpOpenId)}
          onMouseLeave={() => setHelpOpenId(null)}
          className="bg-white border border-gray-200 rounded-xl shadow-2xl p-4 text-left"
          style={{
            position: 'fixed',
            top: helpPos.top,
            left: helpPos.left,
            width: 300,
            zIndex: 9999,
          }}
        >
          <p className="text-xs font-bold text-super-dark-purple mb-2">What it can do:</p>
          <ol className="space-y-1.5 mb-3">
            {HELP_CONTENT[helpOpenId].skills.map((skill, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-[10px]">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{skill}</span>
              </li>
            ))}
          </ol>
          <div className="border-t border-gray-100 pt-2">
            <p className="text-xs font-semibold text-gray-700 mb-1">Why not just ChatGPT?</p>
            <p className="text-xs text-gray-500 leading-relaxed">{HELP_CONTENT[helpOpenId].chatgpt}</p>
          </div>
        </div>
      )}

      </>}

      {/* ── DataSim Risk Analyzer ─────────────────────────────────────────── */}
      {selectedMode.id === 'risk-analyzer' && (
        <DataSimRiskAnalyzer
          key={datasimResetKey}
          loadSessionId={datasimSessionId}
          onSessionChange={(id) => {
            setDatasimSessionId(id);
            if (id) {
              localStorage.setItem('datasim_session_id', String(id));
              localStorage.removeItem('datasim_wants_new');
            } else {
              localStorage.removeItem('datasim_session_id');
              localStorage.setItem('datasim_wants_new', 'true');
            }
            fetchSessions();
          }}
        />
      )}

      {/* Portfolio Reporting — full inline render */}
      {selectedMode.id === 'portfolio-reporting' && (
        <div className="min-h-0 flex-1">
          <PortfolioReporting />
        </div>
      )}

      {selectedMode.id === 'gfd-intake' && (
        <GFDIntakeWorkspace
          key={gfdResetKey}
          loadSessionId={gfdSessionId}
          onSessionChange={(id) => {
            setGfdSessionId(id);
            if (id) {
              localStorage.setItem('gfd_session_id', String(id));
              localStorage.removeItem('gfd_wants_new');
            } else {
              localStorage.removeItem('gfd_session_id');
              localStorage.setItem('gfd_wants_new', 'true');
            }
            fetchSessions();
          }}
        />
      )}

      {selectedMode.id === 'process-map-synthesis' && (
        <div className="min-h-0 flex-1">
          <ProcessMapSynthesisWorkspace
            key={pmsResetKey}
            loadSessionId={pmsSessionId}
            onSessionChange={(id) => {
              setPmsSessionId(id);
              if (id) {
                localStorage.setItem('pms_session_id', String(id));
                localStorage.removeItem('pms_wants_new');
              } else {
                localStorage.removeItem('pms_session_id');
                localStorage.setItem('pms_wants_new', 'true');
              }
              fetchSessions();
            }}
          />
        </div>
      )}

      {/* Main Panel */}
      {selectedMode.id !== 'risk-analyzer' && selectedMode.id !== 'portfolio-reporting' && selectedMode.id !== 'gfd-intake' && selectedMode.id !== 'process-map-synthesis' && (
      <div className={`grid gap-6 transition-all duration-300 ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
        {/* Left — Input */}
        <div className={`space-y-4 transition-all duration-300 ${isExpanded ? 'hidden' : ''}`}>
          {/* Active Mode Banner */}
          {(() => {
            const activeTool = selectedPESubTool || selectedSubTool || selectedMode;
            const ActiveIcon = selectedPESubTool
              ? (ICON_MAP[selectedPESubTool.icon] || Briefcase)
              : selectedSubTool
                ? (ICON_MAP[selectedSubTool.icon] || Layers)
                : (ICON_MAP[selectedMode.icon] || TrendingUp);
            const label = selectedPESubTool ? 'PE / Investment' : selectedSubTool ? 'Strategic Tool' : 'Active Mode';
            return (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border bg-gradient-to-r ${activeTool.gradient} border-gray-200`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: activeTool.color + '20' }}>
                  <ActiveIcon style={{ color: activeTool.color, width: 16, height: 16 }} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-bold text-super-dark-purple">{activeTool.name}</p>
                </div>
              </div>
            );
          })()}

          {/* PE Investment Form */}
          {selectedPESubTool && (
            <div className="space-y-3">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Deal Parameters</p>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Company Name *</label>
                  <input type="text" value={peCompanyName} onChange={e => setPECompanyName(e.target.value)} placeholder="e.g. Acme Corp" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Sector</label>
                  <input type="text" value={peSector} onChange={e => setPESector(e.target.value)} placeholder="e.g. B2B SaaS, Healthcare IT" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Deal Size</label>
                  <input type="text" value={peDealSize} onChange={e => setPEDealSize(e.target.value)} placeholder="e.g. $250M" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20" />
                </div>
                {selectedPESubTool.id === 'financial-analysis' && (
                  <div className="border-t border-gray-100 pt-3 space-y-3">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">LBO Parameters</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Revenue (LTM)', val: peRevenue, set: setPERevenue, ph: '$50M' },
                        { label: 'EBITDA (LTM)', val: peEbitda, set: setPEEbitda, ph: '$15M' },
                        { label: 'Revenue Growth %', val: peRevenueGrowth, set: setPERevenueGrowth, ph: '20' },
                        { label: 'Hold Period (yrs)', val: peHoldPeriod, set: setPEHoldPeriod, ph: '5' },
                        { label: 'Entry Multiple', val: peEntryMultiple, set: setPEEntryMultiple, ph: '10x' },
                        { label: 'Exit Multiple', val: peExitMultiple, set: setPEExitMultiple, ph: '12x' },
                      ].map(({ label, val, set, ph }) => (
                        <div key={label}>
                          <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                          <input type="text" value={val} onChange={e => set(e.target.value)} placeholder={ph} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Debt/Equity Split</label>
                      <input type="text" value={peDebtEquityRatio} onChange={e => setPEDebtEquityRatio(e.target.value)} placeholder="60/40" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue" />
                    </div>
                  </div>
                )}
                {selectedPESubTool.id === 'ic-memo' && (
                  <div className="border-t border-gray-100 pt-3 space-y-3">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">IC Context (optional)</p>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Investment Thesis</label>
                      <textarea value={peInvestmentThesis} onChange={e => setPEInvestmentThesis(e.target.value)} placeholder="e.g. Market leader in fragmented niche with high switching costs..." rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue resize-none" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Key Risks</label>
                      <textarea value={peKeyRisks} onChange={e => setPEKeyRisks(e.target.value)} placeholder="e.g. Customer concentration, competitive pressure..." rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-electric-blue resize-none" />
                    </div>
                  </div>
                )}
              </div>
              {/* PE File Upload */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Documents (optional)</p>
                <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setPEFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)].slice(0, 10)); }} onClick={() => peFileInputRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-electric-blue/50 transition-colors">
                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Drop PDFs, 10-Ks, CIMs, financials</p>
                  <p className="text-xs text-gray-400 mt-0.5">Up to 10 files</p>
                </div>
                <input ref={peFileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls" onChange={e => { if (e.target.files) setPEFiles(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 10)); }} className="hidden" />
                {peFiles.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {peFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate">{f.name}</span>
                        </div>
                        <button onClick={() => setPEFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 ml-2"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* PE Run Button */}
              <button onClick={handlePEAnalyze} disabled={peLoading || !peCompanyName.trim()} className="w-full py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm" style={{ backgroundColor: selectedPESubTool.color }}>
                {peLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</> : <><Send className="w-4 h-4" />Run {selectedPESubTool.name}</>}
              </button>
            </div>
          )}

          {/* File Upload + Prompt — hidden when PE sub-tool is active */}
          {!selectedPESubTool && (<>
          {smartRouter.inRouter && routerPreloadedDocNames.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-xl">
              <FileText className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-sky-900">
                  Pre-loaded {routerPreloadedDocNames.length} document
                  {routerPreloadedDocNames.length === 1 ? '' : 's'} from Smart Router
                </div>
                <div
                  className="text-[11px] text-sky-700 mt-0.5 truncate"
                  title={routerPreloadedDocNames.join('\n')}
                >
                  {routerPreloadedDocNames.slice(0, 3).join(', ')}
                  {routerPreloadedDocNames.length > 3
                    ? ` + ${routerPreloadedDocNames.length - 3} more`
                    : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRouterPreloadedDocNames([])}
                className="text-[11px] text-sky-700 hover:text-sky-900 underline flex-shrink-0"
                title="Hide this notice (keeps the files attached)"
              >
                Dismiss
              </button>
            </div>
          )}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="space-y-2"
          >
            {/* Upload button — always visible unless 5 files added */}
            {files.length < 5 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`w-full flex items-center gap-3 p-3 bg-white border-2 border-dashed rounded-xl transition-all text-left ${
                  selectedMode.id === 'smart-data-analyst'
                    ? 'border-violet-300 hover:border-violet-500 hover:bg-violet-50'
                    : 'border-gray-300 hover:border-electric-blue hover:bg-blue-50'
                }`}
              >
                <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {selectedMode.id === 'smart-data-analyst'
                      ? 'Upload data files (CSV or Excel recommended)'
                      : selectedMode.id === 'powerpoint-restructure'
                        ? 'Upload a PowerPoint (.pptx) to restructure'
                        : selectedMode.id === 'contract-analysis'
                          ? 'Upload a contract (PDF or DOCX) to analyze'
                          : 'Upload files for analysis'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {selectedMode.id === 'powerpoint-restructure'
                      ? 'PowerPoint (.pptx) — 1 file'
                      : selectedMode.id === 'contract-analysis'
                        ? 'PDF or DOCX — up to 5 files'
                        : selectedMode.id === 'migration-architect'
                          ? 'CSV, Excel, TXT, JSON, PDF, DOCX — up to 5 files · .xlsm / .yxmd / .dsx / .tflx / .twbx go straight to Logic Decomposer'
                          : 'CSV, Excel, TXT, JSON, PDF, DOCX — up to 5 files'}
                  </p>
                </div>
              </button>
            )}
            {/* Project interviews as a source — only where it makes sense to reason over them. */}
            {selectedMode.id === 'smart-data-analyst' && (
              <button
                type="button"
                onClick={attachInterviewInsights}
                disabled={loadingInterviewSource}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-60 disabled:cursor-wait text-xs font-medium transition-colors"
              >
                {loadingInterviewSource
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading interviews…</>
                  : <><FileText className="w-3.5 h-3.5" /> Add this project's interview findings</>}
              </button>
            )}

            {/* File chips */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <FileText className="w-3.5 h-3.5 text-electric-blue flex-shrink-0" />
                    <span className="text-xs text-gray-700 font-medium max-w-[140px] truncate">{f.name}</span>
                    <button
                      onClick={() => {
                        setFiles(prev => prev.filter((_, idx) => idx !== i));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="p-0.5 hover:bg-blue-100 rounded transition-colors"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {xlsWarning.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">Old Excel format not supported</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    <strong>{xlsWarning.join(', ')}</strong> is in old <strong>.xls</strong> format which cannot be read. Please open in Excel, save as <strong>.xlsx</strong>, and re-upload.
                  </p>
                </div>
                <button onClick={() => setXlsWarning([])} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              accept={selectedMode.id === 'migration-architect'
                ? `.csv,.xlsx,.xls,.txt,.md,.json,.pdf,.docx,.pptx,.ppt,${LD_ONLY_EXTENSIONS.join(',')}`
                : '.csv,.xlsx,.xls,.txt,.md,.json,.pdf,.docx,.pptx,.ppt'}
              className="hidden"
            />
          </div>

          {/* Smart mode: suggested prompt chips */}
          {selectedMode.id === 'smart-data-analyst' && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Suggested Goals</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Summarize key trends and patterns',
                  'Find anomalies and outliers',
                  'Show top segments by count',
                  'Analyze correlations between columns',
                  'Give me an executive summary of this data',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setPrompt(suggestion)}
                    className="px-3 py-1.5 text-xs rounded-full border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors font-medium"
                  >
                    {suggestion}
                  </button>
                ))}
                <button
                  onClick={() => handleLaunchPipeline(BUDGET_JUSTIFICATION_LAUNCH)}
                  disabled={!!launchingPipelineKey}
                  title="Guided workflow: collect team/rates, build an auditable financial model, and pressure-test the executive case with the AI Council"
                  className="px-3 py-1.5 text-xs rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors font-medium disabled:opacity-60"
                >
                  {launchingPipelineKey === 'budget_justification' ? 'Launching…' : 'Guided: Budget & Headcount Justification'}
                </button>
              </div>
            </div>
          )}

          {/* Prompt Input */}
          <div>
            {selectedMode.id !== 'data-cleaning' && selectedMode.id !== 'contract-analysis' && (
            <div className="flex items-center justify-between mb-1.5 px-0.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Prompt</p>
              <button
                onClick={() => showPromptHelper ? handleClosePromptHelper() : setShowPromptHelper(true)}
                className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {showPromptHelper ? 'Cancel' : 'Help me write a prompt'}
              </button>
            </div>
            )}

            {showPromptHelper ? (
              <div className="border border-purple-200 rounded-xl overflow-hidden bg-white shadow-sm">
                {!generatedPromptPreview ? (
                  <div className="p-4 bg-purple-50">
                    <p className="text-xs text-purple-700 font-medium mb-2">
                      Describe what you want to analyze — we'll turn it into an effective prompt.
                    </p>
                    <textarea
                      rows={3}
                      value={promptDescription}
                      onChange={e => setPromptDescription(e.target.value)}
                      placeholder={`e.g. I want to ${selectedSubTool ? `use ${selectedSubTool.name} to` : `ask ${selectedMode.name} to`} analyze our Q4 performance and identify key risks...`}
                      className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none bg-white"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400">Powered by Claude</span>
                      <button
                        onClick={handleGeneratePrompt}
                        disabled={generatingPrompt || !promptDescription.trim()}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          generatingPrompt || !promptDescription.trim()
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-700 hover:bg-purple-800 text-white'
                        }`}
                      >
                        {generatingPrompt ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5" /> Generate Prompt</>}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-white">
                    <p className="text-xs text-green-700 font-medium mb-2">
                      {generatingPrompt ? '⟳ Generating new prompt...' : '✓ Prompt generated — review and use it:'}
                    </p>
                    <div className={`bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 mb-3 whitespace-pre-wrap ${generatingPrompt ? 'opacity-50' : ''}`}>
                      {generatingPrompt ? (
                        <div className="flex items-center gap-2 text-purple-600"><Loader2 className="w-4 h-4 animate-spin" /> Regenerating...</div>
                      ) : generatedPromptPreview}
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={handleGeneratePrompt}
                        disabled={generatingPrompt}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                          generatingPrompt ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {generatingPrompt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Regenerate
                      </button>
                      <button
                        onClick={handleUseGeneratedPrompt}
                        disabled={generatingPrompt}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          generatingPrompt ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-purple-700 hover:bg-purple-800 text-white'
                        }`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Use this prompt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {selectedMode.id === 'contract-analysis' ? (
                  /* Contract Analysis: file-driven, no prompt needed — show a
                     prominent Run button that's enabled once a file is uploaded.
                     This branch is taken ONLY when this mode is selected, so the
                     shared prompt+button path used by every other mode is
                     literally never entered here. */
                  <div className="p-4">
                    <div className="bg-sky-50/60 border border-sky-100 rounded-lg p-3 mb-3">
                      <p className="text-xs text-sky-800 leading-relaxed">
                        No prompt required — upload a PDF or DOCX contract above and click Run. The pipeline extracts every clause with its exact numbering, all quantitative values, and a plain-English explanation per clause.
                      </p>
                    </div>
                    <label className="flex items-start gap-2 mb-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={skipContractSummaries}
                        onChange={(e) => setSkipContractSummaries(e.target.checked)}
                        disabled={loading}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500 disabled:opacity-40"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">
                        <span className="font-semibold text-gray-700">Skip plain-English summaries</span> — much faster on very large contracts. All extracted data, KDEs and the Structured Export are unaffected; only the per-clause AI summaries are omitted.
                      </span>
                    </label>
                    <button
                      data-analyze-btn
                      onClick={handleAnalyze}
                      disabled={loading || files.length === 0}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 text-white rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                      style={{ backgroundColor: '#0284c7' }}
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing Contract...</>
                      ) : (
                        <><Send className="w-4 h-4" /> {files.length === 0 ? 'Upload a Contract First' : 'Run Contract Analysis'}</>
                      )}
                    </button>
                  </div>
                ) : selectedMode.id === 'data-cleaning' ? (
                  /* Data Cleaning: optional prompt, SME enrichment, prominent Run button */
                  <div className="p-4">
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Optional: add specific instructions (e.g., 'focus on vendor name matching', 'ignore date columns')..."
                      rows={3}
                      className="w-full p-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none leading-relaxed border border-gray-200 rounded-lg mb-3"
                    />

                    {/* SME Enrichment — subject-matter-expert context */}
                    <details className="mb-3 bg-teal-50/40 border border-teal-200 rounded-lg" open={!!smeNotes || smeFiles.length > 0}>
                      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-teal-800 flex items-center gap-2 hover:bg-teal-50 rounded-lg">
                        <span>💡 SME Enrichment (optional)</span>
                        {(smeNotes || smeFiles.length > 0) && (
                          <span className="text-[10px] font-normal text-teal-600">
                            {smeNotes ? 'notes added' : ''}
                            {smeNotes && smeFiles.length > 0 ? ' · ' : ''}
                            {smeFiles.length > 0 ? `${smeFiles.length} file${smeFiles.length === 1 ? '' : 's'}` : ''}
                          </span>
                        )}
                      </summary>
                      <div className="px-3 pb-3">
                        <p className="text-xs text-gray-600 mb-2">
                          Paste interview notes / emails, or upload a document that explains what the systems, applications, or values in your data mean. The AI will use this to propose canonical name mappings and flag SME-backed issues.
                        </p>
                        <textarea
                          value={smeNotes}
                          onChange={(e) => setSmeNotes(e.target.value)}
                          rows={3}
                          placeholder="e.g. 'Infosys Ltd', 'INFOSYS LIMITED', and 'infosys' are the same vendor — canonical: 'Infosys Limited'. 'TCS' = 'Tata Consultancy Services'..."
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none mb-2"
                        />
                        <label className="flex items-center gap-2 text-xs font-semibold text-teal-700 bg-white hover:bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5 cursor-pointer w-fit">
                          <span>📎 Add SME files</span>
                          <input
                            type="file"
                            multiple
                            accept=".txt,.md,.json,.csv,.pdf,.docx,.doc"
                            onChange={(e) => {
                              const picked = Array.from(e.target.files || []);
                              setSmeFiles([...smeFiles, ...picked].slice(0, 5));
                              e.currentTarget.value = '';
                            }}
                            className="hidden"
                          />
                        </label>
                        {smeFiles.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {smeFiles.map((f, i) => (
                              <li key={i} className="flex items-center justify-between gap-2 text-xs bg-white px-2 py-1 rounded border border-teal-100">
                                <span className="truncate text-gray-700">{f.name}</span>
                                <button
                                  type="button"
                                  onClick={() => setSmeFiles(smeFiles.filter((_, idx) => idx !== i))}
                                  className="text-gray-400 hover:text-red-600 text-sm leading-none"
                                  aria-label={`Remove ${f.name}`}
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </details>

                    <button
                      data-analyze-btn
                      onClick={handleAnalyze}
                      disabled={loading || files.length === 0}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 text-white rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                      style={{ backgroundColor: '#14b8a6' }}
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Cleaning Data...</>
                      ) : (
                        <><Send className="w-4 h-4" /> {files.length === 0 ? 'Upload Files First' : 'Run Data Cleaning'}</>
                      )}
                    </button>
                  </div>
                ) : (
                <>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    selectedSubTool
                      ? selectedSubTool.placeholder
                      : selectedMode.id === 'smart-data-analyst'
                        ? 'Describe what you want to analyze — or pick a suggestion above...'
                        : `What would you like ${selectedMode.name} to analyze?`
                  }
                  rows={selectedMode.id === 'smart-data-analyst' ? 5 : 8}
                  className="w-full p-4 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none leading-relaxed"
                />
                {/* Ryan (Q1, "Revisions to Portfolio screen"): file ingestion lives under Migration
                    Architect and flows into Logic Decomposer. This is the entry point into that
                    per-file decompose (upload → extracted logic → translations → export). */}
                {selectedMode.id === 'migration-architect' && (
                  <div className="mx-4 mt-3 rounded-lg border border-electric-blue/30 bg-electric-blue/5 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-800">Decompose workflow files</span> — upload Excel, Excel+VBA, Alteryx, DataStage or Informatica files to extract business logic, lineage and translations.
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => ldFileInputRef.current?.click()}
                          className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-electric-blue px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Upload workflow files
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/app/logic-decomposer')}
                          className="whitespace-nowrap rounded-lg border border-electric-blue/40 px-3 py-2 text-xs font-semibold text-electric-blue hover:bg-electric-blue/10"
                        >
                          Open Logic Decomposer →
                        </button>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-gray-400">
                      Accepted: {LD_ACCEPT.replace(/,/g, ' · ')} — multiple files run as a batch.
                    </p>
                    <input
                      ref={ldFileInputRef}
                      type="file"
                      multiple
                      accept={LD_ACCEPT}
                      onChange={handleLdFileSelect}
                      className="hidden"
                    />
                  </div>
                )}
                {/* Chart-of-accounts conversion mapping lives under Migration Architect too,
                    as its own dedicated tool (Conversion Architect) rather than a file-type
                    handoff — COA files are plain .csv/.xlsx, indistinguishable by extension
                    from a normal Migration Architect upload, so this is a deliberate click-through
                    rather than auto-detection. */}
                {selectedMode.id === 'migration-architect' && (
                  <div className="mx-4 mt-3 rounded-lg border border-amber-400/30 bg-amber-400/5 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-800">Cloud Portfolio</span> — application-layer inventory, Phase 2 assessment, and Phase 3 category tracker (EAID-keyed; not EUC Path D).
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const params = new URLSearchParams(location.search);
                          params.set('mode', 'migration-architect');
                          params.set('maTab', 'cloud-portfolio');
                          navigate(`/app/intelligence?${params.toString()}`);
                        }}
                        className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
                      >
                        <Cloud className="w-3.5 h-3.5" />
                        Open Cloud Portfolio
                      </button>
                    </div>
                  </div>
                )}
                {selectedMode.id === 'migration-architect' && (
                  <div className="mx-4 mt-3 rounded-lg border border-amber-400/30 bg-amber-400/5 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-800">Chart of accounts conversion</span> — map a source COA to a target Oracle Fusion COA, resolve ambiguous mappings, and generate BRD/FRD documentation.
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/app/conversion-architect')}
                        className="whitespace-nowrap rounded-lg border border-amber-500/40 px-3 py-2 text-xs font-semibold text-amber-600 hover:bg-amber-500/10"
                      >
                        Open Conversion Architect →
                      </button>
                    </div>
                  </div>
                )}
                {(selectedMode.id === 'migration-architect' || selectedMode.id === 'risk-analyzer' || selectedMode.id === 'strategic-synthesis') && (
                  <div className="px-4 pt-3">
                    <VerificationToggle
                      mode={verificationMode}
                      onModeChange={setVerificationMode}
                      disabled={loading}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400">⌘ + Enter to analyze</p>
                  <button
                    data-analyze-btn
                    onClick={handleAnalyze}
                    disabled={loading || !prompt.trim()}
                    className="flex items-center gap-2 px-5 py-2 text-white rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm bg-electric-blue"
                    style={selectedSubTool ? { backgroundColor: selectedSubTool.color } : selectedMode.id === 'smart-data-analyst' ? { backgroundColor: '#7c3aed' } : {}}
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {selectedSubTool ? `Running ${selectedSubTool.name}...` : selectedMode.id === 'smart-data-analyst' ? 'Running Analysis...' : selectedMode.id === 'data-cleaning' ? 'Cleaning Data...' : 'Analyzing...'}</>
                    ) : (
                      <><Send className="w-4 h-4" /> {selectedSubTool ? `Run ${selectedSubTool.name}` : selectedMode.id === 'smart-data-analyst' ? 'Run Smart Analysis' : selectedMode.id === 'data-cleaning' ? 'Run Data Cleaning' : 'Analyze'}</>
                    )}
                  </button>
                </div>
                </>
                )}
              </div>
            )}
          </div>


          {/* Data Cleaning options panel (cleaning ops + reconciliation).
              SME enrichment is inline above, next to the Run button. */}
          {selectedMode.id === 'data-cleaning' && !cleanResult && (
            <DataCleaningInput
              cleaningOptions={cleaningOptions}
              setCleaningOptions={setCleaningOptions}
              reconciliationKey={reconciliationKey}
              setReconciliationKey={setReconciliationKey}
              fileCount={files.length}
              combineMode={combineMode}
              setCombineMode={setCombineMode}
            />
          )}
          </>)}
        </div>

        {/* Right — Output */}
        <div>
          {/* Contract Analysis output — per-file verification banner + clause table */}
          {selectedMode.id === 'contract-analysis' && !loading && (
            contractResult && Array.isArray(contractResult.results) && contractResult.results.length > 0 ? (
              <div>
                <div className="flex justify-end mb-2">
                  <button
                    onClick={() => setIsExpanded(v => !v)}
                    className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                  </button>
                </div>
                <ContractAnalysisResults results={contractResult.results} />
              </div>
            ) : (
              <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-sky-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-sky-50">
                  <FileText className="w-7 h-7 text-sky-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-800 mb-1">Contract Analysis</p>
                  <p className="text-sm text-gray-500 max-w-md leading-relaxed">
                    Upload a PDF or DOCX contract and click Run. You'll get every clause with its exact numbering, all quantitative values, a plain-English explanation per clause, and an automated completeness check.
                  </p>
                </div>
              </div>
            )
          )}

          {/* PowerPoint Restructure output (loading is handled by the generic spinner below) */}
          {selectedMode.id === 'powerpoint-restructure' && !loading && (
            pptxResult ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
                <div className="flex items-start justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
                      <CheckCircle2 className="w-4 h-4" />
                      Restructured deck ready
                    </div>
                    <p className="text-xs text-amber-700 mt-1">
                      {pptxResult.slides_in} slides in → {pptxResult.slides_out} slides out · {pptxResult.posts} POST card(s) ordered by EAI ID · {pptxResult.pres} PRE archived · {pptxResult.removed} intro slide(s) removed{pptxResult.others ? ` · ${pptxResult.others} other kept` : ''}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const resp = await api.get(pptxResult.url, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = pptxResult.name || 'restructured.pptx';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                        toast.success('Downloaded restructured deck');
                      } catch {
                        toast.error('Failed to download');
                      }
                    }}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm"
                  >
                    <Download className="w-3.5 h-3.5" /> Download .pptx
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Slide classification (POST / PRE / intro) and EAI-ID ordering are AI-assisted — please review the order and the new intro slide before sharing.
                </p>
              </div>
            ) : (
              <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-amber-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-amber-50">
                  <Presentation className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-800 mb-1">PowerPoint Restructure</p>
                  <p className="text-sm text-gray-500 max-w-xs leading-relaxed">Upload a .pptx, describe the restructure, and click Run. You'll get a reorganized deck to download — original slide formatting preserved.</p>
                </div>
              </div>
            )
          )}

          {/* Data Cleaning Results */}
          {selectedMode.id === 'data-cleaning' && cleanResult && (
            <div className="space-y-3">
              {cleanResult.combinedFile && (
                cleanResult.combinedFile.url ? (
                  <div className="flex items-start justify-between gap-3 bg-teal-50 border border-teal-200 rounded-xl p-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-bold text-teal-800">
                        <CheckCircle2 className="w-4 h-4" />
                        {cleanResult.combinedFile.mode === 'join'
                          ? 'Files joined into one sheet (matched on key)'
                          : 'Files combined into one sheet (union)'}
                      </div>
                      <p className="text-xs text-teal-700 mt-0.5">
                        {(cleanResult.combinedFile.rows ?? 0).toLocaleString()} rows × {cleanResult.combinedFile.columns ?? 0} columns from {cleanResult.combinedFile.sources?.length || 0} source file(s)
                        {cleanResult.combinedFile.key ? ` · key: ${cleanResult.combinedFile.key}` : ''}
                      </p>
                      <p className="text-[11px] text-teal-600 mt-0.5">
                        This combined file is what the <span className="font-semibold">Download (.xlsx)</span> button gives you. The tabs below show each source file's cleaning detail.
                      </p>
                      {Array.isArray(cleanResult.combinedFile.warnings) && cleanResult.combinedFile.warnings.length > 0 && (
                        <ul className="mt-1 text-[11px] text-amber-700 list-disc list-inside">
                          {cleanResult.combinedFile.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                        </ul>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const useXlsx = !!cleanResult.combinedFile.xlsxUrl;
                          const sourceUrl = cleanResult.combinedFile.xlsxUrl || cleanResult.combinedFile.url;
                          const resp = await api.get(sourceUrl, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([resp.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = useXlsx
                            ? (cleanResult.combinedFile.xlsxName || 'combined_data.xlsx')
                            : (cleanResult.combinedFile.name || 'combined_data.csv');
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          toast.success('Downloaded combined file');
                        } catch {
                          toast.error('Failed to download combined file');
                        }
                      }}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5" /> Download combined
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    Could not combine the files{cleanResult.combinedFile.error ? `: ${cleanResult.combinedFile.error}` : ''}. Each file was still cleaned separately below.
                  </div>
                )
              )}
              <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                {[
                  { id: 'results' as const, label: 'Cleaning Results' },
                  { id: 'transform' as const, label: 'Custom Transform' },
                  { id: 'library' as const, label: 'Library' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setCleanSubView(tab.id)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
                      cleanSubView === tab.id
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {cleanSubView === 'transform' && (
                <div className="h-[600px]">
                  <CustomTransformChat
                    sessionId={cleanResult?.sessionId || null}
                    engagementId={cleanResult?.sessionId ? String(cleanResult.sessionId) : ''}
                    dfPath={cleanResult?.cleanedFilePath || ''}
                    dfSchema={cleanResult?.stats ? { columns: Object.keys(cleanResult.stats) } : {}}
                    dfSample={[]}
                    rowCount={cleanResult?.stats?.rowsAfterCleaning || cleanResult?.stats?.totalRows || 0}
                    fileName={cleanResult?.originalFileNames?.[0] || 'dataset'}
                    onTransformApplied={(result: any) => {
                      if (result?.status === 'success') {
                        toast.success('Custom transformation applied');
                      }
                    }}
                  />
                </div>
              )}

              {cleanSubView === 'library' && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 max-h-[600px] overflow-y-auto scrollbar-thin">
                  <TransformationLibrary
                    engagementId={cleanResult?.sessionId ? String(cleanResult.sessionId) : ''}
                    sessionId={cleanResult?.sessionId || null}
                    dfPath={cleanResult?.cleanedFilePath || ''}
                    rowCount={cleanResult?.stats?.rowsAfterCleaning || cleanResult?.stats?.totalRows || 0}
                    onTransformApplied={(result: any) => {
                      if (result?.status === 'success') {
                        toast.success('Library transformation applied');
                      }
                    }}
                  />
                </div>
              )}

              {cleanSubView === 'results' && (
                <CleaningResultsPanel
                  result={cleanResult}
                  isExpanded={isExpanded}
                  onToggleExpand={() => setIsExpanded(v => !v)}
                  onSendToSmartAnalyst={async () => {
                    const smartMode = MODES.find(m => m.id === 'smart-data-analyst');
                    if (!smartMode) return;
                    const autoPrompt = `Analyze this cleaned dataset (originally from: ${cleanResult.originalFileNames?.join(', ')}). Data cleaning summary: ${cleanResult.stats?.duplicatesRemoved || 0} duplicates removed, ${cleanResult.stats?.nullsFilled || 0} nulls filled, ${cleanResult.stats?.typesFixed || 0} types fixed. Provide detailed insights, charts, and key findings.`;
                    let filesToUse = files.length > 0 ? [...files] : [];
                    const totalUnits = cleanResult?.units || (Array.isArray(cleanResult?.files)
                      ? cleanResult.files.reduce((s: number, f: any) => s + ((f.sheets || []).length), 0)
                      : 0);
                    const useWorkbook = totalUnits > 1 && cleanResult?.sessionId;
                    try {
                      toast.loading('Loading cleaned data...', { id: 'sda-transfer' });
                      if (useWorkbook) {
                        const resp = await api.get(`/api/intelligence/sessions/${cleanResult.sessionId}/workbook`, { responseType: 'blob' });
                        const fileName = `cleaned_session_${cleanResult.sessionId}.xlsx`;
                        filesToUse = [new File([resp.data], fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })];
                      } else if (cleanResult?.cleanedFileUrl) {
                        const resp = await api.get(cleanResult.cleanedFileUrl, { responseType: 'blob' });
                        filesToUse = [new File([resp.data], cleanResult.cleanedFileName || 'cleaned_data.csv', { type: 'text/csv' })];
                      }
                      toast.dismiss('sda-transfer');
                    } catch {
                      toast.dismiss('sda-transfer');
                      if (files.length > 0) {
                        toast('Using original files \u2014 cleaned file not available', { icon: '\u2139\uFE0F' });
                      }
                    }
                    setCleanResult(null);
                    setCleanSubView('results');
                    setIsExpanded(false);
                    setSelectedMode(smartMode);
                    setFiles(filesToUse);
                    setPrompt(autoPrompt);
                    setTimeout(() => {
                      const analyzeBtn = document.querySelector('[data-analyze-btn]') as HTMLButtonElement;
                      if (analyzeBtn) analyzeBtn.click();
                    }, 300);
                    toast.success('Data sent to Smart Data Analyst');
                  }}
                  onSendToCouncil={() => {
                    const issuesList = (cleanResult.issuesFound || []).map((i: any) => `- [${i.severity?.toUpperCase()}] ${i.column}: ${i.description} (${i.count} affected, action: ${i.action})`).join('\n');
                    const qs = (cleanResult.stakeholderQuestions || []).map((q: any, idx: number) => `${idx + 1}. **${q.question}** \u2014 ${q.context} (Columns: ${q.affectedColumns?.join(', ')})`).join('\n');
                    const content = `## Data Cleaning Report \u2014 ${cleanResult.originalFileNames?.join(', ')}\n\n### Summary\n${cleanResult.summary || ''}\n\n### Issues Found (${cleanResult.issuesFound?.length || 0})\n${issuesList}\n\n### Stakeholder Questions\n${qs || 'None'}\n\n### Statistics\n- Duplicates Removed: ${cleanResult.stats?.duplicatesRemoved || 0}\n- Nulls Filled: ${cleanResult.stats?.nullsFilled || 0}\n- Types Fixed: ${cleanResult.stats?.typesFixed || 0}\n- Formats Standardized: ${cleanResult.stats?.formatsStandardized || 0}\n- Quality: ${cleanResult.stats?.qualityScoreBefore || 0}% \u2192 ${cleanResult.stats?.qualityScoreAfter || 0}%`;
                    const councilPrompt = `Review this data cleaning report from Jarvis Intelligence. Evaluate the cleaning decisions, identify any concerns with the approach taken, and recommend additional data quality checks or improvements. Debate whether the cleaning strategies were optimal.`;
                    navigate('/app/ai-council', { state: { prompt: councilPrompt, inputData: content } });
                  }}
                />
              )}
            </div>
          )}

          {/* Refine & Chat — Data Reconciliation. Sits as a floating button
              when there's a cleaning result and the panel is closed; mirrors
              the AI Council UX. Does not touch the cleaning pipeline itself —
              the panel reads the latest cleanResult via contextProvider on
              every send, so iterative refinement always sees fresh state. */}
          {selectedMode.id === 'data-cleaning' && cleanResult && !loading && (
            <>
              {!cleaningChatOpen && (
                <button
                  onClick={() => setCleaningChatOpen(true)}
                  className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-all hover:scale-105"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-semibold">Refine & Chat</span>
                </button>
              )}
              <RefineChatPanel
                open={cleaningChatOpen}
                onClose={() => setCleaningChatOpen(false)}
                mode="data-cleaning"
                accentColor="#0d9488"
                contextProvider={() => {
                  // Each cleanResult field below could be undefined, an array
                  // or (in older session shapes) a stringified blob; guard
                  // with Array.isArray so `.map()` never crashes mid-render.
                  const issuesArr   = Array.isArray(cleanResult.issuesFound) ? cleanResult.issuesFound : [];
                  const qsArr       = Array.isArray(cleanResult.stakeholderQuestions) ? cleanResult.stakeholderQuestions : [];
                  const filesArr    = Array.isArray(cleanResult.originalFileNames) ? cleanResult.originalFileNames : [];
                  const issues = issuesArr
                    .map((i: any) => `- [${(i?.severity || '').toUpperCase()}] ${i?.column || ''}: ${i?.description || ''} (${i?.count || 0} affected, action: ${i?.action || ''})`)
                    .join('\n');
                  const qs = qsArr
                    .map((q: any, idx: number) => `${idx + 1}. ${q?.question || ''} — ${q?.context || ''} (Columns: ${Array.isArray(q?.affectedColumns) ? q.affectedColumns.join(', ') : ''})`)
                    .join('\n');
                  const stats = cleanResult.stats || {};
                  const fileNames = filesArr.join(', ');
                  const analysisText = `## Data Cleaning Report — ${fileNames}

### Summary
${cleanResult.summary || ''}

### Issues Found (${(cleanResult.issuesFound || []).length})
${issues || '(none)'}

### Stakeholder Questions
${qs || '(none)'}

### Statistics
- Duplicates Removed: ${stats.duplicatesRemoved ?? 0}
- Nulls Filled: ${stats.nullsFilled ?? 0}
- Types Fixed: ${stats.typesFixed ?? 0}
- Formats Standardized: ${stats.formatsStandardized ?? 0}
- Quality: ${stats.qualityScoreBefore ?? 0}% → ${stats.qualityScoreAfter ?? 0}%`;
                  return { sessionPrompt: prompt || 'Data reconciliation analysis', analysisText };
                }}
              />
            </>
          )}

          {/* Refine for Export — Smart Data Analyst.
              Smart Data Analyst already ships its own "Refine & Chat" floating
              button (bottom-right indigo) for general chat + Make Chart. We
              add a SEPARATE violet button stacked ABOVE it that opens our new
              panel with Presentation / Excel iterative-refinement modes.
              Two distinct entry points, two clear labels, zero collision with
              the existing button and zero changes to the Make Chart path. */}
          {selectedMode.id === 'smart-data-analyst' && smartResult && !loading && (
            <>
              {!smartAnalystChatOpen && (
                <button
                  onClick={() => setSmartAnalystChatOpen(true)}
                  className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-violet-600 text-white rounded-full shadow-lg hover:bg-violet-700 transition-all hover:scale-105"
                  title="Ask follow-up questions, build charts, or generate PowerPoint / Excel / Word from this analysis"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-semibold">Refine &amp; Chat</span>
                </button>
              )}
              <RefineChatPanel
                open={smartAnalystChatOpen}
                onClose={() => setSmartAnalystChatOpen(false)}
                mode="smart-analyst"
                accentColor="#7c3aed"
                enableChart={!!smartResult?.sessionId}
                chartRawData={smartResult?.rawData ?? undefined}
                onAddChartToDashboard={smartResult?.dashboard ? handleAddChartToDashboard : undefined}
                contextProvider={() => {
                  // smartResult is typed as a discriminated union with several
                  // optional shape variants; cast to a loose record so the
                  // optional-chain reads compile cleanly without sprinkling
                  // `(smartResult as any)` everywhere. Every field below is
                  // Array.isArray-guarded because the backend may serialise
                  // these as a string or object in some result shapes — calling
                  // `.map()` on a non-array would crash the panel mid-render.
                  const ex: any = smartResult.executive   || {};
                  const di: any = smartResult.deepInsight || {};
                  const asLines = (val: any, fmt: (item: any, i: number) => string): string =>
                    Array.isArray(val) ? val.map(fmt).join('\n') : (typeof val === 'string' ? val : '');
                  const insights: string = asLines(
                    di.insights,
                    (s: any, i: number) => `${i + 1}. ${s?.what || ''} — so what: ${s?.soWhat || ''} — now what: ${s?.nowWhat || ''}`,
                  );
                  const kpis: string = asLines(
                    ex.kpis,
                    (k: any) => `- ${k?.label || ''}: ${k?.value || ''}`,
                  );
                  const bullets: string = asLines(ex.bullets, (b: any) => `- ${b}`);
                  const recs: string    = asLines(ex.recommendations, (r: any) => `- ${r}`);
                  // ex.summary is the dashboard's pill array — { label, value }
                  // objects. Interpolating it directly produced "[object Object]"
                  // garbage in the downloaded Word; format the pills into a
                  // readable bullet list instead so the Executive Summary
                  // section actually mirrors what the user sees on screen.
                  const summary: string = asLines(
                    ex.summary,
                    (s: any) => `- ${s?.label || ''}: ${s?.value ?? ''}`,
                  );
                  const analysisText = `## Smart Data Analyst Report — ${smartResult.fileName || ''}

### Executive Summary
${summary || '(none)'}

### KPIs
${kpis || '(none)'}

### Key Bullets
${bullets || '(none)'}

### Recommendations
${recs || '(none)'}

### Analysis Plan
${smartResult.analysisPlan || ''}

### Qualitative Insights
${di.qualitativeAnalysis || ''}

### Deep Insights
${insights || '(none)'}

### Recommendations (Deep)
${di.recommendations || ''}`;
                  return { sessionPrompt: prompt || 'Smart data analysis', analysisText, sessionId: smartResult.sessionId };
                }}
              />
            </>
          )}

          {/* Data Cleaning Loading */}
          {selectedMode.id === 'data-cleaning' && loading && !cleanResult && (
            <div className="h-full min-h-[300px] bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-teal-50 animate-pulse">
                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" style={{ animationDuration: '2s' }} />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-gray-700">Cleaning your data...</p>
                <p className="text-xs text-gray-400 mt-1">Running Python cleaning → AI analysis → Identifying stakeholder questions</p>
              </div>
            </div>
          )}

          {/* Data Cleaning Empty State */}
          {selectedMode.id === 'data-cleaning' && !cleanResult && !loading && (
            <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-teal-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-teal-50">
                <Filter className="w-7 h-7 text-teal-600" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-800 mb-1">Data Cleaning & Reconciliation</p>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed">Upload messy CSV/Excel files, configure cleaning options, and click Analyze to clean and reconcile your data.</p>
              </div>
            </div>
          )}

          {/* PE Investment Results */}
          {selectedMode.id === 'pe-investment' && (
            <>
              {peLoading && (
                <div className="h-full min-h-[300px] bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full border-[3px] border-transparent animate-spin" style={{ borderTopColor: selectedPESubTool?.color || '#0ea5e9' }} />
                    <div className="absolute inset-2.5 rounded-full" style={{ backgroundColor: (selectedPESubTool?.color || '#0ea5e9') + '15' }} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">{selectedPESubTool?.name} is analyzing...</p>
                    <p className="text-xs text-gray-400 mt-1">for {peCompanyName} · this may take 30–60 seconds</p>
                  </div>
                </div>
              )}
              {!peLoading && !peResult && selectedPESubTool && (
                <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
                  {(() => { const PEIcon = ICON_MAP[selectedPESubTool.icon] || Briefcase; return (<><div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: selectedPESubTool.color + '15' }}><PEIcon style={{ color: selectedPESubTool.color, width: 28, height: 28 }} /></div><div><p className="text-base font-bold text-super-dark-purple mb-1">{selectedPESubTool.name}</p><p className="text-sm text-gray-500 max-w-xs leading-relaxed">{selectedPESubTool.description}</p></div></>); })()}
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1"><ChevronRight className="w-3 h-3" /><span>Fill in deal parameters and click Run</span></div>
                </div>
              )}
              {!peLoading && !selectedPESubTool && (
                <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#0ea5e915' }}><Briefcase style={{ color: '#0ea5e9', width: 28, height: 28 }} /></div>
                  <div><p className="text-base font-bold text-super-dark-purple mb-1">PE / Investment Analysis</p><p className="text-sm text-gray-500 max-w-xs leading-relaxed">Select an analysis type above to get started</p></div>
                </div>
              )}
              {peResult && !peLoading && (
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PE_SUBTOOLS.find(t => t.id === peResult.mode)?.color }} />
                        <span className="text-sm font-semibold text-super-dark-purple">{peResult.companyName || peCompanyName} — {PE_SUBTOOLS.find(t => t.id === peResult.mode)?.name}</span>
                        {peResult.documentCount > 0 && <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{peResult.documentCount} doc{peResult.documentCount > 1 ? 's' : ''}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => handlePEExport('pdf')} className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium border border-gray-200"><Download className="w-3.5 h-3.5" />PDF</button>
                        <button onClick={() => handlePEExport('docx')} className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-200"><Download className="w-3.5 h-3.5" />DOCX</button>
                        <button onClick={() => handlePEExport('pptx')} className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-orange-50 text-orange-700 rounded-lg text-xs font-medium border border-orange-200"><Download className="w-3.5 h-3.5" />PPTX</button>
                        <button onClick={handlePESendToCouncil} className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-medium" style={{ backgroundColor: '#221551' }}><Send className="w-3 h-3" />AI Council</button>
                      </div>
                    </div>
                    {peResult.redFlags && peResult.redFlags.length > 0 && (
                      <div className="border-b border-gray-100 px-4 py-3 bg-red-50">
                        <button className="flex items-center justify-between w-full" onClick={() => setPEExpandedRedFlags(v => !v)}>
                          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-sm font-semibold text-red-700">{peResult.redFlags.length} Red Flag{peResult.redFlags.length > 1 ? 's' : ''} Detected</span></div>
                          {peExpandedRedFlags ? <ChevronUp className="w-4 h-4 text-red-400" /> : <ChevronDown className="w-4 h-4 text-red-400" />}
                        </button>
                        {peExpandedRedFlags && (
                          <div className="mt-3 space-y-2">
                            {peResult.redFlags.map((rf, i) => (
                              <div key={i} className={`p-3 rounded-lg border ${rf.severity === 'High' ? 'text-red-700 bg-red-50 border-red-200' : rf.severity === 'Medium' ? 'text-yellow-700 bg-yellow-50 border-yellow-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                                <div className="flex items-start gap-2"><span className="text-xs font-bold px-1.5 py-0.5 rounded border">{rf.severity}</span><div><p className="text-xs font-semibold">{rf.flag}</p><p className="text-xs opacity-80 mt-0.5">{rf.description}</p></div></div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-4 prose prose-sm max-w-none overflow-auto max-h-[600px]">
                      {renderMarkdown(peResult.memo || peResult.analysis || '')}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {selectedMode.id !== 'pe-investment' && selectedMode.id !== 'data-cleaning' && loading && (
            <div className="h-full min-h-[300px] bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-full border-[3px] border-transparent animate-spin"
                  style={{ borderTopColor: (selectedSubTool || selectedMode).color }}
                />
                <div
                  className="absolute inset-2.5 rounded-full"
                  style={{ backgroundColor: (selectedSubTool || selectedMode).color + '15' }}
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700">{(selectedSubTool || selectedMode).name} is analyzing...</p>
                <p className="text-xs text-gray-400 mt-1">
                  {selectedMode.id === 'migration-architect' && migrationProgress
                    ? migrationProgress
                    : selectedMode.id === 'smart-data-analyst'
                      ? 'Running qualitative + quantitative analysis in parallel...'
                      : 'This may take a few moments'}
                </p>
              </div>
            </div>
          )}

          {/* Migration Architect: an explicit failure the user can read and act on.
              The QA finding this replaces was a silent reset to a blank panel — no toast,
              no banner, nothing in the console. */}
          {selectedMode.id === 'migration-architect' && migrationError && !loading && (
            <div className="bg-white border border-red-200 rounded-xl shadow-sm p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-red-700">Migration Architect could not finish this analysis</p>
                <p className="text-sm text-gray-600 mt-1 break-words">{migrationError}</p>
                <button
                  onClick={() => setMigrationError(null)}
                  className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Smart Data Analyst Results */}
          {smartResult && !loading && selectedMode.id !== 'pe-investment' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
              {/* Smart Header */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-violet-500 flex-shrink-0" />
                    <span className="text-sm font-semibold text-super-dark-purple">Smart Data Analysis</span>
                    {smartResult.hasFile && (
                      <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                        {smartResult.fileName}
                      </span>
                    )}
                  </div>
                  {exportingSmartPptx && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700">
                      <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                      <span>Generating PowerPoint… this can take <strong>10–15 min</strong>. Please keep this tab open.</span>
                    </div>
                  )}
                  {projects && projects.length > 0 && (
                    <select
                      value={brandingProjectId ?? ''}
                      onChange={e => setBrandingProjectId(e.target.value ? Number(e.target.value) : null)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400"
                      title="Apply client branding to exports"
                    >
                      <option value="">No branding</option>
                      {projects.map((p: any) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleSmartExportPptx}
                      disabled={exportingSmartPptx}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {exportingSmartPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />}
                      PowerPoint
                    </button>
                    <button
                      onClick={handleSmartExportWord}
                      disabled={exportingSmartWord}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {exportingSmartWord ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                      Word
                    </button>
                    <button
                      onClick={handleSmartExportExcel}
                      disabled={exportingSmartExcel}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {exportingSmartExcel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                      Excel
                    </button>
                    <button
                      onClick={handleSendToCouncil}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold transition-colors border border-indigo-200"
                      title="Send this analysis to AI Council for multi-model review"
                    >
                      <Crown className="w-3.5 h-3.5" />
                      AI Council
                    </button>
                  </div>
                  <button
                    onClick={() => setIsExpanded(v => !v)}
                    className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors ml-auto"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                  </button>
                </div>
                {/* Tabs */}
                <div className="flex flex-wrap gap-1 mt-3">
                  {([
                    { id: 'plan', label: 'Analysis Plan' },
                    ...(smartResult.dataQuality ? [{ id: 'quality', label: 'Data Quality' }] : []),
                    { id: 'executive', label: 'Executive Summary' },
                    ...(smartResult.dashboard ? [{ id: 'dashboards', label: 'Dashboards' }] : []),
                    { id: 'insights', label: 'Deep Insights' },
                  ] as { id: 'executive' | 'plan' | 'insights' | 'dashboards' | 'quality'; label: string }[]).map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => { setSmartTab(tab.id); setShowSmartCode(false); if (tab.id === 'quality') setCollapsedDQFiles(new Set()); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                        smartTab === tab.id
                          ? 'bg-violet-600 text-white'
                          : 'bg-white text-gray-500 hover:text-gray-700 border border-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-5 overflow-y-auto">
                {/* Overview Tab */}
                {smartTab === 'executive' && (
                  <div className="space-y-5" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                    {/* Ingestion Check Banner */}
                    {smartResult.executive?.ingestionCheck && (
                      <div className={`rounded-xl border ${smartResult.executive.ingestionCheck.status === 'COMPLETE' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                        {(smartResult.executive.ingestionCheck.fileDetails && smartResult.executive.ingestionCheck.fileDetails.length > 1
                          ? smartResult.executive.ingestionCheck.fileDetails
                          : [smartResult.executive.ingestionCheck]
                        ).map((f, fi) => (
                          <div key={fi} className={`flex items-center gap-3 px-5 py-3 ${fi > 0 ? 'border-t ' + (smartResult.executive!.ingestionCheck!.status === 'COMPLETE' ? 'border-emerald-200' : 'border-amber-200') : ''}`}>
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${f.status === 'COMPLETE' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                              {f.status === 'COMPLETE' ? '✓' : '!'}
                            </div>
                            <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-0.5">
                              <span className="text-sm font-semibold text-gray-700">{(f.fileName || '').replace(/\*\*/g, '')}</span>
                              {f.rows && <span className="text-sm text-gray-500">{f.rows} rows</span>}
                              {f.columns && <span className="text-sm text-gray-500">{f.columns} columns</span>}
                              {f.dateRange && <span className="text-sm text-gray-500">{f.dateRange}</span>}
                            </div>
                            <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${f.status === 'COMPLETE' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {f.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Data Summary Pills */}
                    {smartResult.executive?.summary && smartResult.executive.summary.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {smartResult.executive.summary.map((s, i) => (
                          <div key={i} style={{ background: '#f8f9fa', borderRadius: '10px', padding: '10px 16px', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: '10px', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{s.label}</div>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e' }}>{s.value}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* KPI Cards from Dashboard */}
                    {smartResult.executive?.kpis && smartResult.executive.kpis.length > 0 && (
                      <div>
                        <p style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' }}>Key Metrics</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                          {smartResult.executive!.kpis.map((kpi, i) => {
                            const kpiFmts: Record<string, (v: number) => string> = {
                              usd: (v) => v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v.toFixed(0)}`,
                              count: (v) => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(1)}K` : v.toLocaleString(),
                              pct: (v) => `${v.toFixed(1)}%`,
                              number: (v) => v.toLocaleString(),
                            };
                            const fmtVal = (kpiFmts[kpi.format || 'number'] || kpiFmts.number)(kpi.value);
                            const isPositive = (kpi.delta ?? 0) >= 0;
                            const isGood = (kpi.isPositiveGood !== false) ? isPositive : !isPositive;
                            return (
                              <div key={i} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <div style={{ fontSize: '11px', fontWeight: 500, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{kpi.label}</div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{fmtVal}</div>
                                {kpi.delta != null && (
                                  <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: isGood ? '#d4edda' : '#f8d7da', color: isGood ? '#2ecc71' : '#e74c3c', borderRadius: '4px', padding: '2px 7px', fontSize: '11px', fontWeight: 600 }}>
                                      {isPositive ? '↑' : '↓'} {Math.abs(kpi.delta).toFixed(1)}%
                                    </span>
                                    {kpi.deltaLabel && <span style={{ fontSize: '11px', color: '#9e9e9e' }}>{kpi.deltaLabel}</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {smartResult.executive?.bullets ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <p style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.6px', margin: 0 }}>Key Bullets</p>
                          {!editingExecSummary && (
                            <button
                              onClick={() => { setExecSummaryDraft(smartResult.executive?.bullets || ''); setEditingExecSummary(true); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#4e79a7', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
                            >
                              <Pencil style={{ width: '14px', height: '14px' }} /> Edit Summary
                            </button>
                          )}
                        </div>
                        {editingExecSummary ? (
                          <div>
                            <textarea
                              value={execSummaryDraft}
                              onChange={(e) => setExecSummaryDraft(e.target.value)}
                              rows={10}
                              style={{ width: '100%', boxSizing: 'border-box', fontSize: '14px', color: '#1a1a2e', lineHeight: 1.6, padding: '14px 16px', borderRadius: '10px', border: '1px solid #c9d4e0', resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
                            />
                            <p style={{ fontSize: '11px', color: '#9e9e9e', margin: '6px 2px 0' }}>One bullet per line. Wrap text in **double asterisks** for bold.</p>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                              <button
                                onClick={handleSaveExecSummary}
                                disabled={savingExecSummary}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#1a1a2e', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: savingExecSummary ? 'default' : 'pointer', opacity: savingExecSummary ? 0.6 : 1 }}
                              >
                                {savingExecSummary ? <Loader2 style={{ width: '14px', height: '14px' }} className="animate-spin" /> : <CheckCircle2 style={{ width: '14px', height: '14px' }} />}
                                Save
                              </button>
                              <button
                                onClick={() => setEditingExecSummary(false)}
                                disabled={savingExecSummary}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#6c757d', background: '#fff', border: '1px solid #d6dbe0', borderRadius: '8px', padding: '8px 16px', cursor: savingExecSummary ? 'default' : 'pointer' }}
                              >
                                <X style={{ width: '14px', height: '14px' }} /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {smartResult.executive.bullets.split('\n').filter(l => l.trim() && !/^[-—]+$/.test(l.trim())).map((line, i) => {
                              const ACCENT_COLORS = ['#4e79a7','#f28e2c','#59a14f','#76b7b2','#af7aa1'];
                              const clean = line.replace(/^[•\-*]\s*/, '');
                              const parts = clean.split(/\*\*(.+?)\*\*/g);
                              return (
                                <div key={i} style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', borderLeft: `4px solid ${ACCENT_COLORS[i % ACCENT_COLORS.length]}`, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                  <p style={{ fontSize: '14px', color: '#1a1a2e', lineHeight: 1.6, margin: 0 }}>
                                    {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <span className="text-amber-500 text-lg">⚠</span>
                        <div>
                          <p className="text-sm font-semibold text-amber-800 mb-0.5">Summary not available for this session</p>
                          <p className="text-xs text-amber-600">Re-upload your file and click Analyze to generate the Executive Summary.</p>
                        </div>
                      </div>
                    )}

                    {/* Top 2 Charts */}
                    {smartResult.executive?.charts && smartResult.executive.charts.length > 0 && (
                      <DashboardRenderer spec={{
                        title: '',
                        kpis: [],
                        charts: smartResult.executive!.charts,
                        insights: [],
                      }} forceTwoColumn />
                    )}

                    {/* Recommendations */}
                    {smartResult.executive?.recommendations && (
                      <div>
                        <p style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' }}>Actionable Recommendations</p>
                        <div className="space-y-3">
                          {smartResult.executive.recommendations.split('\n').filter(l => l.trim() && !/^[-—]+$/.test(l.trim())).map((line, i) => {
                            const match = line.match(/^(\d+)[.)]\s*(.+)/);
                            const text = match ? match[2] : line.replace(/^[•\-*]\s*/, '');
                            const num = match ? match[1] : null;
                            const parts = text.split(/\*\*(.+?)\*\*/g);
                            return (
                              <div key={i} style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                {num && <span style={{ flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%', background: '#1a1a2e', color: '#fff', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{num}</span>}
                                <p style={{ fontSize: '14px', color: '#1a1a2e', lineHeight: 1.6, margin: 0 }}>
                                  {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* Analysis Plan Tab */}
                {smartTab === 'plan' && (() => {
                  const STEP_COLORS = ['#4e79a7','#f28e2c','#59a14f','#76b7b2','#af7aa1','#e15759','#edc949','#9c755f'];
                  const renderBold = (text: string) => {
                    let s = text.trim();
                    if (s.startsWith('**') && s.endsWith('**') && s.slice(2, -2).indexOf('**') === -1) {
                      s = s.slice(2, -2);
                    }
                    return s.split(/\*\*(.+?)\*\*/g).map((p, j) =>
                      j % 2 === 1
                        ? <strong key={j} style={{ fontWeight: 600, color: '#1f2937' }}>{p}</strong>
                        : p
                    );
                  };

                  if (!smartResult.analysisPlan) return (
                    <p className="text-sm text-gray-400 italic">No analysis plan available.</p>
                  );

                  const notebookBtn = (smartResult.deepInsight?.quantitative?.code || smartResult.deepInsight?.quantitative?.output) ? (
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={async () => {
                          try {
                            const response = await api.post('/api/intelligence/export-notebook', {
                              analysisPlan: smartResult.analysisPlan,
                              deepInsight: smartResult.deepInsight,
                              fileName: smartResult.fileName,
                              projectId: brandingProjectId,
                            }, { responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([response.data]));
                            const a = document.createElement('a'); a.href = url;
                            a.download = `analysis_notebook_${Date.now()}.zip`; a.click();
                            URL.revokeObjectURL(url);
                          } catch { toast.error('Notebook download failed'); }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Download Notebook (.zip)
                      </button>
                    </div>
                  ) : null;

                  // Parse into steps: numbered lines are step headers, others are sub-bullets
                  type PlanStep = { num: string; title: string; bullets: string[] };
                  const steps: PlanStep[] = [];
                  let cur: PlanStep | null = null;
                  for (const line of smartResult.analysisPlan.split('\n')) {
                    if (!line.trim() || /^[-—]{2,}$/.test(line.trim())) continue;
                    const m = line.match(/^(\d+)[.)]\s*(.+)/);
                    if (m) {
                      if (cur) steps.push(cur);
                      cur = { num: m[1], title: m[2].replace(/\*\*/g, ''), bullets: [] };
                    } else if (cur) {
                      const clean = line.replace(/^[•\-*]\s*/, '').trim();
                      if (clean) cur.bullets.push(clean);
                    }
                  }
                  if (cur) steps.push(cur);

                  // If no numbered steps found, fall back to flat list
                  if (!steps.length) return (
                    <div className="space-y-2">
                      {smartResult.analysisPlan.split('\n').filter(l => l.trim()).map((line, i) => (
                        <div key={i} style={{ padding: '12px 16px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '10px', fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                          {renderBold(line.replace(/^[•\-*]\s*/, ''))}
                        </div>
                      ))}
                    </div>
                  );

                  return (
                    <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                      {notebookBtn}
                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                        <p style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.6px', margin: 0 }}>
                          {steps.length} Step Analysis Roadmap
                        </p>
                        <div style={{ flex: 1, height: '1px', background: '#e9ecef' }} />
                      </div>

                      {/* Timeline */}
                      <div style={{ position: 'relative', paddingLeft: '52px' }}>
                        {/* Vertical connector line */}
                        <div style={{ position: 'absolute', left: '19px', top: '28px', bottom: '28px', width: '2px', background: 'linear-gradient(to bottom, #e9ecef, #e9ecef)', borderRadius: '1px' }} />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {steps.map((step, si) => {
                            const color = STEP_COLORS[si % STEP_COLORS.length];
                            const isLast = si === steps.length - 1;
                            return (
                              <div key={si} style={{ position: 'relative' }}>
                                {/* Step number badge */}
                                <div style={{
                                  position: 'absolute', left: '-52px', top: '14px',
                                  width: '38px', height: '38px', borderRadius: '50%',
                                  background: color, color: '#fff',
                                  fontSize: '15px', fontWeight: 800,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: `0 0 0 3px #fff, 0 0 0 5px ${color}22`,
                                  zIndex: 1,
                                }}>
                                  {step.num}
                                </div>

                                {/* Step card */}
                                <div style={{
                                  background: '#fff', borderRadius: '12px',
                                  border: '1px solid #e9ecef', borderLeft: `4px solid ${color}`,
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                  overflow: 'hidden',
                                }}>
                                  {/* Title bar */}
                                  <div style={{ padding: '14px 18px', borderBottom: step.bullets.length ? '1px solid #f0f0f0' : 'none' }}>
                                    {(() => {
                                      // Two heading styles supported, colon takes priority over em-dash:
                                      //   "Title: description"  → bold "Title:" then regular " description"
                                      //   "Title — description" → bold "Title" then light " — description"
                                      // No match → whole title bold (legacy single-line steps).
                                      const colonMatch = step.title.match(/^(.+?):\s+(.+)$/);
                                      const dashMatch  = !colonMatch
                                        ? step.title.match(/^(.+?)\s+[—–-]\s+(.+)$/)
                                        : null;
                                      return (
                                        <p style={{ fontSize: '15px', color: '#1a1a2e', margin: 0, lineHeight: 1.5, fontWeight: 400 }}>
                                          {colonMatch ? (
                                            <>
                                              <span style={{ fontWeight: 600 }}>{renderBold(colonMatch[1].trim())}:</span>
                                              <span style={{ fontWeight: 400, color: '#374151' }}> {renderBold(colonMatch[2].trim())}</span>
                                            </>
                                          ) : dashMatch ? (
                                            <>
                                              <span style={{ fontWeight: 600 }}>{renderBold(dashMatch[1].trim())}</span>
                                              <span style={{ fontWeight: 400, color: '#6b7280' }}> — {renderBold(dashMatch[2].trim())}</span>
                                            </>
                                          ) : (
                                            <span style={{ fontWeight: 600 }}>{renderBold(step.title)}</span>
                                          )}
                                        </p>
                                      );
                                    })()}
                                  </div>

                                  {/* Sub-bullets */}
                                  {step.bullets.length > 0 && (
                                    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#fafafa' }}>
                                      {step.bullets.map((b, bi) => (
                                        <div key={bi} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                          <span style={{ flexShrink: 0, marginTop: '8px', width: '5px', height: '5px', borderRadius: '50%', background: color, opacity: 0.7 }} />
                                          <p style={{ fontSize: '14px', color: '#6b7280', fontWeight: 400, lineHeight: 1.65, margin: 0 }}>{renderBold(b)}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Connector dot at bottom for non-last steps */}
                                {!isLast && (
                                  <div style={{ position: 'absolute', left: '-34px', bottom: '-8px', width: '6px', height: '6px', borderRadius: '50%', background: '#e9ecef' }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Insights Tab — sectioned dashboard-style view */}
                {smartTab === 'insights' && (() => {
                  const ACCENT = ['#4e79a7','#f28e2c','#59a14f','#76b7b2','#af7aa1','#e15759','#edc949'];
                  const renderBold = (text: string) => text.split(/\*\*(.+?)\*\*/g).map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p);

                  // Render a block of lines — handles tables, bullets, numbered, plain
                  const renderLines = (bodyLines: string[], accent: string) => {
                    const out: React.ReactNode[] = [];
                    let i = 0;
                    while (i < bodyLines.length) {
                      const bl = bodyLines[i];
                      // Markdown table: group consecutive pipe lines
                      if (bl.trim().startsWith('|')) {
                        const tableLines: string[] = [];
                        while (i < bodyLines.length && bodyLines[i].trim().startsWith('|')) {
                          tableLines.push(bodyLines[i]);
                          i++;
                        }
                        const rows = tableLines.filter(r => !/^\s*\|[-:\s|]+\|\s*$/.test(r));
                        const parsed = rows.map(r => r.replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
                        if (parsed.length > 0) {
                          const headers = parsed[0];
                          const body = parsed.slice(1);
                          out.push(
                            <div key={`tbl-${i}`} style={{ overflowX: 'auto', marginTop: '4px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                  <tr style={{ background: '#f8f9fa' }}>
                                    {headers.map((h, hi) => (
                                      <th key={hi} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#1a1a2e', borderBottom: `2px solid ${accent}`, whiteSpace: 'nowrap' }}>{renderBold(h)}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {body.map((row, ri) => (
                                    <tr key={ri} style={{ borderBottom: '1px solid #f0f0f0', background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                                      {row.map((cell, ci) => (
                                        <td key={ci} style={{ padding: '9px 14px', color: '#374151', verticalAlign: 'top', lineHeight: 1.5 }}>{renderBold(cell)}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }
                        continue;
                      }
                      const bullet = bl.match(/^[•\-*]\s+(.+)/);
                      const numbered = bl.match(/^(\d+)[.)]\s+(.+)/);
                      if (bullet) {
                        out.push(
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <span style={{ flexShrink: 0, marginTop: '9px', width: '7px', height: '7px', borderRadius: '50%', background: accent }} />
                            <p style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7, margin: 0 }}>{renderBold(bullet[1])}</p>
                          </div>
                        );
                      } else if (numbered) {
                        out.push(
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <span style={{ flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%', background: accent, color: '#fff', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{numbered[1]}</span>
                            <p style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7, margin: 0 }}>{renderBold(numbered[2])}</p>
                          </div>
                        );
                      } else {
                        out.push(<p key={i} style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7, margin: 0 }}>{renderBold(bl)}</p>);
                      }
                      i++;
                    }
                    return out;
                  };

                  // Parse qualitative analysis into sections by ## / ### headings
                  const lines = (smartResult.deepInsight?.qualitativeAnalysis || '').split('\n');
                  type Section = { title: string; lines: string[] };
                  const sections: Section[] = [];
                  let current: Section | null = null;
                  for (const line of lines) {
                    const h1 = line.match(/^#\s+(.+)/);   // skip h1 — document title
                    const h2 = line.match(/^##\s+(.+)/);
                    const h3 = line.match(/^###\s+(.+)/);
                    if (h1) { continue; }                  // drop document-level title line
                    if (h2 || h3) {
                      if (current) sections.push(current);
                      current = { title: (h2 || h3)![1].replace(/\*\*/g, ''), lines: [] };
                    } else if (current) {
                      current.lines.push(line);
                    } else if (line.trim()) {
                      if (!current) current = { title: 'Overview', lines: [] };
                      current.lines.push(line);
                    }
                  }
                  if (current && current.lines.some(l => l.trim())) sections.push(current);

                  return (
                    <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="space-y-5">
                      {sections.length > 0 ? sections.filter(sec => !/actionable recommendation/i.test(sec.title) && !/^chart$/i.test(sec.title.trim())).map((sec, si) => {
                        const accent = ACCENT[si % ACCENT.length];
                        const bodyLines = sec.lines.filter(l => l.trim() && !/^[-—]{2,}$/.test(l.trim()));
                        if (!bodyLines.length) return null;
                        return (
                          <div key={si} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                            <div style={{ borderBottom: '1px solid #f0f0f0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: `${accent}12` }}>
                              <span style={{ width: '4px', height: '20px', borderRadius: '2px', background: accent, flexShrink: 0, display: 'inline-block' }} />
                              <p style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '0.1px' }}>{sec.title}</p>
                            </div>
                            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {renderLines(bodyLines, accent)}
                            </div>
                          </div>
                        );
                      }) : <div>{renderMarkdown(smartResult.deepInsight?.qualitativeAnalysis || '')}</div>}

                      {/* Recommendations */}
                      {smartResult.deepInsight?.recommendations && (
                        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                          <div style={{ borderBottom: '1px solid #f0f0f0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: '#1a1a2e12' }}>
                            <span style={{ width: '4px', height: '20px', borderRadius: '2px', background: '#1a1a2e', flexShrink: 0, display: 'inline-block' }} />
                            <p style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Actionable Recommendations</p>
                          </div>
                          <div style={{ padding: '14px 18px' }} className="space-y-3">
                            {smartResult.deepInsight.recommendations.split('\n').filter(l => l.trim() && !/^[-—]+$/.test(l.trim())).map((line, i) => {
                              const match = line.match(/^(\d+)[.)]\s*(.+)/);
                              const text = match ? match[2] : line.replace(/^[•\-*]\s*/, '');
                              const num = match ? match[1] : null;
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 16px', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e8eaf6' }}>
                                  {num && <span style={{ flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%', background: '#1a1a2e', color: '#fff', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{num}</span>}
                                  <p style={{ fontSize: '15px', color: '#1a1a2e', lineHeight: 1.65, margin: 0 }}>{renderBold(text)}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Key Insights */}
                      {smartResult.deepInsight?.insights && smartResult.deepInsight.insights.length > 0 && (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                            <span style={{ width: '4px', height: '20px', borderRadius: '2px', background: '#4e79a7', flexShrink: 0, display: 'inline-block' }} />
                            <p style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>Key Insights</p>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {smartResult.deepInsight.insights.map((ins, i) => {
                              const ACCENT = ['#4e79a7','#f28e2c','#59a14f','#76b7b2','#af7aa1'];
                              return (
                                <div key={i} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', borderLeft: `4px solid ${ACCENT[i % ACCENT.length]}`, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '2px' }}>What</span>
                                      <span style={{ fontSize: '14px', color: '#1a1a2e', lineHeight: 1.6 }}>{ins.what}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '2px' }}>So What</span>
                                      <span style={{ fontSize: '14px', color: '#1a1a2e', lineHeight: 1.6 }}>{ins.soWhat}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#4e79a7', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', marginTop: '2px' }}>Now What</span>
                                      <span style={{ fontSize: '14px', color: '#1a1a2e', fontWeight: 500, lineHeight: 1.6 }}>{ins.nowWhat}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Python Code — merged from Quantitative */}
                      {smartResult.deepInsight?.quantitative?.code && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setShowSmartCode(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 transition-colors"
                          >
                            <span className="flex items-center gap-2"><Code2 className="w-3.5 h-3.5 text-violet-400" /> Generated Python Code</span>
                            {showSmartCode ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                          {showSmartCode && (
                            <pre className="p-4 bg-gray-950 text-violet-300 text-xs font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap">
                              {smartResult.deepInsight.quantitative.code}
                            </pre>
                          )}
                        </div>
                      )}
                      {smartResult.deepInsight?.quantitative?.errors && (
                        <div>
                          <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Warnings / Stderr</p>
                          <pre className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-mono whitespace-pre-wrap overflow-x-auto">{smartResult.deepInsight.quantitative.errors}</pre>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Dashboards Tab */}
                {smartTab === 'dashboards' && (
                  <div className="space-y-5">
                    {smartResult.dashboard ? (
                      <DashboardRenderer spec={smartResult.dashboard} rawData={smartResult.rawData ?? undefined} onDrilldownFetch={async ({ column, value, filterType, excludeValues }) => {
                        try {
                          const resp = await api.post('/api/intelligence/drilldown', {
                            sessionId: smartResult.sessionId,
                            column, value, filterType, excludeValues,
                          });
                          return resp.data.success ? resp.data.data : null;
                        } catch { return null; }
                      }} />
                    ) : (
                      <p className="text-sm text-gray-400 italic">No dashboard was generated. Upload a data file to enable chart generation.</p>
                    )}
                  </div>
                )}

                {/* Data Quality Tab */}
                {smartTab === 'quality' && (() => {
                  const renderDQ = (fileName: string, dqFile: any) => {
                    if (dqFile.error) return (
                      <div key={fileName} style={{ background: '#fff1f0', border: '1px solid #fca5a5', borderRadius: '12px', padding: '16px 20px' }}>
                        <p style={{ fontSize: '13px', color: '#dc2626', margin: 0 }}>{dqFile.error}</p>
                      </div>
                    );
                    const cols = dqFile.columns || [];
                    const issueCount = cols.filter((c: any) => c.null_pct >= 5 || (c.outliers !== null && c.outliers > 0)).length;
                    const cleanCount = cols.length - issueCount;
                    const healthPct = cols.length > 0 ? Math.round((cleanCount / cols.length) * 100) : 100;
                    const healthColor = healthPct >= 80 ? '#2ecc71' : healthPct >= 60 ? '#f39c12' : '#e74c3c';

                    const isCollapsed = !collapsedDQFiles.has(fileName);
                    const toggleCollapse = () => setCollapsedDQFiles(prev => {
                      const next = new Set(prev);
                      next.has(fileName) ? next.delete(fileName) : next.add(fileName);
                      return next;
                    });

                    return (
                      <div key={fileName} style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="space-y-4">
                        {/* File header card */}
                        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e9ecef', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                          <div
                            onClick={toggleCollapse}
                            style={{ background: '#f8f9fa', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid #e9ecef' }}
                          >
                            <p style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>{fileName}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: healthColor }} />
                                <span style={{ fontSize: '13px', fontWeight: 700, color: healthColor }}>{healthPct}% Healthy</span>
                              </div>
                              {isCollapsed ? <ChevronRight className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                            </div>
                          </div>
                          {/* KPI row */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid #f0f0f0' }}>
                            {[
                              { label: 'Total Rows', value: dqFile.total_rows?.toLocaleString() ?? '—' },
                              { label: 'Total Columns', value: dqFile.total_cols ?? '—' },
                              { label: 'Columns with Issues', value: issueCount, color: issueCount > 0 ? '#e74c3c' : '#2ecc71' },
                              { label: 'Duplicate Rows', value: dqFile.duplicate_rows > 0 ? dqFile.duplicate_rows.toLocaleString() : '0', color: dqFile.duplicate_rows > 0 ? '#f39c12' : '#2ecc71' },
                            ].map((kpi, ki) => (
                              <div key={ki} style={{ padding: '14px 20px', borderRight: ki < 3 ? '1px solid #f0f0f0' : 'none', textAlign: 'center' }}>
                                <p style={{ fontSize: '22px', fontWeight: 800, color: (kpi as any).color || '#1a1a2e', margin: '0 0 4px' }}>{kpi.value}</p>
                                <p style={{ fontSize: '11px', color: '#9e9e9e', margin: 0, textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>{kpi.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Column cards grid */}
                        {!isCollapsed && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                          {cols.map((col: any, i: number) => {
                            const nullHigh = col.null_pct >= 20;
                            const nullMed = col.null_pct >= 5 && col.null_pct < 20;
                            const hasOutliers = col.outliers !== null && col.outliers > 0;
                            const hasIssue = nullHigh || nullMed || hasOutliers;
                            const statusColor = nullHigh ? '#e74c3c' : nullMed ? '#f39c12' : hasOutliers ? '#f39c12' : '#2ecc71';
                            const statusBg = nullHigh ? '#fff1f0' : nullMed ? '#fffbeb' : hasOutliers ? '#fffbeb' : '#f0fdf4';
                            const statusLabel = nullHigh ? 'High Nulls' : nullMed ? 'Nulls' : hasOutliers ? 'Outliers' : 'Clean';
                            const isNumeric = col.min !== null;
                            return (
                              <div key={i} style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                {/* Top row: name + status badge + dtype */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#1a1a2e', margin: 0, lineHeight: 1.3, wordBreak: 'break-word' }}>{col.column}</p>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, background: statusBg, color: statusColor, padding: '2px 8px', borderRadius: '10px', border: `1px solid ${statusColor}33` }}>{statusLabel}</span>
                                    <span style={{ fontSize: '10px', fontFamily: 'monospace', background: '#f0f0f5', color: '#6c757d', padding: '2px 7px', borderRadius: '4px', fontWeight: 600 }}>{col.dtype}</span>
                                  </div>
                                </div>

                                {/* Null bar */}
                                {col.null_pct > 0 ? (
                                  <div style={{ marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                                      <span style={{ fontSize: '11px', color: '#9e9e9e' }}>Nulls</span>
                                      <span style={{ fontSize: '11px', fontWeight: 700, color: nullHigh ? '#e74c3c' : '#f39c12' }}>{col.null_pct}% ({col.null_count.toLocaleString()})</span>
                                    </div>
                                    <div style={{ height: '5px', background: '#f0f0f0', borderRadius: '3px', overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${Math.min(col.null_pct, 100)}%`, background: nullHigh ? '#e74c3c' : '#f39c12', borderRadius: '3px' }} />
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <span style={{ fontSize: '11px', color: '#2ecc71', fontWeight: 700 }}>✓ No nulls</span>
                                  </div>
                                )}

                                {/* Stats row */}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  <span style={{ fontSize: '11px', color: '#9e9e9e' }}>{col.unique.toLocaleString()} unique</span>
                                  {hasOutliers && (
                                    <span style={{ fontSize: '11px', fontWeight: 700, background: '#fff3cd', color: '#856404', padding: '1px 7px', borderRadius: '10px' }}>⚠ {col.outliers} outliers</span>
                                  )}
                                  {col.outliers === 0 && (
                                    <span style={{ fontSize: '11px', color: '#2ecc71', fontWeight: 700 }}>✓ No outliers</span>
                                  )}
                                </div>

                                {/* Numeric stats */}
                                {isNumeric && (
                                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f0f0f0', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', textAlign: 'center' }}>
                                    {[['Min', col.min], ['Mean', col.mean], ['Max', col.max]].map(([lbl, val]) => (
                                      <div key={lbl as string}>
                                        <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a2e', margin: '0 0 1px', fontVariantNumeric: 'tabular-nums' }}>{typeof val === 'number' ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</p>
                                        <p style={{ fontSize: '10px', color: '#9e9e9e', margin: 0, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{lbl}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>}
                      </div>
                    );
                  };

                  const allFiles = smartResult.dataQuality ? Object.values(smartResult.dataQuality) : [];
                  const totalIssues = allFiles.reduce((sum: number, f: any) => sum + (f.columns || []).filter((c: any) => c.null_pct >= 5 || (c.outliers !== null && c.outliers > 0)).length, 0);
                  const isClean = allFiles.length > 0 && totalIssues === 0;

                  return (
                    <div className="space-y-6">
                      {/* Overall status banner */}
                      {allFiles.length > 0 && (
                        <div style={{ borderRadius: '12px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '14px', background: isClean ? '#f0fdf4' : '#fffbeb', border: `1px solid ${isClean ? '#86efac' : '#fcd34d'}` }}>
                          <div style={{ flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%', background: isClean ? '#22c55e' : '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#fff', fontSize: '18px' }}>{isClean ? '✓' : '⚠'}</span>
                          </div>
                          <div>
                            <p style={{ fontSize: '14px', fontWeight: 700, color: isClean ? '#15803d' : '#92400e', margin: '0 0 2px' }}>
                              {isClean ? 'All data quality checks passed' : `${totalIssues} column${totalIssues > 1 ? 's' : ''} flagged across ${allFiles.length} file${allFiles.length > 1 ? 's' : ''}`}
                            </p>
                            <p style={{ fontSize: '12px', color: isClean ? '#16a34a' : '#b45309', margin: 0 }}>
                              {isClean ? 'Your data is clean and ready for analysis.' : 'Consider reviewing flagged columns before running further analysis.'}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="space-y-8">
                        {smartResult.dataQuality
                          ? Object.entries(smartResult.dataQuality).map(([fileName, dqFile]) => renderDQ(fileName, dqFile))
                          : <p className="text-sm text-gray-400 italic">Data quality analysis not available. Upload a file and run analysis.</p>
                        }
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Smart empty state */}
          {selectedMode.id === 'smart-data-analyst' && !smartResult && !loading && (
            <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-violet-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-violet-50">
                <Database style={{ color: '#8b5cf6', width: 28, height: 28 }} />
              </div>
              <div>
                <p className="text-base font-bold text-super-dark-purple mb-1">Smart Data Analyst</p>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed">Upload a CSV or Excel file, describe your goal, and get a full quantitative + qualitative report with charts.</p>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                <ChevronRight className="w-3 h-3" />
                <span>Results include PowerPoint with charts + styled Excel</span>
              </div>
            </div>
          )}

          {/* Item 4 (Asana 1216373781992284): Migration Architect gets its own tabbed workspace
              instead of the generic single-scroll result card below — see
              MigrationArchitectWorkspace.tsx for why. */}
          {(!loading && selectedMode.id === 'migration-architect') && (
            <MigrationArchitectWorkspace
              analysisMarkdown={result?.analysis || ''}
              fileName={result?.fileName}
              consolidatedSummary={result?.consolidatedSummary ?? null}
              riskFlags={result?.riskFlags ?? null}
              renderMarkdown={renderMarkdown}
              onExportPdf={handleDownload}
              onExportExcel={handleExportExcel}
              onExportPptx={handleExportPptx}
              onExportWord={handleExportWord}
              onSendToCouncil={handleSendToCouncil}
              exportingExcel={exportingExcel}
              exportingPptx={exportingPptx}
              exportingWord={exportingWord}
              requestedTab={new URLSearchParams(location.search).get('maTab')}
            />
          )}

          {result && !loading && selectedMode.id !== 'smart-data-analyst' && selectedMode.id !== 'pe-investment' && selectedMode.id !== 'data-cleaning' && selectedMode.id !== 'migration-architect' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
              {showVerificationPanel && verificationMetadata && (
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                  <VerificationPanel metadata={verificationMetadata} embedded onClose={() => setShowVerificationPanel(false)} />
                </div>
              )}
              {/* Result Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedMode.color }}
                  />
                  <span className="text-sm font-semibold text-super-dark-purple">{result.mode.name} Analysis</span>
                  {result.hasFile && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                      {result.fileName}
                    </span>
                  )}
                  {verificationMetadata && (
                    <VerificationBadge
                      metadata={verificationMetadata}
                      size="sm"
                      onClick={() => setShowVerificationPanel(!showVerificationPanel)}
                    />
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {/* PDF export — only for non-python modes */}
                  {!result.isPython && (
                    <button
                      onClick={handleDownload}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium transition-colors border border-gray-200"
                    >
                      <Download className="w-3.5 h-3.5" />
                      PDF
                    </button>
                  )}
                  {/* Excel export */}
                  {!result.isPython && (
                    <button
                      onClick={handleExportExcel}
                      disabled={exportingExcel}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-green-50 text-green-700 rounded-lg text-xs font-medium transition-colors border border-green-200 disabled:opacity-50"
                    >
                      {exportingExcel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                      Excel
                    </button>
                  )}
                  {/* PowerPoint export */}
                  {!result.isPython && (
                    <button
                      onClick={handleExportPptx}
                      disabled={exportingPptx}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-orange-50 text-orange-700 rounded-lg text-xs font-medium transition-colors border border-orange-200 disabled:opacity-50"
                    >
                      {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />}
                      PPT
                    </button>
                  )}
                  {/* Word export */}
                  {!result.isPython && (
                    <button
                      onClick={handleExportWord}
                      disabled={exportingWord}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-blue-50 text-blue-700 rounded-lg text-xs font-medium transition-colors border border-blue-200 disabled:opacity-50"
                    >
                      {exportingWord ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                      Word
                    </button>
                  )}
                  {/* Send to AI Council */}
                  <button
                    onClick={handleSendToCouncil}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium transition-colors border border-indigo-200"
                    title="Send this analysis to AI Council for multi-model review"
                  >
                    <Crown className="w-3.5 h-3.5" />
                    AI Council
                  </button>
                </div>
                <button
                  onClick={() => setIsExpanded(v => !v)}
                  className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors ml-auto"
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                </button>
              </div>

              {/* Result Content */}
              <div className={`p-5 overflow-y-auto ${isExpanded ? 'h-[calc(100vh-80px)]' : 'max-h-[520px]'}`}>
                {/* Path routing (Migration Architect only, Asana 1216373781992284 item 2) — leads
                    the panel, ahead of the narrative. This is the routing decision (Path A/B/C/D
                    + evidence), never a disposition; Path D rows point at Logic Decomposer for
                    the actual scored disposition instead of showing one invented here. */}
                {result.routing && result.routing.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Path Routing</p>
                    {result.routing.map((r, i) => {
                      const pathStyle: Record<string, string> = {
                        A: 'bg-sky-50 text-sky-700 border-sky-200',
                        B: 'bg-rose-50 text-rose-700 border-rose-200',
                        C: 'bg-violet-50 text-violet-700 border-violet-200',
                        D: 'bg-amber-50 text-amber-700 border-amber-200',
                      };
                      const disposition = result.dispositions?.find((d) => d.file === r.file);
                      return (
                        <div key={`${r.file}-${i}`} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${pathStyle[r.path] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                Path {r.path} — {r.label}
                              </span>
                              <span className="text-xs text-gray-600 truncate">{r.file}</span>
                              {r.confidence != null && (
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">{Math.round(r.confidence * 100)}% confidence</span>
                              )}
                            </div>
                            {disposition && (
                              <button
                                onClick={() => sendRoutedFileToLogicDecomposer(r.file)}
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[11px] font-semibold whitespace-nowrap"
                              >
                                Send to Logic Decomposer
                              </button>
                            )}
                          </div>
                          {disposition && (
                            <p className="text-[11px] text-amber-700 mt-1.5">{disposition.message}</p>
                          )}
                          {r.evidence.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {r.evidence.map((e, j) => (
                                <li key={j} className="text-[11px] text-gray-500">
                                  <span className="italic">&ldquo;{e.quote}&rdquo;</span>{e.reason ? ` — ${e.reason}` : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {result.isPython ? (
                  <div className="space-y-4">
                    {/* Generated Code — collapsible */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setShowCode(v => !v)}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 transition-colors"
                      >
                        <span className="flex items-center gap-2"><Code2 className="w-3.5 h-3.5 text-green-400" /> Generated Python Code</span>
                        {showCode ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {showCode && (
                        <pre className="p-4 bg-gray-950 text-green-300 text-xs font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap">
                          {result.code}
                        </pre>
                      )}
                    </div>
                    {/* Output */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Output</p>
                      {result.output ? (
                        <pre className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto">
                          {result.output}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No output produced.</p>
                      )}
                    </div>
                    {/* Errors (if any) */}
                    {result.errors && (
                      <div>
                        <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Warnings / Stderr</p>
                        <pre className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-mono whitespace-pre-wrap overflow-x-auto">
                          {result.errors}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  renderMarkdown(result.analysis || '')
                )}
              </div>
            </div>
          )}

          {!result && !loading && selectedMode.id !== 'smart-data-analyst' && selectedMode.id !== 'pe-investment' && selectedMode.id !== 'data-cleaning' && selectedMode.id !== 'powerpoint-restructure' && selectedMode.id !== 'contract-analysis' && selectedMode.id !== 'migration-architect' && (
            <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
              {(() => {
                const displayTool = selectedSubTool || selectedMode;
                const DisplayIcon = selectedSubTool ? (ICON_MAP[selectedSubTool.icon] || Layers) : Icon;
                return (
                  <>
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: displayTool.color + '15' }}
                    >
                      <DisplayIcon style={{ color: displayTool.color, width: 28, height: 28 }} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-super-dark-purple mb-1">{displayTool.name}</p>
                      <p className="text-sm text-gray-500 max-w-xs leading-relaxed">{displayTool.description}</p>
                    </div>
                  </>
                );
              })()}
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                <ChevronRight className="w-3 h-3" />
                <span>Enter your question and click Analyze</span>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Floating Chat Toggle Button ─────────────────────────────── */}
      {/* Smart Data Analyst uses the unified RefineChatPanel (which now folds
          chat + chart + export into one panel), so this inline panel is only
          for the other Intelligence result types and PE sub-tools. */}
      {((result && selectedMode.id !== 'smart-data-analyst') || peResult) && !loading && !peLoading && !chatPanelOpen && (
        <button
          onClick={() => setChatPanelOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-[#4F46E5] text-white rounded-full shadow-lg hover:bg-[#4338CA] transition-all hover:scale-105"
        >
          <MessageSquare className="w-4.5 h-4.5" />
          <span className="text-sm font-semibold">Refine & Chat</span>
          {chatMessages.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-white text-[#4F46E5] text-[11px] font-bold flex items-center justify-center">
              {chatMessages.length}
            </span>
          )}
        </button>
      )}

      {/* ── Slide-out Chat Panel ──────────────────────────────────────── */}
      {chatPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[55]" onClick={() => setChatPanelOpen(false)} />
          <div className={`fixed top-0 right-0 bottom-0 max-w-[95vw] bg-white shadow-2xl z-[60] flex flex-col border-l border-gray-200 animate-in slide-in-from-right duration-200 transition-[width] duration-200 ${chatPanelExpanded ? 'w-[760px]' : 'w-[420px]'}`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#4F46E5]" />
                <span className="text-sm font-semibold text-gray-800">Refine & Chat</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setChatPanelExpanded(v => !v)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                  title={chatPanelExpanded ? 'Shrink panel' : 'Expand panel'}
                >
                  {chatPanelExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                </button>
                <button onClick={() => setChatPanelOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600" title="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
              {chatMessages.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-600">Ask follow-up questions</p>
                  <p className="text-xs text-gray-400 mt-1 max-w-[280px] mx-auto">
                    Refine the analysis, ask for clarification, or request specific details about the results.
                    {smartResult?.sessionId && ' Just ask for a chart (e.g. “plot revenue by region”) and a visualization is built from this session’s data — or use “Make a chart” to force it.'}
                  </p>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'user' ? (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-[#4F46E5] text-white">{msg.content}</div>
                  ) : (
                    <div className="w-full rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm">
                      {renderMarkdown(msg.content)}
                      {!msg.chart && hasSlideMarkers(msg.content) && (
                        <div className="mt-3 flex">
                          <button
                            onClick={() => handleDownloadChatAsPptx(i, msg.content)}
                            disabled={chatPptxDownloadingIdx === i}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#4F46E5] text-white hover:bg-[#4338CA] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Convert this structured response into a downloadable PowerPoint file"
                          >
                            {chatPptxDownloadingIdx === i ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Presentation className="w-3.5 h-3.5" />
                            )}
                            {chatPptxDownloadingIdx === i ? 'Building PPTX…' : 'Download as PPTX'}
                          </button>
                        </div>
                      )}
                      {msg.chart && (
                        <div className="mt-3">
                          <div id={`chat-chart-${i}`} className="bg-white rounded-lg border border-gray-200 p-2">
                            <DashboardRenderer
                              spec={{ title: '', charts: [msg.chart] }}
                              forceTwoColumn
                              rawData={smartResult?.rawData ?? undefined}
                            />
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handleAddChartToDashboard(msg.chart as ChartSpec)}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#4F46E5] text-white hover:bg-[#4338CA] transition-colors"
                            >
                              <PlusCircle className="w-3.5 h-3.5" />
                              Add to dashboard
                            </button>
                            <button
                              onClick={() => handleDownloadChart(`chat-chart-${i}`, (msg.chart as ChartSpec).title)}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">{chartMode ? 'Building chart...' : 'Thinking...'}</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="p-4 border-t border-gray-100 bg-white flex-shrink-0">
              {smartResult?.sessionId && !peResult && (
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-xs text-gray-500">
                    {chartMode ? 'Describe the chart you want from this data.' : 'Ask a follow-up question.'}
                  </span>
                  <button
                    onClick={() => setChartMode(v => !v)}
                    disabled={chatLoading}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
                      chartMode
                        ? 'bg-[#4F46E5] text-white border-[#4F46E5]'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Build a new chart from this session's data"
                  >
                    <BarChart2 className="w-3.5 h-3.5" />
                    Make a chart
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (peResult) handlePEChat();
                      else if (chartMode) handleGenerateChart();
                      else handleChat();
                    }
                  }}
                  placeholder={chartMode ? 'e.g. revenue by region as a donut chart...' : 'Ask a follow-up question...'}
                  rows={2}
                  disabled={chatLoading}
                  className="flex-1 p-3 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] placeholder-gray-400 disabled:opacity-50"
                  autoFocus
                />
                <button
                  onClick={() => { if (peResult) handlePEChat(); else if (chartMode) handleGenerateChart(); else handleChat(); }}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 bg-[#4F46E5] text-white rounded-xl font-semibold text-sm disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex-shrink-0 flex items-center justify-center"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Fullscreen Expand Modal (kept for potential future use) ── */}
      {false && isExpanded && (smartResult || result) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsExpanded(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 ${smartResult ? 'bg-gradient-to-r from-violet-50 to-purple-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: smartResult ? '#8b5cf6' : selectedMode.color }} />
                <span className="text-base font-bold text-super-dark-purple">
                  {smartResult ? 'Smart Data Analysis' : `${result?.mode?.name} Analysis`}
                </span>
                {(smartResult?.fileName || result?.fileName) && (
                  <span className="text-xs text-gray-500 bg-white px-2.5 py-1 rounded-full border border-gray-200">
                    {smartResult?.fileName || result?.fileName}
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
              >
                <Minimize2 className="w-4 h-4" />
                Collapse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapped so a render-time crash shows an error instead of silently blanking the panel
// — a boundary cannot catch errors thrown by its own component, so it has to live one
// level above. Asana "Migration Architect — Analyze session silently resets".
const Intelligence: React.FC = () => (
  <PageErrorBoundary label="Jarvis Intelligence">
    <IntelligenceInner />
  </PageErrorBoundary>
);

export default Intelligence;
